/**
 * The misconception catalog.
 *
 * A wrong answer is data about a student's model of mathematics. Naming the
 * common wrong models — and attaching each to the concept that repairs it —
 * is what lets the platform say "review finding common denominators" instead
 * of "try again" (§12).
 */

export type Misconception = {
  id: string;
  /** Short name, shown to teachers and parents. */
  title: string;
  /** What the student is (understandably) doing. Written without blame. */
  description: string;
  /** How to say it to the student, in second person. */
  studentMessage: string;
  /** The concept to revisit. Drives the automatic review recommendation. */
  remediationConceptId: string;
};

export const MISCONCEPTIONS: Misconception[] = [
  {
    id: 'add-denominators',
    title: 'Adding denominators',
    description: 'Adds numerators and denominators separately, as if fractions were pairs of whole numbers.',
    studentMessage:
      'You added the top numbers and the bottom numbers. Denominators name the *size* of the pieces — they are not counted, they have to match before you can add.',
    remediationConceptId: 'common-denominators',
  },
  {
    id: 'cross-multiply-to-add',
    title: 'Multiplying instead of finding a common denominator',
    description: 'Applies the multiplication rule (multiply across) to an addition problem.',
    studentMessage: 'That is the rule for multiplying fractions. Addition needs pieces of the same size first.',
    remediationConceptId: 'common-denominators',
  },
  {
    id: 'larger-denominator-is-larger',
    title: 'Bigger denominator means bigger fraction',
    description: 'Compares fractions by comparing denominators as whole numbers.',
    studentMessage:
      'The bigger the denominator, the *smaller* each piece. Cutting a cake into 8 pieces gives smaller pieces than cutting it into 4.',
    remediationConceptId: 'compare-fractions',
  },
  {
    id: 'forgot-to-simplify',
    title: 'Correct value, not in lowest terms',
    description: 'Computes correctly but leaves the fraction unsimplified when simplifying is the skill assessed.',
    studentMessage: 'That is the right value. Now divide the top and the bottom by their greatest common factor.',
    remediationConceptId: 'simplify-fractions',
  },
  {
    id: 'multiply-then-add-wrong-order',
    title: 'Left-to-right instead of order of operations',
    description: 'Evaluates an expression strictly left to right, ignoring precedence.',
    studentMessage: 'Multiplication and division happen before addition and subtraction, whatever the order on the page.',
    remediationConceptId: 'order-of-operations',
  },
  {
    id: 'place-value-shift',
    title: 'Decimal point misplaced',
    description: 'Answer is right up to a factor of ten — the digits are correct but the place value is not.',
    studentMessage: 'Your digits are right, but the decimal point moved. Check the size of the answer before you write it.',
    remediationConceptId: 'decimal-place-value',
  },
  {
    id: 'decimal-longer-is-larger',
    title: 'More digits means larger',
    description: 'Compares decimals by digit count, treating 0.25 as larger than 0.7.',
    studentMessage: 'Compare place by place, starting from the left. 0.7 is seven tenths — more than two tenths.',
    remediationConceptId: 'compare-decimals',
  },
  {
    id: 'perimeter-for-area',
    title: 'Perimeter used instead of area',
    description: 'Adds the side lengths where the question asks how much surface is covered.',
    studentMessage: 'That is the distance around the shape. Area is how many unit squares fit inside it.',
    remediationConceptId: 'area-rectangles',
  },
  {
    id: 'area-for-perimeter',
    title: 'Area used instead of perimeter',
    description: 'Multiplies the sides where the question asks for the distance around.',
    studentMessage: 'That is the space inside. Perimeter is the walk all the way around the edge.',
    remediationConceptId: 'perimeter',
  },
  {
    id: 'multiplication-always-grows',
    title: 'Multiplication always makes numbers bigger',
    description: 'Expects a product larger than both factors, which fails when multiplying by a fraction below one.',
    studentMessage:
      'Multiplying by a number smaller than 1 takes a *part* of something, so the answer gets smaller. Two thirds of six is four.',
    remediationConceptId: 'multiply-fractions',
  },
  {
    id: 'regrouping-error',
    title: 'Carrying or borrowing missed',
    description: 'Subtracts the smaller digit from the larger in each column, ignoring regrouping.',
    studentMessage: 'Check each column: when the top digit is smaller, you have to regroup from the next place.',
    remediationConceptId: 'multi-digit-subtraction',
  },
  {
    id: 'divide-wrong-direction',
    title: 'Divisor and dividend swapped',
    description: 'Divides the smaller number by the larger, or reverses the roles in a word problem.',
    studentMessage: 'Read the problem again: which number is being shared out, and into how many groups?',
    remediationConceptId: 'division-meaning',
  },
  {
    id: 'ignored-remainder',
    title: 'Remainder dropped',
    description: 'Reports the quotient only, when the question depends on what is left over.',
    studentMessage: 'There is something left over. What does the remainder mean in this situation?',
    remediationConceptId: 'division-remainders',
  },
  {
    id: 'variable-as-label',
    title: 'Letter read as a label, not a number',
    description: 'Treats the variable as an abbreviation for an object rather than an unknown quantity.',
    studentMessage: 'A letter stands for a *number* we do not know yet, not for a thing.',
    remediationConceptId: 'variables',
  },

  /* -------------------------------------------------------------- grade 3 */
  {
    id: 'partial-product-dropped',
    title: 'Only part of the split multiplied',
    description: 'Breaks a factor apart to make it easier, then multiplies only one of the pieces.',
    studentMessage:
      'You split the number up correctly, but only one piece got multiplied. Both pieces are being multiplied by the same number — do each one, then add the two answers.',
    remediationConceptId: 'multiplication-properties',
  },
  {
    id: 'factor-order-changes-product',
    title: 'Order thought to change the product',
    description: 'Treats a × b and b × a as different calculations with different answers.',
    studentMessage:
      'Turn the array a quarter turn: 4 rows of 7 and 7 rows of 4 are the same dots counted two ways. Swapping the order never changes a product.',
    remediationConceptId: 'multiplication-properties',
  },
  {
    id: 'trailing-zeros-dropped',
    title: 'Zeros lost when multiplying by tens',
    description: 'Multiplies the leading digits correctly but keeps too few zeros in the answer.',
    studentMessage:
      'Multiply the front digits, then put back one zero for every ten you set aside. 4 × 30 is 4 × 3 = 12, and then ten times that: 120.',
    remediationConceptId: 'multiply-by-tens',
  },
  {
    id: 'unknown-factor-added',
    title: 'Missing factor found by adding or subtracting',
    description: 'Undoes a multiplication with a subtraction, treating the missing factor like a missing addend.',
    studentMessage:
      'The numbers here are being multiplied, so the way back is division, not subtraction. Ask "what times this gives that?".',
    remediationConceptId: 'fact-families',
  },
  {
    id: 'pattern-rule-misread',
    title: 'Pattern continued by the wrong rule',
    description: 'Extends or classifies a sequence by a rule that does not fit every term shown.',
    studentMessage:
      'Check your rule against *every* number given, not just the first two. A rule that works once is a coincidence; a rule that works all the way along is the pattern.',
    remediationConceptId: 'number-patterns',
  },
  {
    id: 'intermediate-answer-given',
    title: 'The middle answer given as the final one',
    description: 'Completes the first step of a two-step problem and stops there.',
    studentMessage:
      'That is the right number for the first step — but the question asks for something one step further on. What still has to happen to it?',
    remediationConceptId: 'two-step-problems',
  },
  {
    id: 'number-line-ticks-counted',
    title: 'Marks counted instead of gaps',
    description: 'Counts the tick marks on a number line rather than the equal intervals between them.',
    studentMessage:
      'The denominator counts the *gaps* between 0 and 1, not the marks. A line cut into 4 equal parts has 5 marks but only 4 pieces.',
    remediationConceptId: 'fractions-on-number-line',
  },
  {
    id: 'whole-number-fraction-confused',
    title: 'A whole number and its fraction form mixed up',
    description: 'Reads 5/1 as one fifth, or 4/4 as four, losing which number is the count and which is the size.',
    studentMessage:
      'The bottom number says how big the pieces are, the top says how many. 5/1 is five whole ones; 4/4 is four quarters, which is exactly one.',
    remediationConceptId: 'whole-number-fractions',
  },
  {
    id: 'clock-hands-swapped',
    title: 'The two hands read the wrong way round',
    description: 'Reads the long hand as the hour and the short hand as the minutes.',
    studentMessage:
      'The *short* hand is the hour — it moves slowly, so it needs less room. The long hand is the one counting minutes round the edge.',
    remediationConceptId: 'telling-time',
  },
  {
    id: 'minutes-not-multiplied',
    title: 'Numeral read as the number of minutes',
    description: 'Reports the numeral the minute hand points at instead of multiplying it by five.',
    studentMessage:
      'Each numeral is five minutes further round, not one. The minute hand on the 7 means 7 × 5 = 35 minutes past.',
    remediationConceptId: 'telling-time',
  },
  {
    id: 'time-treated-as-decimal',
    title: 'Time calculated in tens instead of sixties',
    description: 'Subtracts or adds clock times as if there were 100 minutes in an hour.',
    studentMessage:
      'An hour is 60 minutes, not 100 — so 3:50 plus 20 minutes is 4:10, never 3:70. Count up to the next whole hour first, then carry on.',
    remediationConceptId: 'elapsed-time',
  },
  {
    id: 'measurement-units-mixed',
    title: 'Two different units combined',
    description: 'Adds or compares quantities without first putting them in the same unit.',
    studentMessage:
      'These are not measured in the same unit yet. Convert one of them first — 1 kilogram is 1000 grams, 1 litre is 1000 millilitres — then the numbers can be compared.',
    remediationConceptId: 'mass-and-capacity',
  },
  {
    id: 'graph-scale-ignored',
    title: 'Key or scale ignored',
    description: 'Counts symbols or bars directly, as if each one stood for a single item.',
    studentMessage:
      'Look at the key first. Each symbol stands for more than one thing, so the count has to be multiplied before it means anything.',
    remediationConceptId: 'scaled-graphs',
  },
  {
    id: 'graph-total-for-difference',
    title: 'Bars added where the question compares',
    description: 'Adds two values when asked how many more one is than the other.',
    studentMessage:
      '"How many more" is a difference, not a total. Subtract the smaller from the larger.',
    remediationConceptId: 'scaled-graphs',
  },
  {
    id: 'count-and-value-swapped',
    title: 'The value read where the count was asked for',
    description: 'On a line plot, reports the number on the axis instead of how many dots sit above it, or the reverse.',
    studentMessage:
      'The number along the bottom is the *measurement*; how many dots are stacked above it is *how often* that measurement happened. The question is asking for the other one.',
    remediationConceptId: 'line-plots',
  },
  {
    id: 'square-not-a-rectangle',
    title: 'A square excluded from the rectangles',
    description: 'Treats square and rectangle as separate kinds rather than one being a special case of the other.',
    studentMessage:
      'A rectangle is any quadrilateral with four right angles. A square has four right angles, so it is a rectangle — a special one, where the sides happen to match.',
    remediationConceptId: 'shape-attributes',
  },
  {
    id: 'shape-named-by-look',
    title: 'Shape named by appearance, not attributes',
    description: 'Classifies a figure by how it is drawn or oriented rather than by counting sides and angles.',
    studentMessage:
      'Count the sides and check the angles before naming it. A shape turned on its point is the same shape it was before you turned it.',
    remediationConceptId: 'shape-attributes',
  },
  {
    id: 'composite-whole-rectangle',
    title: 'The surrounding rectangle measured instead',
    description: 'Multiplies the overall width by the overall height of a composite figure, counting space the shape does not cover.',
    studentMessage:
      'That is the area of the whole rectangle it sits inside. Split the figure into rectangles and add them — or work out the missing corner and take it away.',
    remediationConceptId: 'area-composite',
  },
  {
    id: 'parts-not-equal',
    title: 'Parts counted without checking they are equal',
    description: 'Names one piece of a partitioned shape as a unit fraction without the pieces being the same size.',
    studentMessage:
      'A fraction only has a meaning when the parts are *equal*. Count the equal pieces the whole is cut into — that number goes underneath.',
    remediationConceptId: 'equal-shares',
  },

  /* -------------------------------------------------------------- grade 4 */
  {
    id: 'comparison-read-as-additive',
    title: '"Times as many" read as "more than"',
    description: 'Adds the multiplier instead of multiplying by it, treating a multiplicative comparison as an additive one.',
    studentMessage:
      '"Four times as many" does not mean "four more". Four times as many as 6 is four lots of 6, which is 24 — not 10.',
    remediationConceptId: 'multiplicative-comparison',
  },
  {
    id: 'partial-product-not-shifted',
    title: 'Second partial product not shifted',
    description: 'Multiplies by the tens digit but records the result as though it were a ones digit, losing a factor of ten.',
    studentMessage:
      'The 4 in 46 is not 4 — it is 40. When you multiply by that digit, the answer has to be ten times bigger than the digit alone suggests.',
    remediationConceptId: 'multiply-multi-digit',
  },
  {
    id: 'quotient-zero-skipped',
    title: 'A zero missing from the quotient',
    description: 'Omits a digit of the quotient when a place divides no times, making the answer ten times too small.',
    studentMessage:
      'When a place will not divide even once, the quotient gets a **0** there — you cannot simply move on. Check: does your answer have as many digits as it should?',
    remediationConceptId: 'long-division',
  },
  {
    id: 'one-counted-as-prime',
    title: 'One counted as a prime',
    description: 'Includes 1 among the primes, having checked only that it has no factors other than 1 and itself.',
    studentMessage:
      'A prime has *exactly two* different factors. 1 has only one factor — itself — so it is neither prime nor composite.',
    remediationConceptId: 'prime-composite',
  },
  {
    id: 'odd-assumed-prime',
    title: 'Odd taken to mean prime',
    description: 'Judges primality by parity, so 9, 15 and 21 are called prime and 2 is not.',
    studentMessage:
      'Being odd is not the same as being prime. 9 is odd but 3 × 3 = 9, and 2 is even but it is the very first prime.',
    remediationConceptId: 'prime-composite',
  },
  {
    id: 'denominator-added-with-numerator',
    title: 'Denominator combined along with the numerator',
    description: 'Adds or subtracts both parts of two fractions that already share a denominator, changing the size of the pieces.',
    studentMessage:
      'The bottom number says how big each piece is, and combining pieces does not change their size. Work on the top numbers only — the denominator stays exactly as it is.',
    remediationConceptId: 'add-like-fractions',
  },
  {
    id: 'whole-multiplied-into-denominator',
    title: 'Whole number multiplied into the denominator',
    description: 'Multiplies both numerator and denominator by the whole number, leaving the fraction unchanged in value.',
    studentMessage:
      'Three lots of 2/5 is 2/5 + 2/5 + 2/5 — six *fifths*. The pieces do not change size, so only the top number is multiplied.',
    remediationConceptId: 'fraction-times-whole',
  },
  {
    id: 'fraction-to-decimal-place-lost',
    title: 'Hundredths written as tenths',
    description: 'Writes the numerator straight after the point without regard to the denominator, so 3/100 becomes 0.3.',
    studentMessage:
      'The denominator chooses the column. Hundredths live two places after the point, so 3/100 is 0.03 — 0.3 is ten times too big.',
    remediationConceptId: 'fractions-to-decimals',
  },
  {
    id: 'conversion-factor-wrong',
    title: 'The wrong conversion factor used',
    description: 'Converts in the right direction but with the wrong number — a hundred where the step is a thousand, or a hundred where it is sixty.',
    studentMessage:
      'The direction was right, but not the size of the step. Say the fact out loud before you calculate: one kilometre is a *thousand* metres, and one hour is *sixty* minutes.',
    remediationConceptId: 'unit-conversion',
  },
  {
    id: 'conversion-inverted',
    title: 'Converted the wrong way',
    description: 'Divides when going to the smaller unit, or multiplies when going to the larger, so the answer is out by the conversion factor squared.',
    studentMessage:
      'Going to a smaller unit means *more* of them, so the number gets bigger. Ask which unit is smaller before you decide to multiply or divide.',
    remediationConceptId: 'unit-conversion',
  },
  {
    id: 'protractor-scale-misread',
    title: 'The other protractor scale read',
    description: 'Reads the scale that runs the other way, giving 180 minus the true angle.',
    studentMessage:
      'You read the other scale. Start from the zero that lies along one arm of the angle, and follow *that* row of numbers round.',
    remediationConceptId: 'angle-measure',
  },
  {
    id: 'angle-parts-not-added',
    title: 'Angle parts not combined',
    description: 'Reports one part of a split angle rather than combining the parts as the question asks.',
    studentMessage:
      'An angle cut in two by a ray is the sum of its parts. Decide whether the question wants you to add the pieces or take one away from the whole.',
    remediationConceptId: 'angle-measure',
  },
  {
    id: 'symmetry-counted-by-sides',
    title: 'One line of symmetry assumed per side',
    description: 'Counts a mirror line for every edge, which is true of regular polygons and of almost nothing else.',
    studentMessage:
      'That rule works only when every side and every angle is the same. Fold this shape instead: how many folds leave the two halves exactly on top of each other?',
    remediationConceptId: 'symmetry',
  },
  {
    id: 'diagonal-taken-as-symmetry',
    title: 'A diagonal counted as a mirror line',
    description: 'Counts every line through the centre as a line of symmetry, so a rectangle is credited with four.',
    studentMessage:
      'Fold along the line: the two halves must land exactly on top of each other. A rectangle folded along its diagonal does not — the corners miss.',
    remediationConceptId: 'symmetry',
  },

  /* -------------------------------------------------------------- grade 5 */
  {
    id: 'power-of-ten-zeros-miscounted',
    title: 'The wrong number of zeros written',
    description: 'Knows that multiplying by a power of ten appends zeros but writes one too few or one too many.',
    studentMessage:
      'The exponent counts the zeros, and it counts them exactly. 10^3 is 1000 — three zeros, so the digits move three places, not two and not four.',
    remediationConceptId: 'powers-of-ten',
  },
  {
    id: 'decimal-shift-inverted',
    title: 'The digits moved the wrong way',
    description: 'Shifts the digits left when dividing by a power of ten, or right when multiplying, so the answer is out by the square of the factor.',
    studentMessage:
      'Ask whether the answer should be bigger or smaller before you move anything. Multiplying by 100 makes a number larger, so the digits move towards the bigger places.',
    remediationConceptId: 'powers-of-ten',
  },
  {
    id: 'rounding-place-misread',
    title: 'The wrong digit used to decide',
    description: 'Looks at a digit other than the one immediately to the right of the rounding place.',
    studentMessage:
      'Only one digit decides: the one immediately to the right of the place you are rounding to. Everything further right is already accounted for by that digit.',
    remediationConceptId: 'round-decimals',
  },
  {
    id: 'rounding-chained',
    title: 'Rounded twice, one place at a time',
    description: 'Rounds to the last place, then rounds that result again, so 2.348 becomes 2.35 and then 2.4.',
    studentMessage:
      'Round once, straight to the place you were asked for. Rounding in steps lets a 4 push a 3 up, and 2.348 is nearer to 2.3 than to 2.4.',
    remediationConceptId: 'round-decimals',
  },
  {
    id: 'decimal-places-not-summed',
    title: 'Decimal places counted in one factor only',
    description: 'Multiplies correctly but gives the product as many decimal places as the longer factor, rather than as many as both together.',
    studentMessage:
      'Count the decimal places in *both* factors and add them. Tenths times tenths are hundredths — two places, because each factor contributed one.',
    remediationConceptId: 'multiply-decimals',
  },
  {
    id: 'decimal-point-not-replaced',
    title: 'The point never put back',
    description: 'Multiplies the factors as whole numbers and reports that product, without counting the decimal places back in.',
    studentMessage:
      'Multiplying as though both numbers were whole is the right first move — the digits are correct. Now count the decimal places in the two factors and put that many back into the answer.',
    remediationConceptId: 'multiply-decimals',
  },
  {
    id: 'divisor-decimal-not-cleared',
    title: 'Only one number shifted',
    description: 'Moves the point in the divisor to make it whole but leaves the dividend untouched, changing the quotient.',
    studentMessage:
      'Whatever you do to the number you are dividing by, do to the number being divided. Both move the same number of places, or the answer changes.',
    remediationConceptId: 'divide-decimals',
  },
  {
    id: 'mixed-regroup-missed',
    title: 'A whole left inside the fraction part',
    description: 'Adds mixed numbers correctly but leaves a fraction part of one or more, such as 4 and 7/5.',
    studentMessage:
      'Your value is right, but the fraction part is a whole one and more. Trade the extra whole across: 7/5 is 1 and 2/5.',
    remediationConceptId: 'add-mixed-numbers',
  },
  {
    id: 'mixed-subtraction-not-borrowed',
    title: 'The smaller fraction taken from the larger',
    description: 'Subtracts the smaller fraction part from the larger regardless of which mixed number it belongs to, avoiding the regroup.',
    studentMessage:
      'When the fraction you are taking away is the bigger one, you cannot swap them round. Borrow a whole from the number in front and turn it into fraction pieces first.',
    remediationConceptId: 'add-mixed-numbers',
  },
  {
    id: 'division-always-shrinks',
    title: 'Division expected to make numbers smaller',
    description: 'Assumes a quotient must be less than the dividend, which fails when the divisor is below one.',
    studentMessage:
      'Dividing asks how many of the divisor fit inside. Lots of small pieces fit into a whole, so dividing by a fraction below 1 makes the answer bigger.',
    remediationConceptId: 'divide-unit-fractions',
  },
  {
    id: 'parentheses-ignored',
    title: 'Brackets evaluated in the usual order',
    description: 'Applies precedence as though the brackets were not there, so 3 × (4 + 2) becomes 3 × 4 + 2.',
    studentMessage:
      'Brackets outrank everything. Whatever is inside them happens first, even when the ordinary rules would have done something else.',
    remediationConceptId: 'numerical-expressions',
  },
  {
    id: 'volume-edges-added',
    title: 'Edges added instead of multiplied',
    description: 'Sums the three edge lengths of a prism, giving a length where a volume was asked for.',
    studentMessage:
      'Adding the edges gives a distance, not an amount of space. One layer holds length × width cubes, and there are height layers of them.',
    remediationConceptId: 'volume-unit-cubes',
  },
  {
    id: 'composite-solid-as-one-box',
    title: 'A composite solid measured as one box',
    description: 'Multiplies the overall dimensions of a solid made from two prisms, counting space the solid does not occupy.',
    studentMessage:
      'That is the box the solid sits inside. Cut the solid into two rectangular prisms, find each volume, and add them.',
    remediationConceptId: 'volume-composite',
  },
  {
    id: 'origin-counted-as-one',
    title: 'Counting started at the origin',
    description: 'Counts the origin as the first unit along each axis, so every coordinate comes out one short.',
    studentMessage:
      'Start counting at 0, not at 1. The origin is where both numbers are zero, so the first line you cross going across is 1 — the corner itself is not a step.',
    remediationConceptId: 'coordinate-first-quadrant',
  },
  {
    id: 'hierarchy-inverted',
    title: 'The classification read the wrong way round',
    description: 'Reverses a one-way relationship between shape families, concluding that every rectangle is a square.',
    studentMessage:
      'This works in one direction only. Every square is a rectangle, because a square has everything a rectangle needs — but a rectangle can be long and thin, so it need not be a square.',
    remediationConceptId: 'classify-quadrilaterals',
  },

  /* ------------------------------------------------------- middle school */
  {
    id: 'invert-first-fraction',
    title: 'Flipped the wrong fraction',
    description: 'Inverts the dividend rather than the divisor when dividing fractions.',
    studentMessage:
      'You flipped the first fraction. It is the one you are dividing *by* that gets turned upside down — the first one stays exactly as it is.',
    remediationConceptId: 'divide-fractions',
  },
  {
    id: 'ratio-part-as-whole',
    title: 'Ratio read as a fraction of the total',
    description: 'Reads a part-to-part ratio such as 2:3 as meaning two thirds of the whole.',
    studentMessage:
      'In the ratio 2:3 there are 2 of one thing and 3 of the other, so 5 parts altogether — the first group is 2 out of 5, not 2 out of 3.',
    remediationConceptId: 'ratios',
  },
  {
    id: 'rate-inverted',
    title: 'Rate divided the wrong way round',
    description: 'Divides the second quantity by the first when the unit rate asks for the opposite.',
    studentMessage:
      'Check which quantity the answer is "per". Miles per hour means miles ÷ hours; hours per mile is a different number entirely.',
    remediationConceptId: 'unit-rates',
  },
  {
    id: 'percent-not-converted',
    title: 'Percent used as a whole number',
    description: 'Treats 25% as 25 rather than as 25 hundredths.',
    studentMessage:
      'A percent is a number of hundredths. Before you calculate with 25%, turn it into 0.25 or 25/100.',
    remediationConceptId: 'percentages',
  },
  {
    id: 'negative-magnitude-order',
    title: 'Negatives ordered by size of the digits',
    description: 'Judges −8 to be greater than −3 because 8 is greater than 3.',
    studentMessage:
      'The further left on the number line, the smaller. −8 is further from zero *below* it, so −8 is less than −3, not more.',
    remediationConceptId: 'integers',
  },
  {
    id: 'subtracting-negative',
    title: 'Subtracting a negative treated as subtracting',
    description: 'Computes a − (−b) as a − b, losing the double negative.',
    studentMessage:
      'Taking away a debt leaves you better off. Subtracting a negative adds: 5 − (−3) is 8, not 2.',
    remediationConceptId: 'integers',
  },
  {
    id: 'exponent-as-product',
    title: 'Exponent multiplied instead of repeated',
    description: 'Computes 2³ as 2 × 3 rather than 2 × 2 × 2.',
    studentMessage:
      'The small number counts how many times the base is *multiplied by itself*, not what to multiply it by. 2³ is 2 × 2 × 2 = 8.',
    remediationConceptId: 'exponents',
  },
  {
    id: 'coordinates-reversed',
    title: 'Coordinates plotted in the wrong order',
    description: 'Reads (x, y) as (y, x), moving up before across.',
    studentMessage: 'Along the corridor, then up the stairs: the first number is across, the second is up.',
    remediationConceptId: 'coordinate-first-quadrant',
  },
  {
    id: 'triangle-area-no-half',
    title: 'Halving forgotten in triangle area',
    description: 'Uses base × height for a triangle, giving the area of the surrounding rectangle.',
    studentMessage:
      'A triangle is exactly half of the rectangle around it, so the answer needs halving: area = ½ × base × height.',
    remediationConceptId: 'area-triangles',
  },
  {
    id: 'volume-as-area',
    title: 'Two dimensions used where three are needed',
    description: 'Multiplies only two edge lengths, computing an area instead of a volume.',
    studentMessage:
      'Volume fills a solid, so all three measurements are involved — length, width and height — and the units are cubed.',
    remediationConceptId: 'volume-unit-cubes',
  },
  {
    id: 'decimal-places-not-counted',
    title: 'Decimal point placed by guess',
    description: 'Multiplies the digits correctly but does not count the decimal places of the two factors.',
    studentMessage:
      'The digits are right. Count the decimal places in the question — the answer has as many as both factors put together.',
    remediationConceptId: 'decimal-operations',
  },
  {
    id: 'decimal-divisor-not-shifted',
    title: 'Only one number shifted in a decimal division',
    description: 'Moves the point in the divisor to make it whole but leaves the dividend as it was.',
    studentMessage:
      'Both numbers have to move by the same amount, or you have changed the question. 7.2 ÷ 0.8 becomes 72 ÷ 8, not 7.2 ÷ 8.',
    remediationConceptId: 'decimal-operations',
  },
  {
    id: 'gcf-lcm-swapped',
    title: 'Greatest common factor and lowest common multiple swapped',
    description: 'Answers the opposite of the question — a multiple where a factor was asked for, or the reverse.',
    studentMessage:
      'A factor is never bigger than the number and a multiple is never smaller. Check which side of the numbers your answer landed on.',
    remediationConceptId: 'gcf-lcm',
  },
  {
    id: 'common-factor-not-greatest',
    title: 'A common factor, but not the greatest',
    description: 'Finds a factor shared by both numbers — often the first one spotted — and stops there.',
    studentMessage:
      'That does divide both numbers, but something larger does too. Take every shared prime at its lower power, not just the first factor you see.',
    remediationConceptId: 'gcf-lcm',
  },
  {
    id: 'absolute-value-keeps-sign',
    title: 'Absolute value left negative',
    description: 'Treats the bars as brackets and copies the sign through.',
    studentMessage:
      'Absolute value is a distance from zero, and nothing is a negative distance from anywhere. |−8| is 8.',
    remediationConceptId: 'absolute-value-opposites',
  },
  {
    id: 'absolute-value-ordered-by-position',
    title: 'Distance from zero confused with order',
    description: 'Compares absolute values by which number is greater rather than by which is further from zero.',
    studentMessage:
      '−9 is less than −4, and it is further from zero than −4. Those are different questions, and absolute value asks the second one.',
    remediationConceptId: 'absolute-value-opposites',
  },
  {
    id: 'inequality-endpoint-included',
    title: 'Endpoint wrongly included or excluded',
    description: 'Reads "at least" as strictly greater, or "more than" as allowing the boundary value.',
    studentMessage:
      '"At least" and "at most" allow the boundary value, so they need ≤ or ≥. "More than" and "less than" leave it out.',
    remediationConceptId: 'one-variable-inequalities',
  },
  {
    id: 'inequality-direction-misread',
    title: 'Inequality pointing the wrong way',
    description: 'Writes the symbol reversed, so the condition describes the opposite range.',
    studentMessage:
      'The wide end of the symbol faces the larger side. Test one value that should work: if it makes your inequality false, the symbol is reversed.',
    remediationConceptId: 'one-variable-inequalities',
  },
  {
    id: 'dependent-independent-swapped',
    title: 'Dependent and independent variables swapped',
    description: 'Names the quantity that responds as the one being chosen, which puts the axes the wrong way round.',
    studentMessage:
      'The independent variable is the one you choose; the dependent one follows from it. You pick how many hours to drive, and the distance answers.',
    remediationConceptId: 'two-variable-relationships',
  },
  {
    id: 'coordinates-read-in-reverse',
    title: 'Point read in the wrong order',
    description: 'Reads or plots (x, y) as (y, x) when moving between a table and a graph.',
    studentMessage:
      'A point is read across first, then up: the independent variable comes first. (5, 40) and (40, 5) describe different situations.',
    remediationConceptId: 'two-variable-relationships',
  },
  {
    id: 'histogram-bar-as-value',
    title: 'Bar height read as a measurement',
    description: 'Takes the height of a histogram bar as a value of the quantity rather than as a count of observations.',
    studentMessage:
      'The height of a bar counts how many observations fell in that interval. It is a number of people, not a number of minutes.',
    remediationConceptId: 'histograms',
  },
  {
    id: 'histogram-bins-counted',
    title: 'Intervals counted instead of observations',
    description: 'Answers "how many" with the number of intervals covered rather than the total frequency in them.',
    studentMessage:
      'Two bars is not two people. Add the heights of the bars the question covers to get the number of observations.',
    remediationConceptId: 'histograms',
  },
  {
    id: 'range-as-largest-value',
    title: 'Range given as the largest value',
    description: 'Reports the maximum instead of the difference between maximum and minimum.',
    studentMessage:
      'Range measures how far the data stretches, so it is the largest value *minus* the smallest — a width, not an endpoint.',
    remediationConceptId: 'data-variability',
  },
  {
    id: 'iqr-as-full-range',
    title: 'Interquartile range taken as the whole range',
    description: 'Subtracts minimum from maximum where the width of the middle half was asked for.',
    studentMessage:
      'The interquartile range is Q3 − Q1, the width of the middle half. The full range includes exactly the extreme values the IQR is designed to ignore.',
    remediationConceptId: 'data-variability',
  },
  {
    id: 'deviations-not-absolute',
    title: 'Deviations added with their signs',
    description: 'Averages the signed distances from the mean, which always total zero.',
    studentMessage:
      'Distances from the mean cancel out — that is what makes it the mean. Take each distance as a positive amount before averaging.',
    remediationConceptId: 'data-variability',
  },
  {
    id: 'multiple-not-lowest',
    title: 'A common multiple, but not the lowest',
    description: 'Multiplies the two numbers together, which is a common multiple but is only the lowest when they share no factor.',
    studentMessage:
      'Multiplying the two numbers always lands on a common multiple, but not usually the smallest one. Anything they already share should only be counted once.',
    remediationConceptId: 'gcf-lcm',
  },
  {
    id: 'opposite-sign-unchanged',
    title: 'Opposite given as the number itself',
    description: 'Reports the original number when asked for its opposite, leaving the sign as it was.',
    studentMessage:
      'The opposite is the mirror image across zero, so the sign changes: the opposite of −7 is 7, and the opposite of 7 is −7.',
    remediationConceptId: 'absolute-value-opposites',
  },
  {
    id: 'statistical-question-single-answer',
    title: 'A question with one answer taken as statistical',
    description: 'Chooses a question that has a single fixed answer, where a statistical question is one that expects varying answers.',
    studentMessage:
      'A statistical question expects its answers to vary. If one measurement settles it, collecting data has nothing to add.',
    remediationConceptId: 'histograms',
  },
  {
    id: 'median-unsorted',
    title: 'Median taken before ordering',
    description: 'Picks the middle value of the list as written rather than of the sorted list.',
    studentMessage: 'The median is the middle of the values *in order*. Sort them first, then find the middle.',
    remediationConceptId: 'mean-median-mode',
  },
  {
    id: 'mean-wrong-divisor',
    title: 'Mean divided by the wrong number',
    description: 'Adds the values correctly but divides by something other than how many there are.',
    studentMessage: 'Add every value, then divide by *how many values there are*.',
    remediationConceptId: 'mean-median-mode',
  },

  /* -------------------------------------------------- upper middle school */
  {
    id: 'negative-product-sign',
    title: 'Sign of a product guessed rather than counted',
    description: 'Applies the additive intuition that "a negative makes it negative" to multiplication and division.',
    studentMessage:
      'Count the negative signs. An even number of them cancel out, so (−4) × (−6) is +24; an odd number leaves the answer negative.',
    remediationConceptId: 'rational-numbers',
  },
  {
    id: 'proportion-additive',
    title: 'Proportion solved by adding',
    description: 'Finds the difference between the two known terms and adds it to the third, instead of scaling.',
    studentMessage:
      'A proportion is about *how many times*, not *how many more*. Find what the first ratio was multiplied by, then use that same multiplier on the other part.',
    remediationConceptId: 'proportions',
  },
  {
    id: 'constant-of-proportionality-inverted',
    title: 'Constant of proportionality found upside down',
    description: 'Computes x ÷ y where the relationship y = kx asks for y ÷ x.',
    studentMessage:
      'k is the number you multiply x *by* to get y, so it is y ÷ x. Check it: put your k back into y = kx and see whether the table still works.',
    remediationConceptId: 'proportional-relationships',
  },
  {
    id: 'nonproportional-taken-as-proportional',
    title: 'A relationship assumed proportional when it is not',
    description: 'Applies y = kx to a table or graph with a non-zero starting amount, using one row to find k.',
    studentMessage:
      'Try every row, not just one. If y ÷ x changes as you go down the table, there is a fixed amount on top of the proportional part — and a proportional graph must pass through (0, 0).',
    remediationConceptId: 'proportional-relationships',
  },
  {
    id: 'percent-change-wrong-base',
    title: 'Percent change measured against the wrong amount',
    description: 'Divides the change by the new amount rather than the original.',
    studentMessage:
      'Percent change is always measured against where you *started*. Divide the change by the original amount, not by the one you ended up with.',
    remediationConceptId: 'percent-applications',
  },
  {
    id: 'percent-change-not-applied',
    title: 'The size of the change given instead of the result',
    description: 'Computes the discount, tax or interest correctly and stops, without adding or subtracting it.',
    studentMessage:
      'That is the size of the change, and it is right. The question asks for the amount you end up with, so it still has to be added on or taken off.',
    remediationConceptId: 'percent-applications',
  },
  {
    id: 'undo-out-of-order',
    title: 'Operations undone in the wrong order',
    description: 'Divides before subtracting when solving ax + b = c, unwinding the expression inside out.',
    studentMessage:
      'Undo in the reverse of the order it was built. The x was multiplied first and added to second, so take the addition off first, then divide.',
    remediationConceptId: 'multi-step-equations',
  },
  {
    id: 'distribute-first-term-only',
    title: 'Distributed to the first term only',
    description: 'Expands a(x + b) as ax + b, multiplying the first term inside the bracket but not the second.',
    studentMessage:
      'The multiplier outside applies to *everything* inside the bracket. 3(x + 5) is 3x + 15, not 3x + 5.',
    // Repaired at `linear-expressions`, not at the equation that exposed it:
    // a student who cannot expand a bracket does not have an equation problem.
    remediationConceptId: 'linear-expressions',
  },
  {
    id: 'unlike-terms-combined',
    title: 'Unlike terms added together',
    description: 'Adds a variable term to a constant, as if 3x + 5 could be simplified to 8x.',
    studentMessage:
      '3x means 3 lots of an unknown number, and 5 is just 5. They cannot be merged any more than 3 metres and 5 seconds can. Only terms with the same letter combine.',
    remediationConceptId: 'linear-expressions',
  },
  {
    id: 'negative-bracket-not-distributed',
    title: 'The minus sign kept outside the bracket',
    description: 'Expands a − b(cx + d) leaving the constant positive, applying the subtraction to the first term only.',
    studentMessage:
      'Subtracting a bracket subtracts *everything* in it. − 3(2x + 4) is − 6x − 12: both terms change sign, not just the one you can see.',
    remediationConceptId: 'linear-expressions',
  },
  {
    id: 'factor-not-fully-taken-out',
    title: 'A common factor taken out, but not the greatest',
    description: 'Factorises with a factor that divides both terms but leaves another one behind inside the bracket.',
    studentMessage:
      'That is a common factor, and your answer is equal to the original — but the bracket still has a factor in it. Take out the *greatest* common factor and nothing is left to remove.',
    remediationConceptId: 'linear-expressions',
  },
  {
    id: 'inequality-sign-not-flipped',
    title: 'Inequality sign kept after dividing by a negative',
    description: 'Does not track the direction of an inequality through a negative multiplier — leaving it unchanged when it should flip, or flipping it when it should not.',
    studentMessage:
      'Multiplying or dividing by a negative turns the number line around: 2 < 5, but −2 > −5. The sign has to flip with it.',
    remediationConceptId: 'inequalities',
  },
  {
    id: 'complementary-supplementary-swap',
    title: 'Complementary and supplementary swapped',
    description: 'Subtracts from 180° when the angles are complementary, or from 90° when they are supplementary.',
    studentMessage:
      'Complementary angles make a right angle — 90°. Supplementary angles make a straight line — 180°. Check which one the picture shows.',
    remediationConceptId: 'angles',
  },
  {
    id: 'diameter-for-radius',
    title: 'Diameter used where the radius belongs',
    description: 'Substitutes the diameter into a formula written in terms of r, or halves when no halving was needed.',
    studentMessage:
      'Check which one you were given. The radius reaches from the centre to the edge; the diameter crosses the whole circle and is twice as long.',
    remediationConceptId: 'circles',
  },
  {
    id: 'circumference-for-area',
    title: 'Circumference formula used for area',
    description: 'Computes 2πr when πr² was needed — the two formulas are remembered as a pair and picked at random.',
    studentMessage:
      'Circumference is a length, once round the edge. Area covers the inside, so the radius is used twice: πr².',
    remediationConceptId: 'circles',
  },
  {
    id: 'surface-area-as-volume',
    title: 'Volume computed instead of surface area',
    description: 'Multiplies the three edge lengths when the question asks how much surface the solid has.',
    studentMessage:
      'Surface area is how much wrapping paper it takes, not how much the box holds. Add up the areas of the faces.',
    remediationConceptId: 'surface-area',
  },
  {
    id: 'probability-as-odds',
    title: 'Probability written as odds',
    description: 'Gives favourable outcomes compared to unfavourable ones, rather than to the total.',
    studentMessage:
      'A probability compares the outcomes you want with *all* the outcomes, not with the rest. With 3 red and 5 blue there are 8 altogether, so P(red) is 3/8.',
    remediationConceptId: 'basic-probability',
  },
  {
    id: 'probability-as-count',
    title: 'A count given instead of a probability',
    description: 'Answers with the number of favourable outcomes rather than a fraction of the total.',
    studentMessage:
      'A probability is always between 0 and 1, so it needs the total underneath it. How many outcomes are there altogether?',
    remediationConceptId: 'basic-probability',
  },
  {
    id: 'compound-probability-added',
    title: 'Probabilities added instead of multiplied',
    description: 'Adds the two probabilities when both events must happen, rather than multiplying them.',
    studentMessage:
      'Both things have to happen, so the second probability applies to the slice the first one already left you. That makes the answer *smaller* than either — you multiply. Adding is for "one or the other".',
    remediationConceptId: 'compound-probability',
  },
  {
    id: 'theoretical-given-for-experimental',
    title: 'The prediction given instead of the result',
    description: 'Answers with the theoretical probability where the question asked what the trials actually produced, or the reverse.',
    studentMessage:
      'That is what the model predicts. This question asks what the trials gave — count the successes and put the number of trials underneath.',
    remediationConceptId: 'experimental-probability',
  },
  {
    id: 'trials-taken-as-conclusive',
    title: 'A short run of trials treated as proof',
    description: 'Concludes an object is biased from a handful of trials, where the gap is well within ordinary variation.',
    studentMessage:
      'A few trials wander a long way from the prediction all by themselves. It takes a large gap over *many* trials before the result says anything about the object.',
    remediationConceptId: 'experimental-probability',
  },
  {
    id: 'outcome-thought-due',
    title: 'An outcome thought to be "due"',
    description: 'Adjusts the next probability because of what the previous trials happened to give.',
    studentMessage:
      'A die has no memory. Whatever the last ten throws did, the next one is still the same probability — the run is outvoted by later trials, never repaid.',
    remediationConceptId: 'experimental-probability',
  },
  {
    id: 'sample-space-undercounted',
    title: 'Not every outcome counted',
    description: 'Counts the outcomes of one stage, or misses combinations, when listing the sample space of two events.',
    studentMessage:
      'Each outcome of the first event pairs with *every* outcome of the second. Two coins and a die is 2 × 2 × 6 = 24 outcomes, not 2 + 2 + 6.',
    remediationConceptId: 'compound-probability',
  },
  {
    id: 'replacement-assumed',
    title: 'The first item put back when it was not',
    description: 'Uses the original total on the second draw, although the first item was kept out.',
    studentMessage:
      'The first one did not go back, so there is one fewer to choose from — and one fewer of its colour. Both numbers in the second fraction change.',
    remediationConceptId: 'compound-probability',
  },
  {
    id: 'sample-count-not-scaled',
    title: 'A sample count reported as the population',
    description: 'Gives the number found in the sample instead of scaling that proportion up to the whole population.',
    studentMessage:
      'That is how many were in your sample. The question asks about the whole population, so keep the *proportion* and apply it to the bigger total.',
    remediationConceptId: 'sampling',
  },
  {
    id: 'biased-sample-trusted',
    title: 'A biased sample treated as representative',
    description: 'Draws a conclusion about a population from a sample chosen in a way that favours one answer.',
    studentMessage:
      'Ask who could *not* have ended up in that sample. If a whole part of the population had no chance of being picked, the result describes the people you asked and nobody else.',
    remediationConceptId: 'sampling',
  },
  {
    id: 'variable-term-left-behind',
    title: 'Variable terms not collected',
    description: 'Moves the numbers across the equals sign but leaves a variable term on the far side.',
    studentMessage:
      'There is an x on both sides. Get all of them onto one side first, or you are solving a different equation.',
    remediationConceptId: 'multi-step-equations',
  },
  {
    id: 'angle-sum-confused',
    title: 'Wrong total used for the angles',
    description: 'Uses 360° where the figure sums to 180°, or the reverse.',
    studentMessage:
      'Check what the angles make between them. A straight line and the angles of a triangle both come to 180°; a full turn around a point is 360°.',
    remediationConceptId: 'angles',
  },
  {
    id: 'surface-area-faces-missed',
    title: 'Not every face counted',
    description: 'Adds the areas of some faces but not all of them — typically one of each pair, or a single face of a cube.',
    studentMessage:
      'Every face has to be counted, including the ones you cannot see. A box has six: each of the three you can see has a twin behind it.',
    remediationConceptId: 'surface-area',
  },

  /* ---------------------------------------------------------------- grade 8 */
  {
    id: 'exponent-bases-multiplied',
    title: 'Bases multiplied as well as exponents combined',
    description: 'Computes 2³ × 2⁴ as 4⁷, operating on the base as well as the exponent.',
    studentMessage:
      'The base does not change. You are counting how many 2s are multiplied together — three of them and then four more makes seven 2s, so it is 2⁷.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'exponents-multiplied-not-added',
    title: 'Exponents multiplied when they should be added',
    description: 'Computes xᵃ × xᵇ as xᵃᵇ, applying the power-of-a-power rule to a product.',
    studentMessage:
      'Write it out and count. x³ × x⁴ is three xs then four more xs — seven altogether, so you add the exponents.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'exponents-added-not-multiplied',
    title: 'Exponents added when they should be multiplied',
    description: 'Computes (xᵃ)ᵇ as xᵃ⁺ᵇ, applying the product rule to a power of a power.',
    studentMessage:
      '(x³)⁴ is four copies of x³ multiplied together, so it is 3 + 3 + 3 + 3 = 12 xs. Four lots of three is a multiplication.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'zero-exponent-is-zero',
    title: 'Anything to the power zero read as zero',
    description: 'Gives x⁰ = 0, reasoning that no copies of x are being multiplied.',
    studentMessage:
      'Follow the pattern down: 2³ = 8, 2² = 4, 2¹ = 2 — each step halves. The next step gives 1, not 0. Anything to the power zero is 1.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'negative-exponent-negative-value',
    title: 'Negative exponent read as a negative number',
    description: 'Gives 2⁻³ as −8 rather than 1/8.',
    studentMessage:
      'A negative exponent does not make the answer negative — it flips it over. 2⁻³ is 1 ÷ 2³, which is 1/8.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'scientific-notation-direction',
    title: 'Decimal point moved the wrong way',
    description: 'Gives the exponent the wrong sign, so a very large number is written as a very small one or the reverse.',
    studentMessage:
      'Check the size of your answer against the original. A number bigger than 10 needs a positive power of ten; a number smaller than 1 needs a negative one.',
    remediationConceptId: 'scientific-notation',
  },
  {
    id: 'scientific-notation-not-normalised',
    title: 'Coefficient not between 1 and 10',
    description: 'Writes something like 34 × 10³, which is a correct value but not scientific notation.',
    studentMessage:
      'The value is right, but scientific notation needs exactly one non-zero digit before the decimal point. Move the point one more place and adjust the power.',
    remediationConceptId: 'scientific-notation',
  },
  {
    id: 'root-halved',
    title: 'Square root taken as halving',
    description: 'Computes √36 as 18, dividing by two instead of finding the number that squares to it.',
    studentMessage:
      'A square root asks what number times *itself* gives this. 18 × 18 is 324, not 36 — the answer is 6.',
    remediationConceptId: 'square-roots',
  },
  {
    id: 'root-distributed-over-sum',
    title: 'Root taken of each term separately',
    description: 'Computes √(a² + b²) as a + b, distributing the root across an addition.',
    studentMessage:
      'A square root does not split across a sum. √(9 + 16) is √25 = 5, not 3 + 4 = 7. Add first, then take the root.',
    remediationConceptId: 'square-roots',
  },
  {
    id: 'pythagoras-leg-added',
    title: 'Sides added when a leg was being found',
    description: 'Uses a² + b² = c² to find a shorter side, adding the two known sides instead of subtracting.',
    studentMessage:
      'The hypotenuse is the longest side, and here it is one of the numbers you were given. To find a shorter side, subtract: a² = c² − b².',
    remediationConceptId: 'pythagoras',
  },
  {
    id: 'slope-inverted',
    title: 'Slope calculated as run over rise',
    description: 'Divides the horizontal change by the vertical one.',
    studentMessage:
      'Slope is rise over run — how far *up* for each step across. You have the fraction upside down.',
    remediationConceptId: 'linear-functions',
  },
  {
    id: 'slope-intercept-swapped',
    title: 'Slope and intercept read the wrong way round',
    description: 'In y = mx + c, takes c as the slope and m as the intercept.',
    studentMessage:
      'The number attached to x is the slope; the number on its own is where the line crosses the y-axis. In y = 3x + 2 the slope is 3 and the intercept is 2.',
    remediationConceptId: 'linear-functions',
  },
  {
    id: 'function-repeated-input',
    title: 'A repeated input allowed',
    description: 'Accepts a relation that gives one input two different outputs as a function.',
    studentMessage:
      'A function must give exactly one output for each input. If the same x appears twice with different ys, it is not a function.',
    remediationConceptId: 'functions-intro',
  },
  {
    id: 'system-satisfies-one-equation',
    title: 'A solution that fits only one equation',
    description: 'Solves one equation of a system and stops, without checking the point against the other.',
    studentMessage:
      'The solution has to make *both* equations true at once — that is what the intersection point means. Substitute it into the other one and check.',
    remediationConceptId: 'systems-of-equations',
  },
  {
    id: 'dilation-called-congruent',
    title: 'A resized figure called congruent',
    description: 'Treats a dilation as congruence, because the shape is unchanged.',
    studentMessage:
      'Congruent means identical in size as well as shape. A dilation keeps the shape but changes the size, so the figures are *similar*, not congruent.',
    remediationConceptId: 'transformations',
  },
  {
    id: 'cone-third-omitted',
    title: 'The third forgotten in a cone',
    description: 'Computes πr²h for a cone, giving the volume of the cylinder around it.',
    studentMessage:
      'A cone holds exactly one third of the cylinder with the same base and height. The formula needs that third: V = ⅓πr²h.',
    remediationConceptId: 'round-solids',
  },
  {
    id: 'radius-wrong-power',
    title: 'Radius raised to the wrong power',
    description:
      'Uses the radius the wrong number of times in a volume formula — once where it should be squared, or squared where it should be cubed.',
    studentMessage:
      'Count how many times the radius appears in the formula. A circular base needs r², and a sphere needs r³, because a volume fills three dimensions.',
    remediationConceptId: 'round-solids',
  },
  {
    id: 'correlation-taken-as-cause',
    title: 'Association read as causation',
    description: 'Concludes that one variable causes the other because the scatter plot shows a trend.',
    studentMessage:
      'A scatter plot can only show that two things move together. Something else may be driving both — ice-cream sales and sunburn rise together, and neither causes the other.',
    remediationConceptId: 'scatter-plots',
  },
  {
    id: 'power-not-applied-to-coefficient',
    title: 'The outer power applied to the letter only',
    description: 'Computes (3x⁴)² as 3x⁸, raising the variable but leaving the number in front alone.',
    studentMessage:
      'A bracket means everything inside it. (3x⁴)² is 3x⁴ × 3x⁴, so the 3 is multiplied by itself too — the coefficient becomes 9.',
    remediationConceptId: 'exponent-rules',
  },
  {
    id: 'powers-of-ten-combined-wrongly',
    title: 'Powers of ten combined with the wrong operation',
    description:
      'Multiplies or divides two numbers in scientific notation and then multiplies, subtracts or adds the exponents where the other operation was needed.',
    studentMessage:
      'Handle the two parts separately. The coefficients are multiplied or divided as usual, and the powers of ten follow the exponent laws: add them when multiplying, subtract them when dividing.',
    remediationConceptId: 'scientific-notation',
  },
  {
    id: 'cube-root-as-third',
    title: 'Cube root taken as dividing by three',
    description: 'Computes ∛125 as about 41.7, dividing by three instead of finding the number that cubes to it.',
    studentMessage:
      'A cube root asks what number, multiplied by itself *three times*, gives this. 125 ÷ 3 is not it — 5 × 5 × 5 = 125, so the answer is 5.',
    remediationConceptId: 'cube-roots',
  },
  {
    id: 'root-index-ignored',
    title: 'Square root taken where a cube root was asked',
    description: 'Reads ∛ as √, so a volume is treated as an area and the wrong number of factors is undone.',
    studentMessage:
      'Look at the small 3 on the root sign. √64 undoes two factors and gives 8; ∛64 undoes three and gives 4. They are different questions about the same number.',
    remediationConceptId: 'cube-roots',
  },
  {
    id: 'sum-of-squares-not-rooted',
    title: 'The root not taken at the end',
    description: 'Adds the two squares correctly and reports that total, stopping one step before the square root.',
    studentMessage:
      'You found c², not c. The theorem gives you the square of the side, so there is one step left: take the square root of what you added.',
    remediationConceptId: 'pythagoras',
  },
  {
    id: 'no-solution-called-zero',
    title: 'No solution reported as x = 0',
    description: 'When the variable cancels and a false statement is left, gives a single numerical answer rather than "no solution".',
    studentMessage:
      'The xs cancelled and left something that is simply false, so no number can make the equation true. That is not x = 0 — putting 0 in does not work either. The answer is that there is no solution.',
    remediationConceptId: 'linear-equation-solutions',
  },
  {
    id: 'always-true-never-true-confused',
    title: 'Always true and never true exchanged',
    description:
      'When the variable cancels, reads a true statement as "no solution" or a false one as "infinitely many" — the two outcomes the wrong way round.',
    studentMessage:
      'When the xs cancel, read what is left. If it is true, like 7 = 7, then every number works. If it is false, like 7 = 9, then no number does. You have those two the wrong way round.',
    remediationConceptId: 'linear-equation-solutions',
  },
  {
    id: 'relative-frequency-wrong-total',
    title: 'Divided by the wrong total',
    description: 'Computes a relative frequency out of the grand total when the question asked for a proportion within one row or column.',
    studentMessage:
      'Check which group the question is asking about. "What percentage of the girls" means the girls are the whole — divide by the number of girls, not by everyone surveyed.',
    remediationConceptId: 'two-way-tables',
  },
  {
    id: 'two-way-table-margin-misread',
    title: 'The wrong margin of the table read',
    description: 'Answers with a row total where a column total was asked for, or the reverse.',
    studentMessage:
      'Check which way the question runs. Reading across a row answers "of this group, how many?"; reading down a column answers "of everyone, how many gave this answer?".',
    remediationConceptId: 'two-way-tables',
  },
  {
    id: 'rate-change-not-per-unit',
    title: 'Total change given instead of the rate',
    description: 'Reports how much y changed across a step of several units, rather than the change per single unit of x.',
    studentMessage:
      'A rate of change is per *one* unit of x. Here x jumped by more than one, so divide the change in y by the change in x before you report it.',
    remediationConceptId: 'linear-functions',
  },
  {
    id: 'nonlinear-called-linear',
    title: 'Any rising pattern called linear',
    description: 'Treats a relationship as linear because it increases, without checking that equal steps in x give equal steps in y.',
    studentMessage:
      'Rising is not enough. A linear relationship adds the *same* amount each time — check the differences between consecutive y-values, and if they change, it is not linear.',
    remediationConceptId: 'comparing-functions',
  },
  {
    id: 'functions-compared-by-intercept',
    title: 'Two functions compared by their starting values',
    description: 'Answers "which grows faster" with whichever function is ahead at x = 0.',
    studentMessage:
      'That is which one starts ahead, which is a different question. Growth is about the *rate* — how much y gains per unit of x — and the faster one overtakes the head start eventually.',
    remediationConceptId: 'comparing-functions',
  },
  {
    id: 'linear-form-misjudged',
    title: 'A rule judged linear by its look',
    description: 'Calls a rule linear because it is short and contains an x, without checking the power x is raised to.',
    studentMessage:
      'Look at what happens to x. Linear means x on its own, to the first power — squared, cubed, under a fraction line or up in an exponent all bend the graph.',
    remediationConceptId: 'comparing-functions',
  },

  /* -------------------------------------------------------------- algebra I */
  {
    id: 'number-set-too-narrow',
    title: 'A number placed in the wrong family',
    description: 'Names a set that does not contain the number, usually one further down the nesting than it belongs.',
    studentMessage:
      'Work out what the number actually equals first, then ask which is the smallest family holding it. The sets nest, so a natural number is also an integer, a rational and a real.',
    remediationConceptId: 'real-numbers',
  },
  {
    id: 'root-assumed-irrational',
    title: 'Every square root taken to be irrational',
    description: 'Calls a root irrational without checking whether the number under it is a perfect square.',
    studentMessage:
      'A root sign does not make a number irrational. √16 is 4 — a counting number. Check for a perfect square underneath before deciding.',
    remediationConceptId: 'real-numbers',
  },
  {
    id: 'property-misnamed',
    title: 'The wrong property named',
    description: 'Confuses commutative, associative and distributive when justifying a step.',
    studentMessage:
      'Look at what actually moved. Commutative changes the order, associative moves the brackets without changing the order, and distributive multiplies across a bracket.',
    remediationConceptId: 'real-numbers',
  },
  {
    id: 'term-not-multiplied',
    title: 'A term missed when clearing denominators',
    description: 'Multiplies the fractions by the common denominator but leaves the whole-number terms untouched.',
    studentMessage:
      'Multiplying one side means multiplying *every* term on it, including the ones with no denominator. Miss one and you have solved a different equation.',
    remediationConceptId: 'equations-with-fractions',
  },
  {
    id: 'constant-left-in-numerator',
    title: 'The numerator freed but never unpacked',
    description: 'Clears the denominator correctly, then reports the value of the whole numerator as the value of x.',
    studentMessage:
      'You have found what the numerator equals, not what x equals. There is still a constant sitting beside the x — move it across.',
    remediationConceptId: 'equations-with-fractions',
  },
  {
    id: 'compound-bound-swapped',
    title: 'The other bound reported',
    description: 'Solves the three-part inequality correctly but gives the upper bound where the lower was asked for, or the reverse.',
    studentMessage:
      'Both bounds are right — check which one the question wanted. The lower bound is the number x must exceed; the upper is the one it must stay below.',
    remediationConceptId: 'compound-inequalities',
  },
  {
    id: 'and-or-inequality-swapped',
    title: '"And" and "or" the wrong way round',
    description: 'Treats an intersection as a union or the reverse, giving two pieces where one stretch was meant.',
    studentMessage:
      '"And" needs both conditions at once, so the answer is the overlap — one stretch. "Or" needs only one, so the answer is both pieces together.',
    remediationConceptId: 'compound-inequalities',
  },
  {
    id: 'compound-emptiness-misjudged',
    title: 'A compound inequality wrongly called empty or universal',
    description: 'Concludes there is no solution, or that every number qualifies, where the conditions do in fact define a region.',
    studentMessage:
      'Draw both conditions on a number line before deciding. An "and" is empty only when the two never overlap, and an "or" covers everything only when between them they leave no gap.',
    remediationConceptId: 'compound-inequalities',
  },
  {
    id: 'point-slope-sign-kept',
    title: 'The sign read off the page in point-slope form',
    description: 'Reads x + 4 as a point coordinate of 4, taking the printed sign rather than undoing the subtraction.',
    studentMessage:
      'Point-slope form subtracts the coordinate: y − y₁ = m(x − x₁). A plus sign in the bracket therefore means the coordinate was *negative* — x + 4 came from x − (−4).',
    remediationConceptId: 'linear-equation-forms',
  },
  {
    id: 'point-taken-as-intercept',
    title: 'A point on the line taken as the y-intercept',
    description: 'Reports the y-coordinate of the given point as c, without converting to slope-intercept form.',
    studentMessage:
      'That point is on the line but it is not where the line crosses the y-axis — unless its x-coordinate happens to be 0. Expand the bracket and get y on its own first.',
    remediationConceptId: 'linear-equation-forms',
  },
  {
    id: 'wrong-intercept-read',
    title: 'The other intercept given',
    description: 'Sets the wrong variable to zero, finding the x-intercept where the y-intercept was asked for or the reverse.',
    studentMessage:
      'Check which axis the question means. A point on the x-axis has y = 0; a point on the y-axis has x = 0. Set the *other* variable to zero from the one you are looking for.',
    remediationConceptId: 'linear-equation-forms',
  },
  {
    id: 'variance-not-rooted',
    title: 'The variance given instead of the standard deviation',
    description: 'Stops after averaging the squared deviations, leaving the answer in squared units.',
    studentMessage:
      'That is the variance — one step short, and in squared units. Take its square root to get back to the units the data was measured in.',
    remediationConceptId: 'standard-deviation',
  },
  {
    id: 'spread-judged-by-centre',
    title: 'Spread judged from the average',
    description: 'Compares two data sets by their means when the question is about how spread out they are.',
    studentMessage:
      'Two sets can share a mean and look nothing alike. Spread is about how far the values sit *from* the mean, which the mean itself cannot tell you.',
    remediationConceptId: 'standard-deviation',
  },
  {
    id: 'zero-deviation-misread',
    title: 'A standard deviation of zero misread',
    description: 'Takes zero spread to mean the values are evenly distributed, or that the mean itself is zero.',
    studentMessage:
      'Standard deviation is an average distance from the mean, and an average of distances is zero only when every distance is. That means every value equals the mean.',
    remediationConceptId: 'standard-deviation',
  },
  {
    id: 'left-unfactored',
    title: 'Correct value, not factored',
    description: 'Gives an expression equal to the answer but still written as a sum, when factorising was the task.',
    studentMessage:
      'That is the right expression, but it is still a sum. Factorising means writing it as things multiplied together — a product of brackets.',
    remediationConceptId: 'factoring',
  },
  {
    id: 'sign-error-in-expansion',
    title: 'Signs lost in the brackets',
    description: 'Chooses a factorisation whose numbers are right but whose signs are not.',
    studentMessage:
      'The numbers are right; the signs are not. Expand your brackets and check both the x term and the constant — a wrong sign shows up in one of them.',
    remediationConceptId: 'factoring',
  },
  {
    id: 'binomial-square-no-middle-term',
    title: 'Middle term missing from a squared bracket',
    description: 'Expands (a + b)² as a² + b², squaring each term instead of multiplying the bracket by itself.',
    studentMessage:
      '(a + b)² means (a + b)(a + b), and every term in the first bracket meets every term in the second. That produces a middle term: a² + 2ab + b².',
    remediationConceptId: 'polynomials',
  },
  {
    id: 'only-one-root-given',
    title: 'One root found, the other missed',
    description: 'Solves a quadratic and reports a single solution where there are two.',
    studentMessage:
      'A quadratic usually has *two* solutions — the curve crosses the axis twice. Check whether the other factor also gives zero.',
    remediationConceptId: 'quadratics',
  },
  {
    id: 'square-root-sign-missed',
    title: 'Negative square root forgotten',
    description: 'Solves x² = k as x = √k only, losing the negative solution.',
    studentMessage:
      'Both a positive and a negative number square to give the same result. x² = 25 has solutions 5 *and* −5.',
    remediationConceptId: 'quadratics',
  },
  {
    id: 'zero-product-misapplied',
    title: 'Zero product rule used on a non-zero product',
    description: 'Sets each factor equal to the number on the right instead of rearranging to zero first.',
    studentMessage:
      'The rule only works against zero: if a product is 0, one of the factors must be 0. Move everything to one side first.',
    remediationConceptId: 'quadratics',
  },
  {
    id: 'formula-denominator-partial',
    title: 'Only part of the numerator divided',
    description: 'In the quadratic formula, divides only the root term by 2a rather than the whole numerator.',
    studentMessage:
      'The whole of −b ± √(b² − 4ac) sits over 2a. The −b is divided too, not just the square root.',
    remediationConceptId: 'quadratic-formula',
  },
  {
    id: 'discriminant-sign-misread',
    title: 'Discriminant read the wrong way',
    description: 'Concludes there are two roots from a negative discriminant, or none from a positive one.',
    studentMessage:
      'A positive discriminant means two real roots, zero means exactly one, and negative means none — you cannot take the square root of a negative.',
    remediationConceptId: 'quadratic-formula',
  },
  {
    id: 'vertex-sign-flipped',
    title: 'Sign of the vertex read straight off the bracket',
    description: 'Reads y = (x − 3)² + 2 as having its vertex at x = −3.',
    studentMessage:
      'The bracket is zero when x = 3, and that is where the vertex sits. The sign inside the bracket is the opposite of the x-coordinate.',
    remediationConceptId: 'quadratic-graphs',
  },
  {
    id: 'radical-added-termwise',
    title: 'Roots added as though they combined',
    description: 'Computes √a + √b as √(a + b), or adds unlike radicals as if they were like terms.',
    studentMessage:
      'Roots do not add like that. √9 + √16 is 3 + 4 = 7, but √25 is 5 — different numbers. Only *like* radicals can be collected.',
    remediationConceptId: 'radicals',
  },
  {
    id: 'radical-not-fully-simplified',
    title: 'A square factor left inside the root',
    description: 'Leaves a perfect-square factor under the radical sign.',
    studentMessage:
      'There is still a perfect square inside. Pull out the largest one you can: √72 = √36 × √2 = 6√2.',
    remediationConceptId: 'radicals',
  },
  {
    id: 'exponential-treated-as-linear',
    title: 'Repeated growth added instead of multiplied',
    description: 'Applies a growth rate by adding a fixed amount each period rather than multiplying.',
    studentMessage:
      'Exponential growth multiplies by the same factor each time, so the amount added gets bigger every period. Adding a fixed amount would make it linear.',
    remediationConceptId: 'exponential-functions',
  },
  {
    id: 'sequence-rule-confused',
    title: 'Common difference and common ratio swapped',
    description: 'Adds where the sequence multiplies, or multiplies where it adds.',
    studentMessage:
      'Check what takes you from one term to the next. If you *add* the same amount it is arithmetic; if you *multiply* by the same amount it is geometric.',
    remediationConceptId: 'sequences',
  },
  {
    id: 'rational-terms-cancelled',
    title: 'Terms cancelled instead of factors',
    description: 'Cancels a term that is added rather than a factor that is multiplied, such as reducing (x + 3)/3 to x.',
    studentMessage:
      'Only *factors* cancel — things being multiplied. In (x + 3)/3 the 3 is being added, so it cannot go. Factorise first, then cancel whole brackets.',
    remediationConceptId: 'rational-expressions',
  },
  {
    id: 'absolute-value-one-solution',
    title: 'Only the positive case solved',
    description: 'Solves an absolute value statement in its positive case only, missing the negative one.',
    studentMessage:
      'Absolute value is distance from zero, and there are always two directions to go. |x| = 7 means x = 7 *or* x = −7 — and an inequality has a second case for the same reason, so you have found half of it.',
    remediationConceptId: 'absolute-value',
  },
  {
    id: 'absolute-value-negative-result',
    title: 'A negative absolute value accepted',
    description: 'Produces a solution to |x| = −k, or reports a negative absolute value.',
    studentMessage:
      'An absolute value is a distance, so it can never be negative. An equation like |x| = −4 has no solution at all.',
    remediationConceptId: 'absolute-value',
  },
  {
    id: 'absolute-value-and-or-swapped',
    title: '"And" and "or" exchanged in an absolute value inequality',
    description: 'Joins the two cases of |x| < k with "or" and the two cases of |x| > k with "and".',
    studentMessage:
      'Read it as distance. |x| < k asks for points *close* to zero, so both conditions must hold at once — one band, joined by "and". |x| > k asks for points *far* from zero in either direction, which is two separate pieces joined by "or".',
    remediationConceptId: 'absolute-value',
  },
  {
    id: 'absolute-value-second-case-not-reversed',
    title: 'The second case written without reversing',
    description: 'Turns |x| < k into x < k and x < −k, negating the number but leaving the inequality pointing the same way.',
    studentMessage:
      'The second case is not just the negative of the number — it is the whole inequality flipped. |x| < 5 means −5 < x < 5, so the lower end reads x > −5, not x < −5.',
    remediationConceptId: 'absolute-value',
  },
  {
    id: 'radical-product-not-rooted',
    title: 'Radicands multiplied and the root dropped',
    description: 'Computes √a × √b by multiplying a and b and reporting that product, without taking the root of it.',
    studentMessage:
      'Multiplying under the root keeps it under the root: √3 × √12 = √36, and only then does the root come off to give 6. The product of the radicands is not the answer by itself.',
    remediationConceptId: 'radicals',
  },
  {
    id: 'geometric-term-index-off-by-one',
    title: 'Multiplied by the ratio one time too many',
    description: 'Uses a·rⁿ for the nth term of a geometric sequence, where the first term needs no multiplication at all.',
    studentMessage:
      'The first term has been multiplied by the ratio *zero* times, so the nth term uses r to the power n − 1. Test your rule on n = 1: it should hand back the first term unchanged.',
    remediationConceptId: 'sequences',
  },

  /* ----------------------------------------------- algebra I: the new ground */
  {
    id: 'wrong-letter-isolated',
    title: 'The wrong letter left on its own',
    description: 'Rearranges the formula correctly but for a different variable than the one asked for.',
    studentMessage:
      'Check which letter the question wants alone. The rearrangement is sound — it just answers a different question than the one asked.',
    remediationConceptId: 'literal-equations',
  },
  {
    id: 'literal-operation-inverted',
    title: 'A letter moved by the wrong operation',
    description: 'Moves a multiplying letter across the equals sign by multiplying, or a divided one by dividing.',
    studentMessage:
      'To move something across, undo what it is doing. A letter that multiplies is removed by dividing, and one that divides is removed by multiplying — the same rule as with numbers, and the letters change nothing about it.',
    remediationConceptId: 'literal-equations',
  },
  {
    id: 'literal-partial-division',
    title: 'Only part of the side divided',
    description: 'Divides one term of a side by a factor and leaves the other terms untouched.',
    studentMessage:
      'Dividing applies to the *whole* side, not to the term nearest the sign. If you divide by 2, every term on that side is divided by 2 — which is why the subtraction usually has to happen first.',
    remediationConceptId: 'literal-equations',
  },
  {
    id: 'coefficient-not-divided-out',
    title: 'The last division never made',
    description: 'Solves ax ≥ k as far as isolating the ax term and then reports k, without dividing by a.',
    studentMessage:
      'You have got as far as the x-term, but x is still multiplied by something. One more step: divide both sides by that coefficient — and if it is negative, the inequality turns round as well.',
    remediationConceptId: 'inequalities',
  },
  {
    id: 'domain-and-range-swapped',
    title: 'Domain and range exchanged',
    description: 'Reports the outputs when asked for the inputs, or the reverse.',
    studentMessage:
      'Domain is what goes *in* — the x-values the function accepts. Range is what comes *out* — the y-values it can produce. You have described the other one.',
    remediationConceptId: 'domain-and-range',
  },
  {
    id: 'domain-restriction-ignored',
    title: 'Every real number allowed',
    description: 'Gives the domain as all real numbers without checking for a zero denominator or a negative under a root.',
    studentMessage:
      'Most functions accept everything, which is exactly why the exceptions are worth hunting for. A denominator may not be zero and a square root may not take a negative, so check for both before saying "all real numbers".',
    remediationConceptId: 'domain-and-range',
  },
  {
    id: 'parabola-range-unbounded',
    title: 'The range of a parabola taken as everything',
    description: 'Gives the range of a quadratic as all real numbers, ignoring the vertex that stops it.',
    studentMessage:
      'A parabola turns around. Above its vertex it goes on forever, but below it there is nothing at all — so the range starts (or stops) at the y-value of the vertex.',
    remediationConceptId: 'domain-and-range',
  },
  {
    id: 'half-plane-wrong-side',
    title: 'The wrong side of the boundary shaded',
    description: 'Shades above the line for a "less than" inequality, or below it for a "greater than" one.',
    studentMessage:
      'Do not read the side off the inequality sign — read it off a point. Pick any point not on the line, usually (0, 0), and put it into the inequality: if it is true, shade the side it is on; if not, shade the other.',
    remediationConceptId: 'two-variable-inequalities',
  },
  {
    id: 'strict-boundary-drawn-solid',
    title: 'A strict boundary drawn solid',
    description: 'Draws a solid boundary line for < or >, or a dashed one for ≤ or ≥.',
    studentMessage:
      'The line itself is where the two sides are *equal*. If the inequality allows equality (≤ or ≥) the line is part of the answer and is drawn solid; if it does not (< or >), the line is excluded and drawn dashed — the same idea as the open circle on a number line.',
    remediationConceptId: 'two-variable-inequalities',
  },
  {
    id: 'test-point-on-boundary',
    title: 'A test point taken from the line itself',
    description: 'Chooses a point that lies on the boundary, which satisfies the equation and so decides nothing.',
    studentMessage:
      'That point is on the line, so it makes both sides equal and cannot tell the two halves apart. Pick a point clearly off the line — (0, 0) whenever the line does not pass through it.',
    remediationConceptId: 'two-variable-inequalities',
  },
  {
    id: 'half-coefficient-not-squared',
    title: 'Half the coefficient added without squaring it',
    description: 'Adds b/2 to complete the square, rather than (b/2)².',
    studentMessage:
      'Halve the x-coefficient to find what goes *inside* the bracket, then square that to find what has to be added outside. Those are two different numbers, and only the squared one balances the expression.',
    remediationConceptId: 'completing-the-square',
  },
  {
    id: 'square-completed-without-balancing',
    title: 'A number added and never taken back',
    description: 'Writes (x + b/2)² without subtracting the (b/2)² that creating it introduced.',
    studentMessage:
      'Expanding (x + b/2)² gives you a constant you did not start with, so it has to be subtracted straight back off. Rewriting an expression may change its shape but never its value — expand your answer and check it returns to the original.',
    remediationConceptId: 'completing-the-square',
  },
  {
    id: 'constant-swept-into-bracket',
    title: 'The constant taken inside the bracket',
    description: 'Factors the leading coefficient out of all three terms, including the constant, which it does not divide.',
    studentMessage:
      'Only the first two terms have the leading number in them, so only those two go inside the bracket. Expand what you wrote and the constant comes back multiplied — a different expression from the one you started with.',
    remediationConceptId: 'completing-the-square',
  },
  {
    id: 'leading-coefficient-not-factored',
    title: 'Completing the square with a coefficient still in front',
    description: 'Applies the halve-and-square rule to ax² + bx + c without first taking the a outside.',
    studentMessage:
      'Halving the coefficient works only when the x² stands alone. Factor the leading number out of the first two terms first, complete the square inside the bracket, and then multiply back out.',
    remediationConceptId: 'completing-the-square',
  },

  /* --------------------------------------------------------------- geometry */
  {
    id: 'converse-assumed-true',
    title: 'The converse assumed to follow',
    description: 'Treats "if A then B" as licensing "if B then A".',
    studentMessage:
      'A statement and its converse are different claims. Every square is a rectangle; not every rectangle is a square. Proving one direction says nothing about the other.',
    remediationConceptId: 'geometric-proof',
  },
  {
    id: 'measurement-taken-as-proof',
    title: 'A measurement offered as a proof',
    description: 'Justifies a general claim by checking one drawn example.',
    studentMessage:
      'Measuring this triangle tells you about this triangle. A proof has to give a reason that holds for every one of them, including the ones nobody has drawn.',
    remediationConceptId: 'geometric-proof',
  },
  {
    id: 'contrapositive-confused-with-converse',
    title: 'The converse offered as the contrapositive',
    description: 'Reverses "if A then B" without negating, and calls the result the contrapositive.',
    studentMessage:
      'The contrapositive both reverses *and* negates: "if not B then not A". Reversing alone gives the converse, which is a different claim and need not be true.',
    remediationConceptId: 'conditional-statements',
  },
  {
    id: 'inverse-assumed-true',
    title: 'The inverse assumed to follow',
    description: 'Treats "if not A then not B" as licensing the original conditional.',
    studentMessage:
      'Negating both halves gives the inverse, and the inverse is the converse turned around — so it carries exactly as much weight as the converse does, which is none. Only the contrapositive comes free.',
    remediationConceptId: 'conditional-statements',
  },
  {
    id: 'counterexample-count-misjudged',
    title: 'One counterexample thought not to be enough',
    description: 'Asks for several failing cases before a universal claim is considered refuted.',
    studentMessage:
      'A claim about *every* case is destroyed by one case where it fails. Finding a second adds nothing — the statement was already false after the first.',
    remediationConceptId: 'conditional-statements',
  },
  {
    id: 'inductive-taken-as-deductive',
    title: 'A pattern of examples called deduction',
    description: 'Labels reasoning from observed cases to a general rule as deductive.',
    studentMessage:
      'Reasoning from examples to a rule is *inductive*: it suggests, and it can be wrong. Deductive reasoning starts from statements already accepted and forces the conclusion, which is what a proof does.',
    remediationConceptId: 'conditional-statements',
  },
  {
    id: 'co-interior-taken-equal',
    title: 'Co-interior angles treated as equal',
    description: 'Assumes same-side interior angles are equal rather than adding to 180°.',
    studentMessage:
      'Those two are on the *same* side of the transversal, so they make a straight line together: they add to 180° rather than matching.',
    remediationConceptId: 'angle-relationships',
  },
  {
    id: 'exterior-angle-taken-as-interior',
    title: 'The interior angle given where the exterior was asked for',
    description: 'Stops at the third angle of the triangle instead of the angle beside it, outside.',
    studentMessage:
      'That is the angle *inside* the triangle. The exterior angle is what is left of the straight line beside it — subtract yours from 180°, or add the two angles at the other corners.',
    remediationConceptId: 'angle-relationships',
  },
  {
    id: 'third-angle-not-forced',
    title: 'Two equal angles thought not to be enough',
    description: 'Does not notice that matching two angles forces the third, since all three sum to 180°.',
    studentMessage:
      'Only two angles of a triangle are free. Fix two and the third has no choice, so two matching angles already make the shapes the same — that is all similarity asks for.',
    remediationConceptId: 'angle-relationships',
  },
  {
    id: 'angle-pair-misnamed',
    title: 'The wrong angle pair identified',
    description: 'Confuses corresponding, alternate and co-interior positions.',
    studentMessage:
      'Look at where the two angles sit. Same corner at each crossing means corresponding and equal; opposite sides of the transversal, between the lines, means alternate and equal.',
    remediationConceptId: 'angle-relationships',
  },
  {
    id: 'ssa-accepted-as-congruence',
    title: 'Two sides and a non-included angle accepted',
    description: 'Treats SSA as a congruence criterion, which it is not.',
    studentMessage:
      'With two sides and an angle that is not between them, two different triangles can be built from the same three measurements. The angle has to sit *between* the two sides.',
    remediationConceptId: 'triangle-congruence',
  },
  {
    id: 'angles-alone-give-congruence',
    title: 'Three equal angles taken as congruence',
    description: 'Concludes that equal angles make triangles congruent rather than similar.',
    studentMessage:
      'Equal angles fix the *shape* but not the size — a photograph and its enlargement have identical angles. That is similarity; congruence needs at least one side.',
    remediationConceptId: 'angle-relationships',
  },
  {
    id: 'triangle-inequality-ignored',
    title: 'Impossible side lengths accepted',
    description: 'Accepts three lengths where two of them together do not exceed the third.',
    studentMessage:
      'Any two sides must be longer than the third, or they cannot reach far enough to meet. Check the two shortest against the longest.',
    remediationConceptId: 'triangle-properties',
  },
  {
    id: 'scale-factor-inverted',
    title: 'Scale factor applied the wrong way',
    description: 'Divides when enlarging, or multiplies when reducing.',
    studentMessage:
      'Check which figure you are going to. Going from the small one to the large one multiplies; the other way divides — and the answer should be larger or smaller accordingly.',
    remediationConceptId: 'scale-drawings',
  },
  {
    id: 'area-scaled-linearly',
    title: 'Area scaled by the length factor',
    description: 'Multiplies area by k rather than by k² when a figure is enlarged.',
    studentMessage:
      'Doubling every length gives four times the area, not twice — the enlargement applies in both directions at once. Area scales by k², and volume by k³.',
    remediationConceptId: 'scale-drawings',
  },
  {
    id: 'side-splitter-whole-for-part',
    title: 'The whole side used where a piece was meant',
    description: 'Writes the side-splitter proportion with a full side on one row and a part on the other.',
    studentMessage:
      'Keep the two rows consistent: either part over part on both sides, or whole over whole on both. Mixing a piece with a full side compares two different things.',
    remediationConceptId: 'triangle-proportionality',
  },
  {
    id: 'midsegment-not-halved',
    title: 'A midsegment given the length of the side it joins',
    description: 'Reports the parallel side rather than half of it, or doubles when halving was wanted.',
    studentMessage:
      'The segment joining two midpoints is parallel to the third side and exactly *half* its length. Check which of the two you were given before multiplying or dividing.',
    remediationConceptId: 'triangle-proportionality',
  },
  {
    id: 'parallelogram-diagonals-taken-equal',
    title: 'Diagonals of a parallelogram assumed equal',
    description: 'Claims equal diagonals for any parallelogram rather than for a rectangle.',
    studentMessage:
      'Every parallelogram has diagonals that *bisect* each other, but they are equal only when the parallelogram is a rectangle. Bisecting and being equal are different guarantees.',
    remediationConceptId: 'quadrilateral-properties',
  },
  {
    id: 'rhombus-diagonals-taken-equal',
    title: 'Perpendicular diagonals assumed equal',
    description: 'Confuses the rhombus guarantee (perpendicular diagonals) with the rectangle one (equal diagonals).',
    studentMessage:
      'A rhombus has diagonals that cross at right angles; a rectangle has diagonals of equal length. A square is the shape that manages both, which is why it is the one where the two get mixed up.',
    remediationConceptId: 'quadrilateral-properties',
  },
  {
    id: 'kite-taken-as-parallelogram',
    title: 'A kite treated as a parallelogram',
    description: 'Assumes opposite sides of a kite are parallel or equal.',
    studentMessage:
      'A kite has two *adjacent* pairs of equal sides, not two opposite ones, so nothing in it has to be parallel. What it does guarantee is one pair of equal angles and diagonals that meet at right angles.',
    remediationConceptId: 'quadrilateral-properties',
  },
  {
    id: 'trapezoid-midsegment-not-averaged',
    title: 'The midsegment of a trapezium added, not averaged',
    description: 'Sums the two parallel sides without halving.',
    studentMessage:
      'The midsegment sits halfway between the two parallel sides, so its length is their *average*: add them and divide by two. The sum alone is longer than either side, which cannot be right.',
    remediationConceptId: 'quadrilateral-properties',
  },
  {
    id: 'chord-bisector-halved-twice',
    title: 'Half a chord reported as the whole one',
    description: 'Gives the half-chord found from the right triangle instead of doubling it, or the reverse.',
    studentMessage:
      'The perpendicular from the centre cuts the chord in half, so the right triangle contains only *half* of it. Double that half to answer for the whole chord.',
    remediationConceptId: 'chords-secants',
  },
  {
    id: 'intersecting-chords-added',
    title: 'Chord pieces added instead of multiplied',
    description: 'Applies a sum where the intersecting-chords theorem calls for a product.',
    studentMessage:
      'The theorem is about *products*: the two pieces of one chord multiply to the same value as the two pieces of the other. Adding them gives the chord lengths, which are not generally equal.',
    remediationConceptId: 'chords-secants',
  },
  {
    id: 'secant-external-part-used-whole',
    title: 'The outside piece of a secant used as the whole',
    description: 'Multiplies the two external segments rather than external × whole.',
    studentMessage:
      'For a secant the two lengths are the outside piece and the *whole* secant from the point to the far side of the circle — not the outside piece and the chord inside. Add them before multiplying.',
    remediationConceptId: 'chords-secants',
  },
  {
    id: 'tangent-length-not-squared',
    title: 'The tangent length used unsquared',
    description: 'Sets the tangent length equal to the secant product instead of its square.',
    studentMessage:
      'A tangent is the limiting case where the two secant pieces have merged, so both are the tangent length: the product is t × t. Take the square root of the secant product to recover t.',
    remediationConceptId: 'chords-secants',
  },
  {
    id: 'polygon-angle-sum-wrong',
    title: 'Wrong total for the interior angles',
    description: 'Uses 360° for the interior angles of any polygon, or forgets the (n − 2).',
    studentMessage:
      'A polygon splits into n − 2 triangles, each holding 180°. The *exterior* angles are the ones that always come to 360°.',
    remediationConceptId: 'polygons',
  },
  {
    id: 'inscribed-angle-not-halved',
    title: 'Inscribed angle not halved',
    description: 'Takes an inscribed angle as equal to the central angle on the same arc.',
    studentMessage:
      'An angle at the circumference is exactly half the angle at the centre standing on the same arc. Halve it.',
    remediationConceptId: 'circle-theorems',
  },
  {
    id: 'tangent-angle-missed',
    title: 'The right angle at a tangent overlooked',
    description: 'Fails to use the fact that a tangent meets the radius at 90°.',
    studentMessage:
      'A tangent touches the circle at exactly one point, and the radius to that point is perpendicular to it. That right angle is usually what the question is built on.',
    remediationConceptId: 'circle-theorems',
  },
  {
    id: 'distance-not-rooted',
    title: 'Distance left as the sum of squares',
    description: 'Computes the squared distance and reports it, or omits the squaring entirely.',
    studentMessage:
      'The distance formula is the Pythagorean theorem in disguise: square both differences, add, and then take the square root.',
    // Grade 8 (`coordinate-distance`), not Grade 10: the repair is the
    // triangle under the two points, which is where this is first taught.
    remediationConceptId: 'coordinate-distance',
  },
  {
    id: 'midpoint-differenced',
    title: 'Midpoint found by subtracting',
    description: 'Subtracts the coordinates instead of averaging them.',
    studentMessage:
      'A midpoint is an average, not a difference: add the two x values and halve, then do the same for y. The answer should sit between the points.',
    remediationConceptId: 'coordinate-geometry',
  },
  {
    id: 'trig-ratio-confused',
    title: 'The wrong trigonometric ratio chosen',
    description: 'Uses sine where cosine or tangent is required, or divides the ratio the wrong way up.',
    studentMessage:
      'Label the sides first — opposite, adjacent, hypotenuse — relative to the angle you are given. Then SOH-CAH-TOA tells you which ratio has the two sides you know.',
    remediationConceptId: 'trig-ratios',
  },
  {
    id: 'hypotenuse-misidentified',
    title: 'The wrong side called the hypotenuse',
    description: 'Labels a side other than the one opposite the right angle as the hypotenuse.',
    studentMessage:
      'The hypotenuse is always opposite the right angle, and always the longest side. Find the right angle first, then look straight across from it.',
    remediationConceptId: 'trig-ratios',
  },
  {
    id: 'special-triangle-ratio-swapped',
    title: 'Special triangle ratio used the wrong way round',
    description: 'Multiplies where the ratio divides, in a 45-45-90 or 30-60-90 triangle.',
    studentMessage:
      'Check against the picture: the hypotenuse is the longest side, so going to it multiplies and coming from it divides.',
    remediationConceptId: 'special-triangles',
  },

  {
    id: 'construction-purpose-confused',
    title: 'One construction taken for another',
    description: 'Names the wrong construction for a described set of compass arcs.',
    studentMessage:
      'Look at where the compass point was placed. Arcs swung from the two *ends of a segment* meet on its perpendicular bisector; arcs swung from two points on the *arms of an angle* meet on the bisector of that angle.',
    remediationConceptId: 'constructions',
  },
  {
    id: 'construction-arcs-too-small',
    title: 'Compass opened less than half the segment',
    description: 'Accepts an arc radius under half the segment, where the two arcs never meet.',
    studentMessage:
      'If the opening is less than half of AB, the two arcs never cross and there is no second point to draw the line through. Open the compass wider than half — anything wider works, which is why no measuring is needed.',
    remediationConceptId: 'constructions',
  },
  {
    id: 'equidistant-locus-confused',
    title: 'The wrong locus for "equidistant"',
    description: 'Swaps the perpendicular bisector and the angle bisector as the set of equidistant points.',
    studentMessage:
      'Equidistant from two *points* is the perpendicular bisector of the segment joining them. Equidistant from two *lines* is the bisector of the angle between them. Which two things is the distance being measured to?',
    remediationConceptId: 'constructions',
  },
  {
    id: 'bisector-not-halved',
    title: 'Bisecting without halving',
    description: 'Doubles, or leaves unchanged, an angle that a construction has bisected.',
    studentMessage:
      'To bisect is to cut into two equal parts. Each part is half of what you started with, so the number should come out smaller.',
    remediationConceptId: 'constructions',
  },
  {
    id: 'triangle-centre-confused',
    title: 'Circumcentre and incentre swapped',
    description: 'Attributes the meeting point of the perpendicular bisectors to the angle bisectors, or the reverse.',
    studentMessage:
      'The perpendicular bisectors of the sides meet at the circumcentre, equidistant from the three *vertices* — so it is the centre of the circle through them. The angle bisectors meet at the incentre, equidistant from the three *sides*.',
    remediationConceptId: 'constructions',
  },
  {
    id: 'sector-fraction-omitted',
    title: 'The whole circle used for a slice of it',
    description: 'Computes the full circumference or full area and ignores the angle of the sector.',
    studentMessage:
      'That is the whole circle. A sector is only the part the angle opens onto — the fraction θ/360 of it. Multiply by that fraction.',
    remediationConceptId: 'arc-sector',
  },
  {
    id: 'sector-fraction-inverted',
    title: 'The sector fraction taken upside down',
    description: 'Uses 360/θ where θ/360 is required.',
    studentMessage:
      'The fraction is θ ÷ 360: how much of a full turn the sector opens through. Written the other way up it comes out bigger than 1, which would make the slice larger than the circle it came from.',
    remediationConceptId: 'arc-sector',
  },
  {
    id: 'arc-and-sector-confused',
    title: 'Arc length and sector area swapped',
    description: 'Applies the circumference formula where the area is wanted, or the reverse.',
    studentMessage:
      'Decide first whether you are after a length or an area. An arc is a length, so it comes from the circumference 2πr; a sector is an area, so it comes from πr². The fraction θ/360 is the same either way.',
    remediationConceptId: 'arc-sector',
  },
  {
    id: 'sector-perimeter-radii-omitted',
    title: 'Perimeter of a sector given as its arc',
    description: 'Reports the arc length alone when the whole boundary of the sector is asked for.',
    studentMessage:
      'The arc is only the curved part. Walking round the edge of a sector you also travel out and back along two radii, so add 2r.',
    remediationConceptId: 'arc-sector',
  },
  {
    id: 'semicircle-angle-missed',
    title: 'The right angle in a semicircle overlooked',
    description: 'Fails to use the fact that an angle standing on a diameter is 90°.',
    studentMessage:
      'An angle at the circumference standing on a *diameter* is always 90° — it is the inscribed angle theorem with a central angle of 180°. That right angle is usually what the rest of the question rests on.',
    remediationConceptId: 'circle-theorems',
  },
  {
    id: 'cyclic-quadrilateral-not-supplementary',
    title: 'Opposite angles of a cyclic quadrilateral taken as equal',
    description: 'Repeats the given angle instead of subtracting it from 180°.',
    studentMessage:
      'Opposite angles of a quadrilateral inscribed in a circle add to 180°; they are supplementary, not equal. Subtract from 180.',
    remediationConceptId: 'circle-theorems',
  },
  {
    id: 'volume-scaled-as-area',
    title: 'Volume scaled by the square of the factor',
    description: 'Applies k² to a volume, where three dimensions are being stretched rather than two.',
    studentMessage:
      'Area involves two lengths and volume involves three, so an enlargement of factor k multiplies area by k² and volume by k³.',
    remediationConceptId: 'similarity',
  },
  {
    id: 'circle-equation-radius-not-rooted',
    title: 'r² read off as the radius',
    description: 'Reports the number on the right of the circle equation without taking its square root.',
    studentMessage:
      'The equation ends in r², not r. The number on the right is the radius *squared* — take its square root before calling it a radius.',
    remediationConceptId: 'coordinate-geometry',
  },
  {
    id: 'circle-equation-centre-sign-flipped',
    title: 'Centre of a circle read with the wrong sign',
    description: 'Takes the number inside the bracket as the coordinate, without reversing its sign.',
    studentMessage:
      'The form is (x − a)² + (y − b)² = r², and the operation in the bracket is a subtraction. So (x + 3)² means a = −3: the sign flips as it comes out.',
    remediationConceptId: 'coordinate-geometry',
  },
  {
    id: 'perpendicular-slope-not-negated',
    title: 'Reciprocal taken without the sign change',
    description: 'Turns the slope upside down but leaves its sign alone.',
    studentMessage:
      'Perpendicular slopes are *negative* reciprocals — flipped and then negated. Two lines both sloping upwards can never meet at a right angle.',
    remediationConceptId: 'coordinate-geometry',
  },
  {
    id: 'perpendicular-slope-not-inverted',
    title: 'Sign changed without flipping the slope',
    description: 'Negates the slope but does not take its reciprocal.',
    studentMessage:
      'Negating alone gives the mirror image of the line, not a perpendicular one. The two slopes must multiply to −1, so flip the fraction as well as changing the sign.',
    remediationConceptId: 'coordinate-geometry',
  },
  {
    id: 'inverse-trig-omitted',
    title: 'The ratio reported as the angle',
    description: 'Computes the ratio of two sides and gives that number as the angle.',
    studentMessage:
      'That number is the ratio, not the angle. sin⁻¹, cos⁻¹ and tan⁻¹ run the ratio backwards and hand you the angle that produced it.',
    remediationConceptId: 'trig-applications',
  },
  {
    id: 'elevation-depression-confused',
    title: 'Elevation or depression measured from the vertical',
    description: 'Takes the angle from the vertical, or confuses looking up with looking down.',
    studentMessage:
      'Both angles are measured from the *horizontal* — the line of sight tilted up for elevation, down for depression. Measuring from the vertical gives the other angle in the triangle, which is its complement.',
    remediationConceptId: 'trig-applications',
  },
  {
    id: 'trig-ratio-applied-inverted',
    title: 'The ratio applied the wrong way round',
    description: 'Multiplies by the ratio where the unknown is the denominator, or divides where it is the numerator.',
    studentMessage:
      'Look at where the unknown sits in the ratio. On top, you multiply; underneath, you divide the known side by the ratio. A sine or cosine is always less than 1, so multiplying by one shortens and dividing by one lengthens — check your answer against the picture.',
    remediationConceptId: 'trig-applications',
  },

  /* ------------------------------------------------------------ algebra II */
  {
    id: 'i-squared-taken-positive',
    title: 'i² treated as +1',
    description: 'Multiplies powers of i without applying i² = −1, or takes i² to be 1.',
    studentMessage:
      'i is defined as the number whose square is −1, so i² = −1. That minus sign is the whole point of i — without it nothing has been gained.',
    remediationConceptId: 'complex-numbers',
  },
  {
    id: 'complex-parts-combined',
    title: 'Real and imaginary parts added together',
    description: 'Collapses a + bi into a single number, as though the two parts could be combined.',
    studentMessage:
      'The two parts cannot merge — 3 + 2i is as finished as "3 metres and 2 seconds". Add real to real and imaginary to imaginary, and leave the answer in two parts.',
    remediationConceptId: 'complex-numbers',
  },
  {
    id: 'negative-radicand-taken-real',
    title: 'A negative under the root given a real value',
    description: 'Reports √(−9) as −3 or as 3, rather than as 3i.',
    studentMessage:
      'No real number squares to a negative, so this root is not real at all. Take out √(−1) = i first, and what is left is an ordinary root: √(−9) = 3i.',
    remediationConceptId: 'complex-numbers',
  },
  {
    id: 'remainder-theorem-sign-flipped',
    title: 'The remainder-theorem substitution reversed',
    description: 'Evaluates P(−a) when dividing by (x − a), or P(a) when dividing by (x + a).',
    studentMessage:
      'Substitute the value that makes the divisor *zero*. For x − 3 that is x = 3; for x + 3 it is x = −3. Set the bracket equal to zero and read the number off.',
    remediationConceptId: 'polynomial-division',
  },
  {
    id: 'missing-degree-not-held',
    title: 'A missing degree left out of the division',
    description: 'Omits a zero placeholder for an absent power, shifting every later coefficient.',
    studentMessage:
      'A polynomial with no x² term still has an x² place — write 0 there. Leaving the column out slides every coefficient after it one place along.',
    remediationConceptId: 'polynomial-division',
  },
  {
    id: 'end-behaviour-from-wrong-term',
    title: 'End behaviour read off the wrong term',
    description: 'Predicts what a polynomial does far from the origin from the constant or a middle term.',
    studentMessage:
      'Far out, the highest power dwarfs everything else — at x = 100, x³ is ten thousand times x. Only the leading term decides which way the ends go.',
    remediationConceptId: 'polynomial-functions',
  },
  {
    id: 'multiplicity-ignored',
    title: 'Repeated roots counted once',
    description: 'Counts distinct roots rather than roots with multiplicity, or expects every root to be a crossing.',
    studentMessage:
      'A repeated factor is a repeated root. (x − 2)² has 2 as a root twice, and at a doubled root the curve touches the axis and turns back rather than crossing it.',
    remediationConceptId: 'polynomial-functions',
  },
  {
    id: 'shift-direction-inverted',
    title: 'A horizontal shift taken the wrong way',
    description: 'Reads f(x + 3) as a shift right, and f(x − 3) as a shift left.',
    studentMessage:
      'Inside the bracket, things run backwards: f(x − 3) moves the graph *right* by 3, because x now has to be 3 bigger to give the same output. Ask which x produces the old value.',
    remediationConceptId: 'function-transformations',
  },
  {
    id: 'inside-outside-swapped',
    title: 'Inside and outside changes confused',
    description: 'Treats a change inside the bracket as vertical, or one outside it as horizontal.',
    studentMessage:
      'Outside the function acts on the *output*, so it moves the graph up and down. Inside acts on the *input*, so it moves it left and right — and backwards.',
    remediationConceptId: 'function-transformations',
  },
  {
    id: 'inverse-as-reciprocal',
    title: 'The inverse read as a reciprocal',
    description: 'Interprets the −1 in f⁻¹(x) as an exponent, giving 1 / f(x).',
    studentMessage:
      'That −1 is not a power. f⁻¹ is the function that undoes f, not one divided by it — the notation is unfortunate, and 1/f(x) is a different function entirely.',
    remediationConceptId: 'inverse-functions',
  },
  {
    id: 'inverse-not-swapped',
    title: 'Inverse found without exchanging the variables',
    description: 'Rearranges the rule but never swaps x and y, so the "inverse" is the original function rewritten.',
    studentMessage:
      'An inverse exchanges the roles of input and output. Swap x and y first, *then* solve for y — otherwise you have only rewritten the same function.',
    remediationConceptId: 'inverse-functions',
  },
  {
    id: 'asymptote-from-numerator',
    title: 'A vertical asymptote taken from the numerator',
    description: 'Solves the top of a fraction equal to zero when looking for a vertical asymptote.',
    studentMessage:
      'A vertical asymptote is where the function is undefined, and that is where the *bottom* is zero. Setting the top to zero finds the roots instead.',
    remediationConceptId: 'rational-functions',
  },
  {
    id: 'hole-called-asymptote',
    title: 'A cancelled factor reported as an asymptote',
    description: 'Reports every zero of the original denominator as an asymptote, including one that cancels.',
    studentMessage:
      'A factor appearing on the top and the bottom cancels, and leaves a single missing point rather than an asymptote. Factorise and cancel before deciding what each gap is.',
    remediationConceptId: 'rational-functions',
  },
  {
    id: 'extraneous-root-kept',
    title: 'An invented solution kept',
    description: 'Squares both sides and reports every resulting value without checking it in the original equation.',
    studentMessage:
      'Squaring both sides can create answers that were never there — 2 = −2 is false, and squaring makes it true. Put each answer back into the *original* equation and keep only the ones that survive.',
    remediationConceptId: 'radical-equations',
  },
  {
    id: 'root-isolated-too-late',
    title: 'Squared before the root stood alone',
    description: 'Squares an equation while the radical still has something added to it, leaving a root behind.',
    studentMessage:
      'Squaring only removes a root that stands by itself. (√x + 3)² is not x + 9 — get the radical alone on one side first.',
    remediationConceptId: 'radical-equations',
  },
  {
    id: 'log-of-sum-split',
    title: 'A logarithm split across an addition',
    description: 'Rewrites log(a + b) as log a + log b.',
    studentMessage:
      'The log laws turn *multiplication* into addition, not addition into addition: log(ab) = log a + log b. log(a + b) does not break up at all.',
    remediationConceptId: 'logarithms',
  },
  {
    id: 'log-quotient-divided',
    title: 'A logarithm of a quotient divided',
    description: 'Rewrites log(a / b) as log a ÷ log b.',
    studentMessage:
      'A quotient inside a log becomes a *subtraction* outside it: log(a/b) = log a − log b. Dividing the two logarithms is a different quantity altogether.',
    remediationConceptId: 'logarithms',
  },
  {
    id: 'log-base-confused',
    title: 'The base of the logarithm mixed up with the answer',
    description: 'Answers log_b(x) with the base, or reads every logarithm as base 10.',
    studentMessage:
      'Read it as a question: log₂(8) asks "2 to what power gives 8?" The base is what gets raised, and the answer is the power — here, 3.',
    remediationConceptId: 'logarithms',
  },
  {
    id: 'sine-cosine-coordinates-swapped',
    title: 'Sine and cosine read off the wrong axis',
    description: 'Takes the x-coordinate of the point on the unit circle as sine rather than cosine.',
    studentMessage:
      'On the unit circle the point is (cos θ, sin θ): cosine is across, sine is up. Alphabetical order matches coordinate order, which is worth remembering.',
    remediationConceptId: 'unit-circle',
  },
  {
    id: 'quadrant-sign-ignored',
    title: 'The sign in the quadrant overlooked',
    description: 'Uses the size given by the reference angle but keeps it positive where it should be negative.',
    studentMessage:
      'The reference angle gives the size; the quadrant gives the sign. In the second quadrant x is negative, so cosine is negative too — check the direction before writing the answer.',
    remediationConceptId: 'unit-circle',
  },
  {
    id: 'reference-angle-from-wrong-axis',
    title: 'The reference angle measured to the wrong axis',
    description: 'Measures the acute angle to the vertical axis rather than to the horizontal one.',
    studentMessage:
      'A reference angle is always measured to the x-axis, never to the y-axis. Find the nearer half of the *horizontal* axis — 0° or 180° — and measure to that.',
    remediationConceptId: 'unit-circle',
  },
  {
    id: 'degrees-radians-mixed',
    title: 'Degrees and radians mixed',
    description: 'Treats a value in radians as though it were in degrees, or converts in the wrong direction.',
    studentMessage:
      'Half a turn is 180°, and also π radians — two units for one thing. Multiplying by π/180 goes to radians, and by 180/π comes back.',
    remediationConceptId: 'unit-circle',
  },
  {
    id: 'period-read-as-coefficient',
    title: 'The period taken straight from the coefficient',
    description: 'Reports the period of sin(bx) as b rather than as 2π / b.',
    studentMessage:
      'A larger number inside makes the wave *shorter*, not longer — it drives the input round the circle faster. The period is 2π divided by that number.',
    remediationConceptId: 'trig-graphs',
  },
  {
    id: 'amplitude-taken-as-range',
    title: 'Amplitude confused with the full height',
    description: 'Gives the distance from the lowest point to the highest as the amplitude.',
    studentMessage:
      'Amplitude is measured from the middle line up to the peak — half of the total rise and fall. A wave running between −4 and 4 has amplitude 4, not 8.',
    remediationConceptId: 'trig-graphs',
  },
  /* ---------------------------------------------------------- precalculus */
  {
    id: 'identity-treated-as-equation',
    title: 'An identity mistaken for an equation',
    description: 'Tries to solve an identity for x, rather than showing that it holds for every x.',
    studentMessage:
      'An identity is not a question about which angle. It is a claim about *every* angle, so there is nothing to solve for — the work is turning one side into the other.',
    remediationConceptId: 'trig-identities',
  },
  {
    id: 'pythagorean-identity-rearranged-wrongly',
    title: 'sin² + cos² = 1 rearranged with the wrong sign',
    description: 'Turns sin²θ + cos²θ = 1 into cos²θ = 1 + sin²θ, adding where the rearrangement subtracts.',
    studentMessage:
      'The two squares *add* to 1, so each one is 1 − the other: cos²θ = 1 − sin²θ. Check it at θ = 0, where cos² is 1 and sin² is 0.',
    remediationConceptId: 'trig-identities',
  },
  {
    id: 'trig-function-distributed-over-sum',
    title: 'A trigonometric function spread across a sum',
    description: 'Writes sin(A + B) as sin A + sin B, treating sin as though it multiplied.',
    studentMessage:
      'sin is a function, not a factor, so it does not distribute. Try A = B = 30°: sin 60° is about 0.87, while sin 30° + sin 30° is exactly 1.',
    remediationConceptId: 'trig-identities',
  },
  {
    id: 'trig-solution-single',
    title: 'Only the calculator’s solution given',
    description: 'Reports the one angle an inverse function returns, missing the second solution in the revolution.',
    studentMessage:
      'The inverse function hands back one angle, but the circle passes each height twice per turn. Draw the horizontal line across the circle and find the second crossing too.',
    remediationConceptId: 'trig-equations',
  },
  {
    id: 'general-solution-period-wrong',
    title: 'The wrong period added to a general solution',
    description: 'Adds 180°n where the function repeats every 360°, or the reverse.',
    studentMessage:
      'Add the period of the function you actually have. Sine and cosine repeat every 360°; tangent repeats every 180°. The wrong one invents angles that are not solutions.',
    remediationConceptId: 'trig-equations',
  },
  {
    id: 'cosine-rule-terms-not-separated',
    title: 'The cosine rule read as one bracket',
    description: 'Computes (a² + b² − 2ab)·cos C, applying the cosine to the whole expression.',
    studentMessage:
      'Only the last term is multiplied by cos C: c² = a² + b² − 2ab·cos C. The a² and b² stand on their own, exactly as in Pythagoras — which is this rule at C = 90°.',
    remediationConceptId: 'law-of-sines-cosines',
  },
  {
    id: 'wrong-triangle-rule-chosen',
    title: 'The sine rule used where the cosine rule is needed',
    description: 'Reaches for a pair of opposite side and angle that the given measurements do not provide.',
    studentMessage:
      'The sine rule needs a side and the angle *opposite* it. With three sides, or with two sides and the angle between them, no such pair is known yet — that is the cosine rule’s case.',
    remediationConceptId: 'law-of-sines-cosines',
  },
  {
    id: 'ambiguous-case-missed',
    title: 'The obtuse triangle overlooked',
    description: 'Takes the acute angle an inverse sine returns without checking whether its supplement fits too.',
    studentMessage:
      'sin 40° and sin 140° are the same number, so an inverse sine cannot tell you which one you have. When the side opposite the known angle is the shorter one, check the obtuse triangle as well.',
    remediationConceptId: 'law-of-sines-cosines',
  },
  {
    id: 'term-given-for-sum',
    title: 'The nth term given where the sum was asked for',
    description: 'Reports the value of the last term instead of the total of the series.',
    studentMessage:
      'That is the last *term*, not the total. A series is the sequence added up — the term formula and the sum formula answer two different questions.',
    remediationConceptId: 'series',
  },
  {
    id: 'endless-sum-called-endless',
    title: 'Every endless sum assumed to grow without bound',
    description: 'Says an infinite series can have no total, because it has no last term.',
    studentMessage:
      'Endlessly many terms does not mean an endless total. When each term is a fixed fraction of the one before, the running total closes in on a number — 1/2 + 1/4 + 1/8 + … never passes 1.',
    remediationConceptId: 'series',
  },
  {
    id: 'geometric-ratio-as-difference',
    title: 'The common ratio found by subtracting',
    description: 'Subtracts consecutive terms of a geometric sequence, as though it were arithmetic.',
    studentMessage:
      'A geometric sequence *multiplies*, so its ratio is one term divided by the one before. Subtracting is how you find the common difference of an arithmetic sequence.',
    remediationConceptId: 'series',
  },
  {
    id: 'series-term-count-off-by-one',
    title: 'The number of terms miscounted',
    description: 'Uses the last index as the count when the sum does not start at 1.',
    studentMessage:
      'Count the terms rather than reading off the final index. From the 4th term to the 10th there are 7 terms, not 6 — both ends are included.',
    remediationConceptId: 'series',
  },
  {
    id: 'order-counted-when-it-does-not-matter',
    title: 'Arrangements counted where selections were asked for',
    description: 'Uses a permutation for a question whose outcomes are unordered, counting each group once per arrangement.',
    studentMessage:
      'Picking Ann then Ben gives the same committee as picking Ben then Ann. When order does not change the outcome, divide the arrangements out — that is what a combination does.',
    remediationConceptId: 'combinatorics-intro',
  },
  {
    id: 'order-ignored-when-it-matters',
    title: 'Selections counted where order matters',
    description: 'Uses a combination for a question about arrangement, position or ranking.',
    studentMessage:
      'Gold, silver and bronze are three different jobs, so the same three runners in another order is a different result. Counting the order in means a permutation, not a combination.',
    remediationConceptId: 'combinatorics-intro',
  },
  {
    id: 'counting-stages-added',
    title: 'Stages of a choice added instead of multiplied',
    description: 'Adds the number of options at each stage, rather than multiplying them.',
    studentMessage:
      'Every shirt can go with every pair of trousers, so the choices multiply. Adding would make 4 shirts and 3 trousers into 7 outfits, when they make 12.',
    remediationConceptId: 'combinatorics-intro',
  },
  {
    id: 'binomial-coefficients-omitted',
    title: 'The binomial coefficients left out',
    description: 'Expands (x + y)³ as x³ + x²y + xy² + y³, taking every coefficient to be 1.',
    studentMessage:
      'Each middle term can be built in more than one way, and the coefficient counts those ways. There are 3 ways to take one y from three brackets, so the term is 3x²y.',
    remediationConceptId: 'binomial-theorem',
  },
  {
    id: 'binomial-term-index-off-by-one',
    title: 'The term number read as the index',
    description: 'Takes the kth term of an expansion to be the one with C(n, k), which is the (k + 1)th.',
    studentMessage:
      'The expansion starts at C(n, 0), so the first term has index 0 and the kth term has index k − 1. Count from zero and the powers come out right.',
    remediationConceptId: 'binomial-theorem',
  },
  {
    id: 'power-distributed-over-sum',
    title: 'A power spread across a sum',
    description: 'Writes (x + y)ⁿ as xⁿ + yⁿ.',
    studentMessage:
      'A power does not distribute over a sum. (1 + 1)³ is 8 while 1³ + 1³ is 2, and everything between those two numbers is the middle terms you dropped.',
    remediationConceptId: 'binomial-theorem',
  },
  {
    id: 'expected-value-unweighted',
    title: 'Outcomes averaged without their probabilities',
    description: 'Adds the possible values and divides by how many there are, ignoring how likely each is.',
    studentMessage:
      'A rare outcome should pull the average less than a common one. Multiply each value by its probability and add — do not treat the outcomes as equally likely unless they are.',
    remediationConceptId: 'probability-distributions',
  },
  {
    id: 'distribution-does-not-total-one',
    title: 'A distribution whose probabilities do not total 1',
    description: 'Accepts or produces a set of probabilities summing to something other than 1.',
    studentMessage:
      'Something must happen, so the probabilities of all the outcomes have to add to exactly 1. A total above or below that means an outcome has been double-counted or missed.',
    remediationConceptId: 'probability-distributions',
  },
  {
    id: 'binomial-trials-and-successes-swapped',
    title: 'Trials and successes the wrong way round',
    description: 'Computes C(k, n) rather than C(n, k), choosing the trials from the successes.',
    studentMessage:
      'You are choosing *which* of the n trials succeed, so the trials are the pool: C(n, k), with n on the outside. C(k, n) asks which of the successes are trials, which is not a question.',
    remediationConceptId: 'probability-distributions',
  },
  {
    id: 'conic-radius-not-rooted',
    title: 'r² read as the radius',
    description: 'Reports 25 as the radius of x² + y² = 25, leaving the square root untaken.',
    studentMessage:
      'The equation gives the radius *squared*. x² + y² = 25 is the circle of radius 5, because 5² = 25 — the same square root the distance formula ends with.',
    remediationConceptId: 'conic-sections',
  },
  {
    id: 'conic-centre-sign-inverted',
    title: 'The centre read with the wrong sign',
    description: 'Reads (x − 3)² + (y + 2)² = 16 as centred at (−3, 2).',
    studentMessage:
      'The form is (x − h)², so the subtraction is already written in: (x − 3)² means h = 3, and (y + 2)² means k = −2. Ask which point makes both brackets zero.',
    remediationConceptId: 'conic-sections',
  },
  {
    id: 'ellipse-axes-swapped',
    title: 'The long axis of an ellipse taken as the short one',
    description: 'Assigns the major axis by which term is written first, rather than by which denominator is larger.',
    studentMessage:
      'The larger denominator marks the long direction, wherever it is written. Under x² it stretches the ellipse sideways; under y² it stretches it upwards.',
    remediationConceptId: 'conic-sections',
  },
  {
    id: 'vector-magnitude-as-sum',
    title: 'Components added to give the length',
    description: 'Reports the magnitude of (3, 4) as 7, adding the components instead of using Pythagoras.',
    studentMessage:
      'Three east then four north does not leave you seven from home — the two legs meet at a right angle, so the length is √(3² + 4²) = 5.',
    remediationConceptId: 'vectors',
  },
  {
    id: 'vector-direction-ignored',
    title: 'Vectors added as though they were sizes',
    description: 'Adds magnitudes directly, ignoring that the two vectors point in different directions.',
    studentMessage:
      'Direction is half of what a vector is. Add the across-parts together and the up-parts together; only vectors pointing the same way have lengths that simply add.',
    remediationConceptId: 'vectors',
  },
  {
    id: 'vector-components-swapped',
    title: 'The components swapped',
    description: 'Uses the vertical component where the horizontal is needed, or reverses the two in a direction calculation.',
    studentMessage:
      'The first component is across and the second is up, in that order — the same convention as a coordinate. Swapping them reflects the vector in the line y = x.',
    remediationConceptId: 'vectors',
  },
  {
    id: 'limit-taken-as-value',
    title: 'The limit confused with the value',
    description: 'Says a limit does not exist because the function is undefined at the point.',
    studentMessage:
      'A limit asks where the function is *heading*, not where it lands. The point x = a is left out of the question entirely, so a hole there does not stop the limit existing.',
    remediationConceptId: 'limits',
  },
  {
    id: 'indeterminate-form-called-undefined',
    title: '0/0 taken as the final answer',
    description: 'Substitutes, sees 0/0 and stops, rather than cancelling the common factor first.',
    studentMessage:
      '0/0 is not an answer — it is a signal that the top and the bottom share a factor. Cancel it and substitute again; that is the whole method.',
    remediationConceptId: 'limits',
  },

  /* ------------------------------------------- sum, difference, double angle */
  {
    id: 'addition-formula-sign-flipped',
    title: 'The wrong sign between the two products',
    description: 'Uses cos A cos B + sin A sin B for cos(A + B), copying the sign of the bracket where the cosine formula reverses it.',
    studentMessage:
      'The sine formula keeps the sign of the bracket; the cosine formula reverses it. Check at A = B = 90°: cos 180° is −1, and only cos A cos B − sin A sin B gives −1.',
    remediationConceptId: 'trig-addition-formulas',
  },
  {
    id: 'double-angle-taken-as-doubling',
    title: 'sin 2θ read as twice sin θ',
    description: 'Treats the 2 in sin 2θ as a factor outside the function rather than as a doubling of the angle.',
    studentMessage:
      'The 2 doubles the *angle*, not the answer. At θ = 90°, sin 2θ is sin 180° = 0 while 2 sin θ is 2, so the two cannot be the same rule.',
    remediationConceptId: 'trig-addition-formulas',
  },

  /* -------------------------------------------------------------- induction */
  {
    id: 'induction-base-case-omitted',
    title: 'The base case left out',
    description: 'Proves the step from k to k + 1 and stops, without checking that the claim holds anywhere to begin with.',
    studentMessage:
      'The step only carries a claim forward from somewhere. "If it holds at k then it holds at k + 1" is true of the claim n = n + 1 as well, and that one holds nowhere — so the first domino has to be pushed.',
    remediationConceptId: 'mathematical-induction',
  },
  {
    id: 'induction-conclusion-assumed',
    title: 'The claim assumed for every n',
    description: 'Assumes the statement for all n and then derives it, which assumes the very thing being proved.',
    studentMessage:
      'You may assume the claim for *one* value, k, and nothing more. Assuming it for every n is assuming the conclusion, and a proof may not do that.',
    remediationConceptId: 'mathematical-induction',
  },
  {
    id: 'pattern-checked-not-proved',
    title: 'A handful of cases taken as a proof',
    description: 'Verifies the claim for several values of n and concludes that it holds for all of them.',
    studentMessage:
      'Checking cases can only ever check finitely many. n² + n + 41 is prime for every n from 0 to 39 and composite at n = 40 — which is why the general step is the proof and the cases are not.',
    remediationConceptId: 'mathematical-induction',
  },
  {
    id: 'induction-step-not-substituted',
    title: 'The next case written without substituting',
    description: 'Writes P(k + 1) with k still in place, restating P(k) rather than the case being proved.',
    studentMessage:
      'P(k + 1) is the same claim with k + 1 written everywhere n was. Leaving k in place states P(k) again, and getting from P(k) to P(k) proves nothing.',
    remediationConceptId: 'mathematical-induction',
  },

  /* ------------------------------------------------------ polar coordinates */
  {
    id: 'polar-angle-from-arctan-alone',
    title: 'The angle taken straight from the inverse tangent',
    description: 'Computes arctan(y/x) without checking which quadrant the point is in.',
    studentMessage:
      'The inverse tangent cannot tell (−3, −3) from (3, 3) — both hand it the same ratio. Find the acute angle to the x-axis, then place it in the quadrant the point is actually in.',
    remediationConceptId: 'polar-coordinates',
  },
  {
    id: 'polar-coordinates-swapped',
    title: 'The radius and the angle the wrong way round',
    description: 'Writes (θ, r) where (r, θ) is meant, or reads the angle of a polar pair as a distance.',
    studentMessage:
      'A polar pair is written distance first, direction second: (r, θ). Reading (4, 30°) as 4° round from the axis at a distance of 30 describes a different point entirely.',
    remediationConceptId: 'polar-coordinates',
  },

  /* --------------------------------------------------------- rate of change */
  {
    id: 'difference-quotient-not-divided',
    title: 'The change reported instead of the rate',
    description: 'Gives f(b) − f(a) as the rate of change, without dividing by b − a.',
    studentMessage:
      'That is how much it changed, not how fast. A rate is a change *per unit* of the input, so divide by how far the input moved.',
    remediationConceptId: 'rates-of-change',
  },
  {
    id: 'rate-of-change-inverted',
    title: 'The rate turned upside down',
    description: 'Computes the change in x over the change in y, inverting the quotient.',
    studentMessage:
      'A rate of change reads "y per x", so the change in y goes on top. Upside down you have answered how far the input moves per unit of output, which is a different question.',
    remediationConceptId: 'rates-of-change',
  },
  {
    id: 'average-rate-taken-as-instantaneous',
    title: 'An average rate reported as the rate at a moment',
    description: 'Gives the slope of a secant over a whole interval as the rate of change at one point of it.',
    studentMessage:
      'An average over an interval is not the value at a point — a car averaging 60 km/h was not doing 60 at every moment. Shrink the interval towards nothing and take the limit of what you get.',
    remediationConceptId: 'rates-of-change',
  },
  /* --------------------------------------- Algebra II, the rest of the year */
  {
    id: 'rational-exponent-parts-swapped',
    title: 'Index and power exchanged in a rational exponent',
    description: 'Reads x^(m/n) as the m-th root of x to the power n — numerator and denominator the wrong way round.',
    studentMessage:
      'The denominator is the root and the numerator is the power. x^(2/3) is the *cube* root of x squared, because the 3 is underneath.',
    remediationConceptId: 'rational-exponents',
  },
  {
    id: 'rational-exponent-multiplied',
    title: 'Rational exponent multiplied instead of applied',
    description: 'Computes 8^(2/3) as 8 × 2/3, treating the exponent as a factor.',
    studentMessage:
      'An exponent is not a multiplier. 8^(2/3) says "take the cube root of 8, then square it" — root first, then power.',
    remediationConceptId: 'rational-exponents',
  },
  {
    id: 'rational-exponent-power-dropped',
    title: 'The root taken, the power forgotten',
    description: 'Evaluates x^(m/n) by taking the n-th root and stopping, so the numerator is never used.',
    studentMessage:
      'You took the root correctly — but the number on top of the exponent is still waiting. x^(2/3) means root first, and *then* square.',
    remediationConceptId: 'rational-exponents',
  },
  {
    id: 'composition-order-reversed',
    title: 'Composition done in the wrong order',
    description: 'Computes g(f(x)) when asked for f(g(x)) — the outer and inner functions exchanged.',
    studentMessage:
      'f(g(x)) means g goes first: it is the one wrapped in the brackets. Work outwards from the inside, the way you would with any nested expression.',
    remediationConceptId: 'function-operations',
  },
  {
    id: 'composition-as-product',
    title: 'Composition read as multiplication',
    description: 'Takes (f ∘ g)(x) or f(g(x)) as f(x) × g(x).',
    studentMessage:
      'That circle is not a times sign. f(g(x)) means "run g, then feed its output into f" — one machine after the other, not two answers multiplied.',
    remediationConceptId: 'function-operations',
  },
  {
    id: 'function-sum-one-part-only',
    title: 'Only one function evaluated in a sum',
    description: 'Evaluates (f + g)(a) by substituting into f alone, or into g alone.',
    studentMessage:
      '(f + g)(a) means f(a) + g(a). Put the same input into *both* functions, then add what comes out.',
    remediationConceptId: 'function-operations',
  },
  {
    id: 'piecewise-wrong-branch',
    title: 'The wrong rule chosen from a piecewise definition',
    description: 'Substitutes into a branch whose condition the input does not satisfy — usually the first one listed.',
    studentMessage:
      'Check the condition before you substitute. The rules are not alternatives to choose between; the input decides which one applies.',
    remediationConceptId: 'piecewise-functions',
  },
  {
    id: 'piecewise-endpoint-misassigned',
    title: 'The boundary given to the wrong piece',
    description: 'At the value where the definition splits, uses the branch whose inequality is strict rather than the one that includes it.',
    studentMessage:
      'Look at whether the sign is < or ≤. Exactly one branch is allowed to own that endpoint, and the little line under the sign is what says which.',
    remediationConceptId: 'piecewise-functions',
  },
  {
    id: 'piecewise-boundary-given-as-value',
    title: 'The condition answered instead of the output',
    description: 'Reports the number the branch condition names rather than the value of the function there.',
    studentMessage:
      'That number is the *input* the rule applies from — it is a condition, not an answer. Substitute it into the matching rule and see what comes out.',
    remediationConceptId: 'piecewise-functions',
  },
  {
    id: 'second-intersection-dropped',
    title: 'Only one intersection reported',
    description: 'Solves a line-and-curve system, finds one point and stops, though the quadratic has two roots.',
    studentMessage:
      'A line can cut a parabola twice, and this one does. Solving gave a quadratic — check whether it has a second root before you stop.',
    remediationConceptId: 'nonlinear-systems',
  },
  {
    id: 'curve-eliminated-as-if-linear',
    title: 'The squared term subtracted away',
    description: 'Eliminates by subtracting the two equations as though both were linear, discarding the x² term.',
    studentMessage:
      'Subtracting only cancels terms that match. There is no x² in the line to cancel the x² in the curve, so substitution — not elimination — is the method here.',
    remediationConceptId: 'nonlinear-systems',
  },
  {
    id: 'denominator-dropped-not-multiplied',
    title: 'Denominators deleted rather than cleared',
    description: 'Crosses out the denominators without multiplying every term of the equation by them.',
    studentMessage:
      'You can multiply *both whole sides* by the common denominator, which is not the same as rubbing the denominators out. Every term has to be multiplied, including the ones that never had a fraction.',
    remediationConceptId: 'rational-equations',
  },
  {
    id: 'excluded-value-accepted',
    title: 'A solution that makes a denominator zero',
    description: 'Keeps a root of the cleared equation that was never in the domain of the original.',
    studentMessage:
      'Substitute it back: that value makes a denominator zero, so the original equation never even had a value there. Multiplying up can invent answers, exactly as squaring can.',
    remediationConceptId: 'rational-equations',
  },
  {
    id: 'cross-multiplied-across-a-sum',
    title: 'Cross-multiplication used on a sum',
    description: 'Applies cross-multiplication where one side is a sum of two fractions rather than a single one.',
    studentMessage:
      'Cross-multiplying only works for one fraction equal to one fraction. Combine the sum into a single fraction first, or multiply through by the common denominator.',
    remediationConceptId: 'rational-equations',
  },
  {
    id: 'roots-given-for-solution-set',
    title: 'The boundary points offered as the solution',
    description: 'Solves the associated equation and reports its roots, where the question asks which values satisfy an inequality.',
    studentMessage:
      'Those are the places where the expression is *zero* — the edges of the answer, not the answer. The solution is the stretch between or beyond them.',
    remediationConceptId: 'polynomial-inequalities',
  },
  {
    id: 'interval-sign-assumed',
    title: 'The sign between the roots taken for granted',
    description: 'Assumes the expression is positive to the right and negative to the left, without testing an interval.',
    studentMessage:
      'Test one number from each interval. A cubic with three roots changes sign three times, and which end starts positive depends on the leading coefficient.',
    remediationConceptId: 'polynomial-inequalities',
  },
  {
    id: 'inequality-multiplied-by-unknown-sign',
    title: 'Multiplied by an expression that might be negative',
    description: 'Clears the denominator of a rational inequality by multiplying by a term whose sign is unknown.',
    studentMessage:
      'Multiplying an inequality by a negative flips it — and you do not yet know the sign of that bracket. Move everything to one side and read the sign of the whole expression instead.',
    remediationConceptId: 'polynomial-inequalities',
  },
  {
    id: 'exponent-divided-out',
    title: 'The exponent treated as a coefficient',
    description: 'Solves an exponential equation by dividing, as though 3^x meant 3 times x.',
    studentMessage:
      '3^x is not 3 × x, so dividing by 3 does not free the x. Taking the logarithm of both sides is what brings an exponent down.',
    remediationConceptId: 'exponential-equations',
  },
  {
    id: 'log-taken-on-one-side',
    title: 'The logarithm applied to one side only',
    description: 'Writes log of the left-hand side equal to the untouched right-hand side.',
    studentMessage:
      'Whatever you do to one side you must do to the other — that has been true since one-step equations, and a logarithm is no exception.',
    remediationConceptId: 'exponential-equations',
  },
  {
    id: 'change-of-base-inverted',
    title: 'The change-of-base fraction upside down',
    description: 'Computes log_b(x) as log(b) / log(x) rather than log(x) / log(b).',
    studentMessage:
      'The number you are taking the log *of* goes on top; the base goes underneath. Sanity-check it: log₂(8) must come out as 3, not as ⅓.',
    remediationConceptId: 'exponential-equations',
  },
  {
    id: 'log-domain-ignored',
    title: 'A solution that puts a negative inside a logarithm',
    description: 'Accepts a root of the rewritten equation that makes the argument of a logarithm zero or negative.',
    studentMessage:
      'Put it back into the original: you cannot take the log of a negative number, so that candidate is not a solution. Log equations need checking, like radical ones.',
    remediationConceptId: 'exponential-equations',
  },

  /* ------------------------------------------------------ radical functions */
  {
    id: 'radical-domain-taken-as-all-reals',
    title: 'The square-root curve given the whole line',
    description: 'Reports the domain of a square-root function as every real number, ignoring what the root refuses.',
    studentMessage:
      'A square root will not take a negative. Set whatever is under the root to be zero or more and solve that — the answer is where the curve is allowed to start.',
    remediationConceptId: 'radical-functions',
  },
  {
    id: 'cube-root-domain-restricted',
    title: 'The cube root given the square root’s domain',
    description: 'Restricts the domain of a cube-root function to non-negative inputs, as for a square root.',
    studentMessage:
      'Nothing squares to a negative, which is why √x stops at zero. But (−2)³ = −8, so ∛(−8) = −2 is perfectly ordinary — a cube root accepts every number there is.',
    remediationConceptId: 'radical-functions',
  },
  {
    id: 'radical-start-point-sign-flipped',
    title: 'The starting point read with the wrong sign',
    description: 'Takes the curve of y = √(x − h) + k as starting at x = −h, reading the sign in the bracket as the direction of the shift.',
    studentMessage:
      'The curve starts where the root is first allowed: solve what is under it equals zero. For √(x − 4) that is x = 4, to the *right*, even though the sign in the bracket is a minus.',
    remediationConceptId: 'radical-functions',
  },

  /* ----------------------------------------------------- exponential models */
  {
    id: 'compound-periods-not-scaled',
    title: 'The annual rate used for a shorter period',
    description: 'Applies the full annual rate at every compounding period instead of dividing it among them.',
    studentMessage:
      'Compounding monthly does not pay the year’s rate twelve times. Each period pays r ÷ n of it, and there are n × t of them — divide the rate and multiply the count.',
    remediationConceptId: 'exponential-models',
  },
  {
    id: 'compound-interest-taken-as-simple',
    title: 'Interest taken on the original amount every year',
    description: 'Computes P(1 + rt) rather than P(1 + r)^t, so the interest never earns interest.',
    studentMessage:
      'That is simple interest — the same amount every year, because it is always taken from the opening balance. Compound interest is taken from the *new* balance, so it grows every year.',
    remediationConceptId: 'exponential-models',
  },
  {
    id: 'half-life-divided-not-halved',
    title: 'The half-life divided into the amount',
    description: 'Divides the quantity by the number of half-lives instead of halving it that many times.',
    studentMessage:
      'Count the halvings first: elapsed time ÷ half-life. That count is an *exponent* — the amount is halved that many times, not divided by the count.',
    remediationConceptId: 'exponential-models',
  },
  {
    id: 'growth-rate-used-as-factor',
    title: 'The percentage used as the multiplier',
    description: 'Uses b = r rather than b = 1 + r, multiplying by the growth alone and losing the amount already there.',
    studentMessage:
      'Growing by 8% leaves you with 108% of what you had, so the multiplier is 1.08. Multiplying by 0.08 throws away the starting amount and keeps only the growth.',
    remediationConceptId: 'exponential-models',
  },

  /* ------------------------------------------ common and natural logarithms */
  {
    id: 'natural-log-read-as-base-ten',
    title: 'ln read as log',
    description: 'Evaluates a natural logarithm as though its base were 10, or the reverse.',
    studentMessage:
      'ln is the logarithm to base e, not to base 10. They ask different questions — ln 100 asks what power of 2.718… gives 100, and the answer is not 2.',
    remediationConceptId: 'natural-logarithm',
  },
  {
    id: 'natural-base-rounded-away',
    title: 'e rounded to a whole number',
    description: 'Treats e as 3 (or as 2) rather than as 2.71828…, and reports a whole-number answer.',
    studentMessage:
      'e is 2.71828…, an irrational number like π. Rounding it before computing changes the answer, and on an exponent it changes it by a lot.',
    remediationConceptId: 'natural-logarithm',
  },
  {
    id: 'continuous-rate-not-multiplied-by-time',
    title: 'The exponent left as the rate alone',
    description: 'Computes Pe^r rather than Pe^(rt), dropping the time from the exponent of a continuous model.',
    studentMessage:
      'In A = Pe^(rt) the rate and the time are multiplied *inside* the exponent. Leaving the t out charges one year’s growth however long the money is invested.',
    remediationConceptId: 'natural-logarithm',
  },

  /* -------------------------------------------------------- tangent function */
  {
    id: 'tangent-as-product',
    title: 'Tangent taken as sine times cosine',
    description: 'Multiplies the two coordinates instead of dividing them.',
    studentMessage:
      'tan θ is sin θ ÷ cos θ — the y-coordinate over the x-coordinate. Multiplying them gives a number that never leaves [−½, ½], and tangent takes every value there is.',
    remediationConceptId: 'tangent-function',
  },
  {
    id: 'tangent-period-taken-as-two-pi',
    title: 'The tangent given sine’s period',
    description: 'States the period of tangent as 2π, by analogy with sine and cosine.',
    studentMessage:
      'Tangent repeats twice as often as sine. Halfway round the circle both coordinates change sign, and a quotient of two negatives equals the quotient of the two positives — so its period is π.',
    remediationConceptId: 'tangent-function',
  },
  {
    id: 'tangent-asymptote-from-numerator',
    title: 'The gap placed where sine is zero',
    description: 'Puts the asymptotes of tangent where the numerator vanishes rather than the denominator.',
    studentMessage:
      'A quotient blows up where the *bottom* is zero. sin θ = 0 makes tan θ = 0 — a crossing, not a gap. The gaps sit where cos θ = 0.',
    remediationConceptId: 'tangent-function',
  },

  /* ------------------------------------------------------ recursive sequences */
  {
    id: 'recursive-rule-used-as-explicit',
    title: 'The term number substituted into a recursive rule',
    description: 'Puts n into a rule of the form aₙ = aₙ₋₁ + d, as if it were a closed formula.',
    studentMessage:
      'A recursive rule takes the *previous term* as its input, not the term number. To reach a₅ you must have a₄ first — that is exactly what writing the rule this way costs.',
    remediationConceptId: 'recursive-sequences',
  },
  {
    id: 'recursive-seed-ignored',
    title: 'The first term never used',
    description: 'Applies the recursive step without starting from the given first term.',
    studentMessage:
      'A recursive definition is two statements, and the first term is one of them. Without it the same rule describes infinitely many different sequences.',
    remediationConceptId: 'recursive-sequences',
  },
  {
    id: 'recursion-applied-once',
    title: 'One step taken where several were needed',
    description: 'Applies the recursive rule a single time however far the required term is from the one given.',
    studentMessage:
      'Count the steps. Going from a₂ to a₆ is four applications of the rule, not one — write the terms out in a row and you cannot lose count.',
    remediationConceptId: 'recursive-sequences',
  },

  /* ----------------------------------------------------- normal distributions */
  {
    id: 'z-score-not-divided-by-sd',
    title: 'The distance from the mean given as the z-score',
    description: 'Computes x − μ and stops, without dividing by the standard deviation.',
    studentMessage:
      'A z-score counts standard deviations, not units. Subtract the mean to get the distance, then divide by σ to say how many σ that distance is.',
    remediationConceptId: 'normal-distribution',
  },
  {
    id: 'empirical-rule-tail-doubled',
    title: 'Both tails given when one was asked for',
    description: 'Reports the whole area outside an interval when the question asked for one side of it.',
    studentMessage:
      'The curve is symmetric, so whatever is left over splits equally between the two ends. Asked about one tail, halve it.',
    remediationConceptId: 'normal-distribution',
  },
  {
    id: 'normal-area-complemented',
    title: 'The area on the wrong side of the value',
    description: 'Gives the proportion above a value when asked for the proportion below it, or the reverse.',
    studentMessage:
      'Sketch the curve and shade the side the question asks about. The two sides add to 100%, so the other one is a perfectly good number answering a different question.',
    remediationConceptId: 'normal-distribution',
  },
  {
    id: 'normal-assumed-for-any-data',
    title: 'The bell curve assumed',
    description: 'Applies the 68–95–99.7 rule to a distribution that is visibly skewed or bounded.',
    studentMessage:
      'Those percentages are properties of a *normal* distribution. Incomes, waiting times and marks bunched against a ceiling are not bell-shaped, and the rule says nothing about them.',
    remediationConceptId: 'normal-distribution',
  },

  /* ------------------------------------------------ regression and correlation */
  {
    id: 'correlation-read-from-slope',
    title: 'Strength judged by steepness',
    description: 'Takes a steeper line of best fit to mean a stronger correlation.',
    studentMessage:
      'Slope and strength are different questions. Slope says how much y changes per unit of x; r says how tightly the points hug the line. A gentle line through points in a perfect row still has r = 1.',
    remediationConceptId: 'regression',
  },
  {
    id: 'correlation-sign-dropped',
    title: 'The sign of r discarded',
    description: 'Reads a strong negative correlation as a weak one, ranking coefficients by value rather than by size.',
    studentMessage:
      'The sign of r is the direction and its size is the strength. r = −0.9 is a strong relationship — exactly as strong as +0.9 — with y falling as x rises.',
    remediationConceptId: 'regression',
  },
  {
    id: 'extrapolated-beyond-the-data',
    title: 'The line trusted outside the data',
    description: 'Uses a line of best fit far beyond the range of x that was actually observed.',
    studentMessage:
      'The line was fitted to the range you have. Outside it nothing was measured, and a line that fits ages 5 to 15 will cheerfully predict a nine-foot adult.',
    remediationConceptId: 'regression',
  },
  {
    id: 'intercept-read-as-slope',
    title: 'The intercept described as the rate',
    description: 'Interprets a of ŷ = a + bx as the change per unit of x, or b as the value at x = 0.',
    studentMessage:
      'The intercept is the prediction *at* x = 0; the slope is how much the prediction changes *per unit* of x. Swapping them reports a starting value as a rate.',
    remediationConceptId: 'regression',
  },
  {
    id: 'regression-residual-sign-inverted',
    title: 'The residual taken the wrong way round',
    description: 'Computes predicted minus actual rather than actual minus predicted, so an underestimate looks like an overestimate.',
    studentMessage:
      'A residual is actual − predicted. A point above the line has a positive residual, which is the line having guessed too low.',
    remediationConceptId: 'regression',
  },

  /* ------------------------------------------------------------- phase shift */
  {
    id: 'phase-shift-not-divided',
    title: 'The constant inside read as the shift',
    description: 'Reads y = sin(bx − c) as shifted by c, without dividing by the coefficient of x.',
    studentMessage:
      'Factor the coefficient out first: sin(2x − π) is sin(2(x − π/2)), so the shift is π/2, not π. Whatever is done to x inside is done to the shift as well.',
    remediationConceptId: 'trig-graphs',
  },
  /* ------------------------------ Grade 12: the three core topics finished */
  {
    id: 'tangent-addition-distributed',
    title: 'tan(A + B) split into tan A + tan B',
    description: 'Distributes the tangent across a sum, dropping the denominator of the addition formula entirely.',
    studentMessage:
      'Tangent does not distribute, any more than sine did. tan(A + B) = (tan A + tan B) / (1 − tan A tan B) — and that denominator is not optional; it is cos A cos B divided by itself.',
    remediationConceptId: 'half-angle-formulas',
  },
  {
    id: 'half-angle-sign-from-wrong-quadrant',
    title: 'The half-angle sign read from the wrong angle',
    description: 'Chooses the sign of sin(A/2) or cos(A/2) from the quadrant of A rather than the quadrant of A/2.',
    studentMessage:
      'The formula gives the square, so the sign comes from where A/2 is — not from where A is. Halve the bounds on A first: an angle between 270° and 360° has its half between 135° and 180°, which is a different quadrant with different signs.',
    remediationConceptId: 'half-angle-formulas',
  },
  {
    id: 'half-angle-not-halved',
    title: 'The double-angle formula used for a half angle',
    description: 'Applies sin 2θ = 2 sin θ cos θ, or reads cos A off directly, where the half-angle formula is needed.',
    studentMessage:
      'That formula doubles an angle, and this question halves one. Use sin²(A/2) = (1 − cos A)/2 — the same identity solved for the other letter.',
    remediationConceptId: 'half-angle-formulas',
  },
  {
    id: 'dot-product-left-as-vector',
    title: 'Components multiplied but never added',
    description: 'Reports a pair of numbers as the dot product, multiplying component by component without summing.',
    studentMessage:
      'A dot product is a single number. Multiply the matching components *and then add them* — the adding is what turns two directions into one measurement, and without it the answer says nothing about the angle.',
    remediationConceptId: 'dot-product',
  },
  {
    id: 'dot-product-magnitudes-omitted',
    title: 'The angle taken from the dot product alone',
    description: 'Uses cos θ = a · b without dividing by the two magnitudes.',
    studentMessage:
      'a · b = |a||b| cos θ, so the dot product has to be divided by both lengths before the cosine appears. Skipping that step routinely gives a "cosine" bigger than 1.',
    remediationConceptId: 'dot-product',
  },
  {
    id: 'perpendicular-tested-by-equality',
    title: 'Perpendicular vectors looked for by matching components',
    description: 'Tests two vectors for a right angle by comparing components rather than by the dot product.',
    studentMessage:
      'Two vectors are perpendicular exactly when their dot product is zero. That is the whole test, it has no special cases, and it works for the vertical direction that broke the negative-reciprocal slope rule.',
    remediationConceptId: 'dot-product',
  },
  {
    id: 'complex-division-componentwise',
    title: 'Complex numbers divided part by part',
    description: 'Divides real by real and imaginary by imaginary, as though the two parts were independent.',
    studentMessage:
      'Division does not go through the parts separately — the i in the denominator mixes them. Multiply the top and the bottom by the conjugate of the denominator, which makes the bottom real, and then divide.',
    remediationConceptId: 'complex-polar',
  },
  {
    id: 'complex-argument-quadrant-ignored',
    title: 'The argument taken straight from the arctangent',
    description: 'Reports arctan(b/a) as the argument without adjusting for the quadrant of the point.',
    studentMessage:
      'arctan cannot tell 1 + i from −1 − i; the ratio is 1 for both. Plot the point, name its quadrant, and adjust — the same care polar coordinates already needed.',
    remediationConceptId: 'complex-polar',
  },
  {
    id: 'de-moivre-modulus-not-raised',
    title: 'De Moivre applied to the angle only',
    description: 'Multiplies the argument by n but leaves the modulus unraised, or raises the modulus and leaves the angle alone.',
    studentMessage:
      'Both halves change, and differently: the modulus is raised to the power and the argument is multiplied by it. zⁿ has modulus rⁿ and argument nθ.',
    remediationConceptId: 'complex-polar',
  },

  /* --------------------------------------- Grade 12: the calculus pathway */
  {
    id: 'one-sided-limits-not-compared',
    title: 'A two-sided limit claimed from one side',
    description: 'Reports a limit at a jump, taking the value from whichever branch was read first.',
    studentMessage:
      'The two-sided limit exists only when both one-sided limits agree. Here they do not, so there is no limit at that point — even though each side on its own is perfectly definite.',
    remediationConceptId: 'continuity',
  },
  {
    id: 'defined-taken-as-continuous',
    title: 'Having a value mistaken for continuity',
    description: 'Concludes a function is continuous at a point because f(a) exists.',
    studentMessage:
      'Continuity needs three things: f(a) exists, the limit exists, and the two are equal. A function can be defined at a jump and still be discontinuous there — being defined is only the first of the three.',
    remediationConceptId: 'continuity',
  },
  {
    id: 'constant-derivative-kept',
    title: 'The constant term carried into the derivative',
    description: 'Differentiates a polynomial correctly but leaves the constant term unchanged.',
    studentMessage:
      'The derivative of a constant is 0. y = 7 is a horizontal line and its slope is zero everywhere — so the constant term contributes nothing at all to f′.',
    remediationConceptId: 'derivatives',
  },
  {
    id: 'power-rule-exponent-not-reduced',
    title: 'The power brought down but not reduced',
    description: 'Multiplies by the exponent and leaves the exponent as it was.',
    studentMessage:
      'The power rule does two things: bring the power down as a multiplier *and* reduce it by one. x³ differentiates to 3x², not 3x³.',
    remediationConceptId: 'derivatives',
  },
  {
    id: 'power-rule-coefficient-omitted',
    title: 'The exponent reduced but not brought down',
    description: 'Lowers the power by one without multiplying by the original exponent.',
    studentMessage:
      'Half the rule has been applied. x³ differentiates to 3x², and the 3 comes from the exponent you started with — it is the part of the rule that carries the size of the change.',
    remediationConceptId: 'derivatives',
  },
  {
    id: 'product-rule-as-product-of-derivatives',
    title: 'A product differentiated factor by factor',
    description: 'Applies (uv)′ = u′v′, treating a product the way a sum is treated.',
    studentMessage:
      'Differentiation goes through a sum term by term and through a product it does not. Test it on x · x: the product is x², whose derivative is 2x, and u′v′ would give 1. The rule is u′v + uv′.',
    remediationConceptId: 'derivative-rules',
  },
  {
    id: 'chain-rule-inner-derivative-dropped',
    title: 'The inside derivative left out',
    description: 'Differentiates the outer function correctly and omits the factor for the inner one.',
    studentMessage:
      'The chain rule has two factors and this answer has one. After differentiating the outside, multiply by the derivative of the bracket — for (3x + 1)¹⁰ that factor is 3, and without it the answer is wrong by exactly that much.',
    remediationConceptId: 'derivative-rules',
  },
  {
    id: 'quotient-rule-terms-reversed',
    title: 'The quotient rule numerator written backwards',
    description: 'Writes uv′ − u′v, reversing the subtraction and so negating the answer.',
    studentMessage:
      'The top is differentiated first: (u′v − uv′)/v². Subtraction is not commutative, so swapping those two terms flips the sign of the whole derivative — and a flipped slope claims the curve rises where it falls.',
    remediationConceptId: 'derivative-rules',
  },
  {
    id: 'implicit-dy-dx-omitted',
    title: 'y differentiated as though it were a constant',
    description: 'Differentiates y² to 2y, leaving off the dy/dx the chain rule requires.',
    studentMessage:
      'y is a function of x, so differentiating y² needs the chain rule: 2y · dy/dx. Every y you differentiate leaves a dy/dx behind — count them before rearranging.',
    remediationConceptId: 'implicit-differentiation',
  },
  {
    id: 'related-rates-substituted-early',
    title: 'The instant substituted before differentiating',
    description: 'Puts the moment’s values into the relationship first, so the varying quantities become constants.',
    studentMessage:
      'Differentiate first, substitute second. A quantity replaced by a number has derivative zero, so substituting early deletes the very rate the question gave you.',
    remediationConceptId: 'implicit-differentiation',
  },
  {
    id: 'stationary-point-assumed-extremum',
    title: 'A flat point taken to be a turning point',
    description: 'Reports every solution of f′(x) = 0 as a maximum or minimum without testing it.',
    studentMessage:
      'f′(c) = 0 makes c a candidate, not an answer. y = x³ is flat at the origin and does not turn there — check the sign of f′ either side, or the sign of f″, before classifying.',
    remediationConceptId: 'derivative-applications',
  },
  {
    id: 'extremum-reported-as-x-value',
    title: 'The location given where the value was asked for',
    description: 'Answers "what is the maximum" with the x that produces it rather than with f(x).',
    studentMessage:
      'A maximum is a height. You have found *where* it happens; substitute that x back into f to get the value the question asked for.',
    remediationConceptId: 'derivative-applications',
  },
  {
    id: 'concavity-confused-with-direction',
    title: 'Concave up read as increasing',
    description: 'Treats the sign of f″ as though it reported whether the function rises or falls.',
    studentMessage:
      'f′ says which way the curve goes; f″ says how it bends. A curve can fall while concave up — slowing its fall — so the two questions have to be answered separately.',
    remediationConceptId: 'derivative-applications',
  },
  {
    id: 'optimisation-constraint-differentiated',
    title: 'The constraint differentiated instead of the objective',
    description: 'Differentiates the fixed-resource equation rather than the quantity being maximised.',
    studentMessage:
      'Two equations do two different jobs. The one you differentiate is the quantity you want to make large or small; the constraint is the one you substitute into it first.',
    remediationConceptId: 'optimization',
  },
  {
    id: 'optimisation-answered-with-variable',
    title: 'The working stopped at x',
    description: 'Reports the critical value rather than the maximum area, cost or volume the question asked for.',
    studentMessage:
      'x is where the optimum happens, not what it is. Substitute it back and answer the question in its own units.',
    remediationConceptId: 'optimization',
  },
  {
    id: 'speed-given-as-signed-velocity',
    title: 'A negative reported as a speed',
    description: 'Gives the signed velocity when the size of it was asked for, or drops the sign when direction was.',
    studentMessage:
      'Speed is the size of the velocity and is never negative; velocity carries the direction. Check which of the two the question named.',
    remediationConceptId: 'motion-derivatives',
  },
  {
    id: 'acceleration-assumed-zero-at-rest',
    title: 'Zero velocity taken to mean zero acceleration',
    description: 'Concludes that a momentarily stationary object has no acceleration.',
    studentMessage:
      'At the top of a throw the velocity is zero and gravity is still pulling. "At rest" is a statement about v alone — a is found by differentiating again, and is usually not zero there.',
    remediationConceptId: 'motion-derivatives',
  },
  {
    id: 'antiderivative-constant-omitted',
    title: 'The + C left off',
    description: 'Gives a single antiderivative where the family was asked for.',
    studentMessage:
      'Differentiation destroys the constant, so undoing it cannot recover one. Every indefinite integral ends in + C — without it the answer claims to be the only antiderivative, and there are infinitely many.',
    remediationConceptId: 'antiderivatives',
  },
  {
    id: 'antiderivative-differentiated-instead',
    title: 'Differentiated where an antiderivative was wanted',
    description: 'Applies the power rule forwards — lowering the exponent — when the question asked to reverse it.',
    studentMessage:
      'This question runs the rule backwards: raise the power by one and divide by the new power. Lowering it is differentiation, which is the operation being undone.',
    remediationConceptId: 'antiderivatives',
  },
  {
    id: 'riemann-width-not-applied',
    title: 'Rectangle heights added without their width',
    description: 'Sums f at each endpoint and stops, never multiplying by Δx.',
    studentMessage:
      'Those are heights, and an area needs height × width. Multiply the total by Δx once at the end — every strip has the same width, so it factors out of the sum.',
    remediationConceptId: 'integrals',
  },
  {
    id: 'riemann-endpoint-side-swapped',
    title: 'Right endpoints used where left were asked for',
    description: 'Takes the heights from the wrong side of each strip, turning an underestimate into an overestimate.',
    studentMessage:
      'Left endpoints start at a and stop one strip short of b; right endpoints start one strip in and finish at b. On a rising curve the first underestimates and the second overestimates, so the side is the whole answer.',
    remediationConceptId: 'integrals',
  },
  {
    id: 'ftc-limits-subtracted-backwards',
    title: 'F(a) − F(b) instead of F(b) − F(a)',
    description: 'Evaluates the antiderivative at the two limits and subtracts them in the wrong order.',
    studentMessage:
      'Top limit minus bottom limit. Reversing them negates the answer, and unlike most sign errors this one produces a plausible number with nothing to flag it.',
    remediationConceptId: 'fundamental-theorem',
  },
  {
    id: 'definite-integral-given-constant',
    title: '+ C attached to a definite integral',
    description: 'Carries the constant of integration into an evaluation between two limits.',
    studentMessage:
      'A definite integral is a number. Whichever antiderivative you choose, the constant appears in both F(b) and F(a) and cancels in the subtraction — so there is nothing for a + C to do.',
    remediationConceptId: 'fundamental-theorem',
  },

  /* ----------------------------------------------------- probability, rebuilt */
  /*
   * The Grade 13 band, where a wrong answer is most often a *rule applied
   * outside its conditions*. Favourable ÷ total on outcomes that are not
   * equally likely; P(A) + P(B) on events that overlap; P(A)P(B) on draws
   * without replacement; np on a variable that has no fixed n. None of these is
   * a slip. Each is a correct procedure, carried out correctly, on a situation
   * that fails its precondition — which is why each is named here rather than
   * marked wrong.
   */
  {
    id: 'outcomes-summarised-not-equally-likely',
    title: 'A summary treated as a sample space',
    description:
      'Counts categories — totals, counts, colours — as though they were equally likely outcomes, when the outcomes underneath them are not equally stocked.',
    studentMessage:
      'Favourable ÷ total needs the listed outcomes to be equally likely. There are eleven possible totals on two dice and they are nothing like equally likely — six ways to make 7, one to make 12. List the outcomes underneath the summary and count those.',
    remediationConceptId: 'sample-spaces',
  },
  {
    id: 'union-overlap-double-counted',
    title: 'P(A) + P(B) with the overlap counted twice',
    description: 'Adds two probabilities for an "or" question without subtracting the intersection.',
    studentMessage:
      'Anything in both events has now been counted twice — once in each. Subtract P(A and B) once to bring it back to a single count: P(A or B) = P(A) + P(B) − P(A and B).',
    remediationConceptId: 'probability-events',
  },
  {
    id: 'complement-not-subtracted',
    title: 'The event given where its complement was asked for',
    description: 'Answers with P(A) when the question asked for the probability that A does *not* happen, or the reverse.',
    studentMessage:
      'Read which way round the question asked. "Not A" is everything left over: P(not A) = 1 − P(A). Your number is the other side of that subtraction.',
    remediationConceptId: 'probability-events',
  },
  {
    id: 'exclusive-taken-as-independent',
    title: 'Mutually exclusive treated as independent',
    description: 'Multiplies the probabilities of two events that cannot both happen, or calls such a pair independent.',
    studentMessage:
      'Those are near-opposite ideas. If the two cannot both happen, then knowing one occurred tells you the other definitely did not — which is as far from "tells you nothing" as it is possible to get. P(A and B) is 0 here, not P(A)P(B).',
    remediationConceptId: 'independence',
  },
  {
    id: 'conditional-denominator-is-total',
    title: 'The grand total used as the denominator',
    description: 'Divides by the whole sample size for a conditional probability, instead of by the size of the conditioning event.',
    studentMessage:
      'Being told B happened throws away every outcome where it did not. What survives is B, so B is what goes underneath — on a table that is a row or column total, never the grand total.',
    remediationConceptId: 'conditional-probability',
  },
  {
    id: 'conditional-direction-reversed',
    title: 'P(B | A) computed where P(A | B) was asked',
    description: 'Conditions on the wrong one of the two events, dividing by the wrong marginal total.',
    studentMessage:
      'The bar is not symmetric. Whatever stands after it is the thing you have been told, and that is what goes underneath. Write the bar down and decide which side each event belongs on before dividing.',
    remediationConceptId: 'conditional-probability',
  },
  {
    id: 'independence-assumed-without-replacement',
    title: 'Unchanged probabilities used after a draw',
    description: 'Multiplies identical probabilities across successive draws when the item was not replaced.',
    studentMessage:
      'The first draw changed what is left in the bag. The second probability has one fewer item underneath — and one fewer of that colour on top, if that is what came out first.',
    remediationConceptId: 'independence',
  },
  {
    id: 'bayes-denominator-single-path',
    title: 'One path used as the denominator',
    description: 'Divides by a single branch probability rather than by the total probability of the observed outcome.',
    studentMessage:
      'Count every way the thing you observed could have come about. The denominator is the total of all those paths, not just the one you care about — otherwise the answer can exceed 1.',
    remediationConceptId: 'bayes-theorem',
  },
  {
    id: 'base-rate-ignored',
    title: 'The likelihood quoted as the answer',
    description: 'Reports P(evidence | cause) where P(cause | evidence) was asked, ignoring how rare the cause is.',
    studentMessage:
      'That is the test’s accuracy, not your answer. A rare condition means the false positives are drawn from an enormous group and the true positives from a tiny one, so the two numbers can differ enormously. Reverse the conditional properly.',
    remediationConceptId: 'bayes-theorem',
  },

  /* ------------------------------------------------------- random variables */
  {
    id: 'cumulative-boundary-excluded',
    title: 'The boundary value left out of a cumulative sum',
    description: 'Computes P(X < k) where P(X ≤ k) was asked, or the matching confusion between "at least" and "more than".',
    studentMessage:
      '"At most 3" includes 3; "fewer than 3" does not. On a whole-number variable those are genuinely different sums — circle the boundary value and decide which side owns it before you add anything.',
    remediationConceptId: 'discrete-random-variables',
  },
  {
    id: 'expectation-shift-scaled',
    title: 'The added constant scaled as well',
    description: 'Applies the multiplier to the constant term when computing E(aX + b).',
    studentMessage:
      'Only X is multiplied. E(aX + b) = aE(X) + b — the b was added to every outcome equally, so it moves the average by exactly b and the a never touches it.',
    remediationConceptId: 'expected-value',
  },
  {
    id: 'expectation-of-square-confused',
    title: 'E(X²) and [E(X)]² interchanged',
    description: 'Squares the mean where the mean of the squares was needed, or the reverse.',
    studentMessage:
      'Square-then-average and average-then-square are different numbers, and the gap between them is exactly the variance. E(X²) weights each *squared* value by its probability; μ² squares the single number you already have.',
    remediationConceptId: 'random-variable-variance',
  },
  {
    id: 'variance-mean-square-not-subtracted',
    title: 'E(X²) reported as the variance',
    description: 'Stops after the mean of the squares, without subtracting the square of the mean.',
    studentMessage:
      'That is E(X²), the first half of the calculation. Variance is E(X²) − μ²: the spread is what is left once the position of the centre has been taken out.',
    remediationConceptId: 'random-variable-variance',
  },
  {
    id: 'variance-shift-added',
    title: 'The constant added to the variance',
    description: 'Adds b to the variance when transforming aX + b.',
    studentMessage:
      'Adding the same amount to every value slides the whole distribution and changes no distance inside it. Var(aX + b) = a²Var(X) — the b vanishes entirely.',
    remediationConceptId: 'random-variable-variance',
  },
  {
    id: 'variance-scale-not-squared',
    title: 'The scale factor not squared',
    description: 'Multiplies a variance by a rather than by a² under the transformation aX + b.',
    studentMessage:
      'Variance is in squared units, so a scale factor arrives squared too. Doubling every value doubles every distance from the mean, and a doubled distance has four times the square.',
    remediationConceptId: 'random-variable-variance',
  },
  {
    id: 'standard-deviations-added',
    title: 'Standard deviations added directly',
    description: 'Combines two independent variables by adding their standard deviations instead of their variances.',
    studentMessage:
      'Variances add; standard deviations do not. Square both, add, then take the square root — √(3² + 4²) = 5, not 7. Independent wobbles partly cancel, and the Pythagorean form is what records that.',
    remediationConceptId: 'random-variable-sd',
  },
  {
    id: 'variance-of-difference-subtracted',
    title: 'Variances subtracted for a difference',
    description: 'Computes Var(X − Y) as Var(X) − Var(Y).',
    studentMessage:
      'A difference is noisier than either quantity, because both of them were uncertain. X − Y is X + (−Y), and negating a variable leaves its variance alone — so the variances still add.',
    remediationConceptId: 'random-variable-sd',
  },
  {
    id: 'repeated-observation-taken-as-scaling',
    title: '2X confused with X₁ + X₂',
    description: 'Treats the sum of two independent observations as one observation doubled, or the reverse.',
    studentMessage:
      'Doubling one measurement doubles its error along with it: Var(2X) = 4σ². Two separate measurements let their errors partly cancel: Var(X₁ + X₂) = 2σ². Same mean, different spread.',
    remediationConceptId: 'random-variable-sd',
  },
  {
    id: 'density-height-taken-as-probability',
    title: 'The height of a density read as a probability',
    description: 'Quotes f(x) as P(X = x), or treats a density value above 1 as impossible.',
    studentMessage:
      'Only areas are probabilities. A density may exceed 1 quite legally — a variable squeezed into half a unit has to be tall — and a single point has zero width, so P(X = x) is 0.',
    remediationConceptId: 'continuous-random-variables',
  },

  /* ---------------------------------------------------------- distributions */
  {
    id: 'binomial-failure-exponent-wrong',
    title: 'The failure exponent taken as n',
    description: 'Writes (1 − p) to the power n instead of n − k in a binomial probability.',
    studentMessage:
      'Only the trials that did not succeed can fail. k of the n went one way, so exactly n − k are left to go the other — the two exponents have to total n.',
    remediationConceptId: 'binomial-distribution',
  },
  {
    id: 'binomial-conditions-not-checked',
    title: 'Binomial applied where its conditions fail',
    description: 'Uses the binomial formula on trials that are dependent, or where the number of trials is not fixed.',
    studentMessage:
      'Check all four before reaching for the formula: fixed n, two outcomes, constant p, independent trials. Drawing without replacement breaks the last two, and "how many tries until…" has no fixed n at all.',
    remediationConceptId: 'binomial-distribution',
  },
  {
    id: 'geometric-coefficient-included',
    title: 'A binomial coefficient in a geometric probability',
    description: 'Multiplies a geometric probability by a combination, as though the successes could be arranged.',
    studentMessage:
      'There is exactly one way for the first success to fall on the kth try: fail, fail, …, succeed. Nothing can be rearranged, so there is no coefficient to count arrangements with.',
    remediationConceptId: 'geometric-distribution',
  },
  {
    id: 'geometric-exponent-off-by-one',
    title: 'The wrong number of failures before the success',
    description: 'Uses k failures rather than k − 1 for the first success on trial k.',
    studentMessage:
      'If the first success is on trial k, then k − 1 trials failed before it — not k. The kth trial is the success itself, and it cannot also be a failure.',
    remediationConceptId: 'geometric-distribution',
  },
  {
    id: 'geometric-mean-inverted',
    title: 'p given where the expected wait was asked',
    description: 'Reports the success probability, or 1 − p, instead of the mean number of trials 1/p.',
    studentMessage:
      'The expected wait is 1/p: a 1-in-6 event takes about 6 tries. A probability and a number of trials are not even the same kind of quantity — one has no units and the other is counted in trials.',
    remediationConceptId: 'geometric-distribution',
  },
  {
    id: 'poisson-lambda-not-scaled',
    title: 'The rate left unscaled',
    description: 'Uses the quoted rate without rescaling it to the interval the question asks about.',
    studentMessage:
      'λ belongs to an interval. Six calls an hour means λ = 3 for half an hour and λ = 18 for three hours — scale the rate first, then substitute. Scaling the answer afterwards computes nothing at all.',
    remediationConceptId: 'poisson-distribution',
  },
  {
    id: 'poisson-factorial-omitted',
    title: 'The k! left out of a Poisson probability',
    description: 'Computes e to the minus lambda times lambda to the k, without dividing by k factorial.',
    studentMessage:
      'The k! is what keeps the whole distribution totalling 1. Without it the terms grow with k instead of eventually falling away, and a single one can exceed 1 outright.',
    remediationConceptId: 'poisson-distribution',
  },
  {
    id: 'continuity-correction-omitted',
    title: 'A whole-number count handed straight to a continuous curve',
    description: 'Approximates a binomial by a normal without moving the boundary half a unit.',
    studentMessage:
      'The bar for X = 7 stretches from 6.5 to 7.5. Standardising 7 itself cuts that bar in half and loses the part the question wanted — shift the boundary half a unit, in whichever direction includes the value you meant.',
    remediationConceptId: 'normal-random-variable',
  },
  {
    id: 'inverse-normal-tail-swapped',
    title: 'The wrong tail used in an inverse normal',
    description: 'Looks the stated proportion up directly when the table is indexed by the area to the left, or the reverse.',
    studentMessage:
      'The table is indexed by the area to the LEFT. "The top 15%" is an area of 0.85 to the left, so the z you want is the one for 0.85 — sketch and shade before looking anything up.',
    remediationConceptId: 'normal-random-variable',
  },

  /* -------------------------------------------------------------- inference */
  {
    id: 'standard-error-not-used',
    title: 'σ used where the standard error belonged',
    description: 'Standardises a sample mean with the population standard deviation instead of σ ÷ √n.',
    studentMessage:
      'A mean of n observations varies far less than one observation does. Divide σ by √n before standardising — that shrinkage is the entire reason a larger sample is worth collecting.',
    remediationConceptId: 'sampling-distributions',
  },
  {
    id: 'standard-error-divided-by-n',
    title: 'σ divided by n rather than by √n',
    description: 'Uses σ ÷ n as the standard error of a sample mean.',
    studentMessage:
      'The square root is not decoration. Variances divide by n, and the standard error is the square root of that — so σ/√n. Dividing by n itself claims a precision four times better than the data supports at n = 16.',
    remediationConceptId: 'sampling-distributions',
  },
  {
    id: 'clt-applied-to-population',
    title: 'The central limit theorem applied to the population',
    description: 'Claims that a large sample makes the population itself normal.',
    studentMessage:
      'The theorem is about the distribution of *sample means*, not about the population. Incomes stay skewed however many people you survey; it is the averages of samples that straighten out.',
    remediationConceptId: 'sampling-distributions',
  },
  {
    id: 'critical-value-omitted',
    title: 'An interval built from one standard error',
    description: 'Reports the estimate plus or minus a single standard error, leaving out the critical value for the level.',
    studentMessage:
      'One standard error is only about 68% confidence. Multiply it by z* — 1.645, 1.96 or 2.576 — to reach the level the question actually asked for.',
    remediationConceptId: 'confidence-intervals',
  },
  {
    id: 'critical-value-level-mismatched',
    title: 'The critical value for the wrong level',
    description: 'Uses 1.96 for a 99% interval, or 2.576 for a 95% one.',
    studentMessage:
      'Match z* to the level: 1.645 for 90%, 1.96 for 95%, 2.576 for 99%. A higher level is bought with extra width, so the critical value has to grow with it.',
    remediationConceptId: 'confidence-intervals',
  },
  {
    id: 'confidence-read-as-probability',
    title: 'A probability claimed about a fixed parameter',
    description: 'Says there is a 95% probability that the population mean lies inside the computed interval.',
    studentMessage:
      'μ is a fixed number — it is either inside this interval or it is not, and no probability attaches to a fact. The 95% describes the procedure: intervals built this way capture μ 95% of the time.',
    remediationConceptId: 'confidence-intervals',
  },
  {
    id: 'sample-size-rounded-down',
    title: 'A required sample size rounded down',
    description: 'Rounds n down after solving a margin-of-error inequality, leaving the requirement unmet.',
    studentMessage:
      'The inequality says "at least this many". Rounding down lands just under the requirement, so a sample-size answer always rounds up — even when the decimal part is tiny.',
    remediationConceptId: 'confidence-intervals',
  },
  {
    id: 'p-value-as-probability-of-null',
    title: 'The p-value read as the probability the null is true',
    description: 'Interprets a p-value as the probability that the null hypothesis holds.',
    studentMessage:
      'A p-value is P(data this extreme | H₀), not P(H₀ | data). Those are opposite conditionals — reversing them is the same error as reading a screening test’s accuracy as your chance of being ill.',
    remediationConceptId: 'hypothesis-testing',
  },
  {
    id: 'two-tailed-p-not-doubled',
    title: 'One tail counted in a two-tailed test',
    description: 'Reports a single tail area as the p-value when the alternative was two-sided.',
    studentMessage:
      '"At least this extreme" includes being extreme the other way. A two-sided alternative needs both tails, so double the one you looked up.',
    remediationConceptId: 'hypothesis-testing',
  },
  {
    id: 'nonsignificant-taken-as-proof',
    title: 'Failing to reject read as proving the null',
    description: 'Concludes that the null hypothesis is true because the p-value exceeded α.',
    studentMessage:
      'You found no evidence against H₀, which is not the same as evidence for it. A small sample can miss a real effect entirely — absence of evidence is not evidence of absence.',
    remediationConceptId: 'hypothesis-testing',
  },
  {
    id: 'significance-taken-as-importance',
    title: 'Statistical significance read as a large effect',
    description: 'Treats a small p-value as evidence that the difference found is big enough to matter.',
    studentMessage:
      'A large enough sample makes any difference significant, however tiny. The p-value says the effect is probably real; the size of the effect is a separate number, and it is the one that decides whether anybody should care.',
    remediationConceptId: 'hypothesis-testing',
  },
  {
    id: 'r-and-r-squared-confused',
    title: 'r quoted where r² was meant',
    description: 'Reports the correlation as the proportion of variation explained, or takes r² for r.',
    studentMessage:
      'r² is the proportion of the variation in y that the line accounts for, and it is always the smaller of the two: r = 0.6 gives r² = 0.36, so the line explains barely a third rather than most of it.',
    remediationConceptId: 'correlation',
  },
  {
    id: 'correlation-assumed-for-any-relationship',
    title: 'r expected to detect any relationship',
    description: 'Takes r near zero to mean the variables are unrelated, when the relationship is strong but curved.',
    studentMessage:
      'r measures *linear* association only. A perfect parabola can have r = 0 — its left half and its right half contribute opposite products, which cancel. Plot the points before concluding anything from r.',
    remediationConceptId: 'correlation',
  },
  {
    id: 'regression-slope-not-rescaled',
    title: 'r used as the slope',
    description: 'Reports the correlation coefficient as the regression slope, without the ratio of standard deviations.',
    studentMessage:
      'r has no units and a slope must have them. b = r × (s_y ÷ s_x) converts the unit-free measure into "so much y per one x" — without that rescaling the number cannot be a slope at all.',
    remediationConceptId: 'linear-regression',
  },
  {
    id: 'intercept-taken-as-mean',
    title: 'The mean of y reported as the intercept',
    description: 'Gives the mean of y as the regression intercept, skipping the subtraction of b times the mean of x.',
    studentMessage:
      'a = ȳ − bx̄. The line passes through the point of averages, so to reach the y-axis from there you must travel back x̄ units along a slope of b — and that is the term you have left out.',
    remediationConceptId: 'linear-regression',
  },

  /* ---------------------------------------- Calculus II: the university year */
  /*
   * The first-year misconceptions divide cleanly in two, and the division is
   * worth keeping in mind when reading what follows.
   *
   * The *techniques* half is bookkeeping: a differential not converted, a
   * constant not borrowed, limits left belonging to the wrong variable, an
   * answer left in θ. None of these is a misunderstanding of calculus. Each is
   * a step of a procedure omitted, and each produces an answer that looks
   * exactly like a correct one.
   *
   * The *series* half is the opposite. Almost every entry there is a student
   * holding a true statement and using its converse — terms tending to zero read
   * as convergence, a ratio limit of 1 read as a verdict, conditional
   * convergence read as absolute. These are not slips; they are coherent beliefs
   * that happen to be false, and they need naming rather than correcting.
   */
  {
    id: 'substitution-dx-not-converted',
    title: 'dx carried through a substitution unchanged',
    description: 'Renames the inside function as u but leaves dx in place, integrating u-something with respect to x.',
    studentMessage:
      'dx has to become du, not travel along unchanged. Differentiate your u to get du = g′(x) dx, and replace that whole group — otherwise the answer is out by a factor that itself depends on x, and no constant can repair it.',
    remediationConceptId: 'u-substitution',
  },
  {
    id: 'substitution-constant-not-adjusted',
    title: 'The constant from du not borrowed',
    description: 'Substitutes correctly but ignores the numerical factor between du and the differential actually present.',
    studentMessage:
      'Compare du with what the integral really offers. If du = 2x dx and you only have x dx, then x dx is half of du — so a factor of ½ comes out in front. Constants can be moved across the integral sign; nothing else can.',
    remediationConceptId: 'u-substitution',
  },
  {
    id: 'substitution-limits-not-changed',
    title: 'Old limits used with the new variable',
    description: 'Evaluates an antiderivative in u at the original x-limits, mixing two variables in one bracket.',
    studentMessage:
      'Limits belong to a variable. Once the integral is in u, the numbers on the integral sign have to be u-values — or convert your answer back to x first. Doing neither gives a plausible number that answers no question at all.',
    remediationConceptId: 'u-substitution',
  },
  {
    id: 'parts-sign-dropped',
    title: 'The minus sign in the parts formula lost',
    description: 'Writes uv + ∫v du, adding the traded integral instead of subtracting it.',
    studentMessage:
      'The formula is uv − ∫v du. That minus comes from moving a term across the equals sign when the product rule was rearranged, and dropping it changes the answer by twice the second integral.',
    remediationConceptId: 'integration-by-parts',
  },
  {
    id: 'parts-v-not-integrated',
    title: 'dv used where v was needed',
    description: 'Substitutes the differential dv into the formula without integrating it first.',
    studentMessage:
      'v is the *integral* of dv, not dv itself. Fill in all four boxes before applying the formula: u and dv on the top row, du and v on the bottom — the bottom-right one is the one most often left blank.',
    remediationConceptId: 'integration-by-parts',
  },
  {
    id: 'parts-choice-inverted',
    title: 'u and dv chosen the wrong way round',
    description: 'Picks u so that differentiating it makes the integrand more complicated, not less.',
    studentMessage:
      'Parts trades one integral for another, so choose u to be the part that gets *simpler* when differentiated. If your new integral is harder than the one you started with, swap u and dv rather than pressing on.',
    remediationConceptId: 'integration-by-parts',
  },
  {
    id: 'trig-power-rule-misapplied',
    title: 'The power rule applied to a trigonometric power',
    description: 'Integrates sinⁿx as sinⁿ⁺¹x/(n+1), as though sin x were the variable.',
    studentMessage:
      'The power rule raises the power of the *variable*, and here the variable is x, not sin x. Differentiate sin³x/3 and the chain rule leaves an extra cos x that was never in the integrand.',
    remediationConceptId: 'trigonometric-integrals',
  },
  {
    id: 'trig-parity-misread',
    title: 'The wrong identity chosen for the parities',
    description: 'Reaches for the half-angle identity when a power is odd, or tries to peel a factor when both are even.',
    studentMessage:
      'Check the parities before choosing a method. An odd power means peel one factor off and convert the rest with the Pythagorean identity; both even means the half-angle identities, because there is nothing to peel.',
    remediationConceptId: 'trigonometric-integrals',
  },
  {
    id: 'trig-sub-form-mismatched',
    title: 'The substitution matched to the wrong root',
    description: 'Uses a sine substitution on a² + x², or a tangent on a² − x².',
    studentMessage:
      'Each root has exactly one substitution that kills it: a² − x² needs a sin θ, a² + x² needs a tan θ, and x² − a² needs a sec θ. Match the signs and the order of the two terms, not just the letters.',
    remediationConceptId: 'trigonometric-substitution',
  },
  {
    id: 'trig-sub-not-converted-back',
    title: 'The answer left in θ',
    description: 'Completes the integral in the substituted variable and stops there.',
    studentMessage:
      'The question was asked in x, so the answer has to be in x. Draw the right triangle the substitution describes and read each trigonometric function off its sides — and remember a bare θ becomes an inverse trigonometric function.',
    remediationConceptId: 'trigonometric-substitution',
  },
  {
    id: 'partial-fractions-improper-not-divided',
    title: 'Split attempted before dividing',
    description: 'Applies partial fractions to a rational function whose numerator has degree at least that of the denominator.',
    studentMessage:
      'Check the degrees first. If the top is not of lower degree than the bottom, divide before splitting — the pieces A/(x − a) all tend to zero far out, so they can never add up to something that tends to a non-zero constant.',
    remediationConceptId: 'partial-fractions',
  },
  {
    id: 'partial-fractions-repeated-term-missing',
    title: 'A repeated factor given only one term',
    description: 'Writes B/(x − a)² for a squared factor without the accompanying A/(x − a).',
    studentMessage:
      'A factor appearing to the power k contributes k terms — every power from 1 up to k. With only the highest one there are not enough constants to match the numerator, and the equations come out inconsistent.',
    remediationConceptId: 'partial-fractions',
  },
  {
    id: 'partial-fractions-constant-swapped',
    title: 'The other constant reported',
    description: 'Finds both constants correctly and hands back the wrong one.',
    studentMessage:
      'That is the other constant. The one you want sits over the factor whose root you substitute — put x at the root of *that* factor, and the term you are not looking for is the one that disappears.',
    remediationConceptId: 'partial-fractions',
  },
  {
    id: 'partial-fractions-cover-up-sign',
    title: 'The cover-up division taken backwards',
    description: 'Divides by the other factor evaluated the wrong way round, reversing the sign of the constant.',
    studentMessage:
      'Check the sign. After substituting the root, the remaining bracket is (root of yours − root of the other), in that order — reversing it negates the constant and flips the whole term.',
    remediationConceptId: 'partial-fractions',
  },
  {
    id: 'partial-fractions-quadratic-given-constant',
    title: 'An irreducible quadratic given a constant numerator',
    description: 'Writes A/(x² + 1) where the decomposition needs (Ax + B)/(x² + 1).',
    studentMessage:
      'A quadratic denominator takes a linear numerator, Ax + B. The rule is that the numerator is one degree lower than the factor beneath it — which is why a linear factor gets a constant and a quadratic gets a linear term.',
    remediationConceptId: 'partial-fractions',
  },
  {
    id: 'improper-limit-not-taken',
    title: 'Infinity substituted rather than approached',
    description: 'Evaluates an antiderivative at ∞ as though it were a number, skipping the limit.',
    studentMessage:
      'Replace the infinite endpoint with a variable t, integrate normally, and then let t run. ∞ is not a value that can be substituted, and writing the limit line is what forces you to notice whether the answer settles at all.',
    remediationConceptId: 'improper-integrals',
  },
  {
    id: 'improper-p-test-reversed',
    title: 'The two p-tests swapped',
    description: 'Applies the convergence condition for the tail to a spike at the origin, or the reverse.',
    studentMessage:
      'The two tests point opposite ways. Out at infinity, the integral of x to the minus p converges when p > 1; near zero it converges when p < 1. The same exponent helps at one end and hurts at the other.',
    remediationConceptId: 'improper-integrals',
  },
  {
    id: 'improper-interior-discontinuity-ignored',
    title: 'Integrated straight across a vertical asymptote',
    description: 'Applies the fundamental theorem across a point where the integrand is undefined.',
    studentMessage:
      'The fundamental theorem needs the integrand continuous on the whole interval. When it blows up inside, split there and test both halves separately — the two infinities do not cancel, and the bracket evaluation cancels them without permission.',
    remediationConceptId: 'improper-integrals',
  },
  {
    id: 'arc-length-one-omitted',
    title: 'The 1 dropped from the arc-length integrand',
    description: 'Integrates the derivative, or its square root, without the 1 that carries the horizontal distance.',
    studentMessage:
      'The 1 under the root is the dx part of the little triangle — the horizontal distance the piece covers. Without it a horizontal line comes out with length zero, and every answer is too small.',
    remediationConceptId: 'arc-length',
  },
  {
    id: 'arc-length-root-distributed',
    title: 'The square root taken term by term',
    description: 'Replaces the root of 1 + (f′)² with 1 + f′, distributing a root across a sum.',
    studentMessage:
      'A square root does not distribute over addition. The root of 1 + (f′)² is not 1 + f′ — test it where f′ = 1, and the first is √2 while the second is 2. Square the derivative, add 1, then take the root of the whole thing.',
    remediationConceptId: 'arc-length',
  },
  {
    id: 'surface-computed-as-volume',
    title: 'A volume integral used for a surface',
    description: 'Uses the disc element πy² dx where the surface element 2πy ds was needed.',
    studentMessage:
      'πy² dx is a disc, and a stack of discs is a volume. A surface is made of arc swept round, so the element is 2πy ds — the circumference times the arc length, and the square root has to be there.',
    remediationConceptId: 'surface-of-revolution',
  },
  {
    id: 'surface-arc-element-omitted',
    title: 'The arc element replaced by dx',
    description: 'Writes 2πy dx instead of 2πy ds, ignoring the slant of the curve.',
    studentMessage:
      'A slanted piece of curve sweeps out more surface than a vertical one of the same width — exactly as much more as ds exceeds dx. Keep the square-root factor; without it you have measured a stack of cylinders.',
    remediationConceptId: 'surface-of-revolution',
  },
  {
    id: 'work-force-taken-as-constant',
    title: 'Force times distance used on a varying force',
    description: 'Multiplies a single quoted force by the total distance rather than integrating.',
    studentMessage:
      'That force is the value at one particular position, not the force throughout. When F changes with x the work is the integral of F(x) — and for a spring the answer is exactly half what the naive product suggests.',
    remediationConceptId: 'work-integrals',
  },
  {
    id: 'spring-extension-measured-wrongly',
    title: 'Spring extension measured from the wrong place',
    description: 'Uses the spring’s total length, or the distance moved, in place of its extension from natural length.',
    studentMessage:
      'x in Hooke’s law is the extension beyond the natural length, always measured from there. A spring stretched from 20 cm to 25 cm with a natural length of 18 cm runs from x = 2 cm to x = 7 cm, not from 0 to 5.',
    remediationConceptId: 'work-integrals',
  },
  {
    id: 'pumping-distance-mismeasured',
    title: 'The lifting distance measured to the wrong place',
    description: 'Uses a slice’s depth below the surface, or its height above the base, rather than how far it must travel.',
    studentMessage:
      'Each slice is lifted to wherever it is going — the rim or the spout — so the distance is measured from the slice up to that. Getting this backwards gives an answer of exactly the right size and the wrong value.',
    remediationConceptId: 'work-integrals',
  },
  {
    id: 'centroid-not-divided-by-area',
    title: 'A moment reported as a centroid',
    description: 'Computes the moment integral and stops, without dividing by the total area.',
    studentMessage:
      'Centre of mass is moment divided by total mass — the division is what makes it an average rather than a total. A moment on its own has the wrong units and grows when you enlarge the region without moving it.',
    remediationConceptId: 'center-of-mass',
  },
  {
    id: 'centroid-height-not-halved',
    title: 'The strip placed at the top rather than its midpoint',
    description: 'Uses the integral of f² or of f for the vertical moment, without the factor of one half.',
    studentMessage:
      'A vertical strip has mass f(x) dx and its material is spread from 0 up to f(x), so it acts as though it sat at its own midpoint, f(x)/2. Multiply the two and the moment is half of f(x)² dx — the half is not decoration.',
    remediationConceptId: 'center-of-mass',
  },
  {
    id: 'centroid-weights-ignored',
    title: 'The positions averaged without their masses',
    description: 'Takes the plain mean of the positions, giving every mass equal say.',
    studentMessage:
      'Every position has to be weighted by the mass sitting there. A plain average puts the balance point in the middle whatever the masses are — but a see-saw with a heavy child on one end does not balance in the middle.',
    remediationConceptId: 'center-of-mass',
  },
  {
    id: 'centroid-taken-as-average-height',
    title: 'The centroid confused with the average value',
    description: 'Reports the mean value of f as the vertical coordinate of the centroid.',
    studentMessage:
      'The average of f is the mean height of the curve; the centroid is lower, because the region is filled beneath the curve rather than concentrated along it. For y = x² on [0, 1] they are 1/3 and 3/10.',
    remediationConceptId: 'center-of-mass',
  },
  {
    id: 'sequence-growth-order-confused',
    title: 'The growth hierarchy misremembered',
    description: 'Judges that a power of n outgrows an exponential, or an exponential a factorial.',
    studentMessage:
      'The order is ln n, then any power of n, then any exponential, then n!, then nⁿ — and each beats everything before it however large the exponent. 2ⁿ overtakes n¹⁰⁰ eventually, whatever the first two hundred terms suggest.',
    remediationConceptId: 'sequence-limits',
  },
  {
    id: 'sequence-judged-from-early-terms',
    title: 'A limit read off the first few terms',
    description: 'Concludes from the behaviour of the opening terms rather than from the tail.',
    studentMessage:
      'Convergence is a statement about the tail, and the opening terms can point the other way. n²/2ⁿ rises until n = 4 and then collapses to zero; any verdict from the first few terms would have been the wrong one.',
    remediationConceptId: 'sequence-limits',
  },
  {
    id: 'terms-to-zero-taken-as-convergence',
    title: 'Shrinking terms read as a convergent series',
    description: 'Concludes that a series converges because its terms tend to zero.',
    studentMessage:
      'Terms going to zero is necessary and nowhere near sufficient. The harmonic series 1 + ½ + ⅓ + ¼ + … has terms tending to zero and grows without bound. A limit of zero tells you nothing at all — reach for a real test.',
    remediationConceptId: 'series-convergence',
  },
  {
    id: 'geometric-formula-used-outside-radius',
    title: 'The geometric sum formula used with a ratio of size at least one',
    description: 'Applies a/(1 − r) to a series whose ratio is too large for it to converge.',
    studentMessage:
      'a/(1 − r) is only a sum when |r| < 1. Used on r = 2 it cheerfully returns a negative number for a series of positive terms, which is the clearest possible sign that the condition was doing real work.',
    remediationConceptId: 'series-convergence',
  },
  {
    id: 'geometric-first-term-misidentified',
    title: 'The wrong term taken as a',
    description: 'Uses the coefficient rather than the actual first term of the series in the geometric sum formula.',
    studentMessage:
      'a is the first term the series actually produces, once the starting index is put in — not the constant in front. A sum from n = 1 of 3(1/2)ⁿ starts at 3/2, so a is 3/2 rather than 3.',
    remediationConceptId: 'series-convergence',
  },
  {
    id: 'telescoping-ends-mismatched',
    title: 'The wrong end kept after telescoping',
    description: 'Collapses a telescoping sum and reports one of the survivors instead of their difference.',
    studentMessage:
      'Two things survive the cancellation, not one: the first piece of the first bracket and the last piece of the last. The partial sum is the difference between them, so write both down before simplifying.',
    remediationConceptId: 'series-convergence',
  },
  {
    id: 'integral-value-taken-as-sum',
    title: 'The integral reported as the sum of the series',
    description: 'Treats the integral test as an evaluation rather than a comparison.',
    studentMessage:
      'The integral test compares; it does not equate. The integral and the sum are within one term of each other, which decides convergence and never gives the total — Σ1/n² is π²/6, and the integral is 1.',
    remediationConceptId: 'integral-test',
  },
  {
    id: 'p-series-threshold-misremembered',
    title: 'The p-series boundary put in the wrong place',
    description: 'Believes the p-series converges for p at least 1, or only for p below 1.',
    studentMessage:
      'Σ1/n^p converges exactly when p > 1, strictly. p = 1 is the harmonic series and diverges, which is what makes the boundary sharp: p = 1.001 converges and p = 0.999 does not.',
    remediationConceptId: 'integral-test',
  },
  {
    id: 'integral-test-conditions-unchecked',
    title: 'The integral test applied without its hypotheses',
    description: 'Uses the test on terms that are not positive, or not eventually decreasing.',
    studentMessage:
      'The test needs f positive, continuous and eventually decreasing. Those are what make the rectangles bound the curve at all — without them the picture is not a bound and the comparison says nothing.',
    remediationConceptId: 'integral-test',
  },
  {
    id: 'comparison-inequality-reversed',
    title: 'The comparison made in the useless direction',
    description: 'Bounds a series below by a convergent one, or above by a divergent one, and draws a conclusion.',
    studentMessage:
      'Only two of the four combinations conclude: smaller than convergent means convergent, bigger than divergent means divergent. Being smaller than something infinite is no constraint at all.',
    remediationConceptId: 'comparison-tests',
  },
  {
    id: 'comparison-series-misjudged',
    title: 'The comparison series read off the wrong term',
    description: 'Chooses a comparison from a lower-order term instead of the dominant behaviour.',
    studentMessage:
      'Keep the dominant power and discard everything else. (2n + 1)/(n³ − 4) behaves like 2/n², not like 1/n or 1/n³ — the leading term on top over the leading term underneath.',
    remediationConceptId: 'comparison-tests',
  },
  {
    id: 'ratio-test-one-read-as-verdict',
    title: 'A ratio limit of one treated as an answer',
    description: 'Concludes convergence or divergence from a ratio limit of exactly 1.',
    studentMessage:
      'L = 1 is the test returning nothing, not a near miss. Every p-series gives L = 1, and those include both the divergent harmonic series and the convergent Σ1/n² — so the ratio cannot possibly be deciding between them.',
    remediationConceptId: 'ratio-test',
  },
  {
    id: 'ratio-test-inverted',
    title: 'The ratio taken upside down',
    description: 'Computes the current term over the next one, inverting the verdict.',
    studentMessage:
      'The ratio is the next term over the current one — it measures whether the terms are shrinking. Taking it the other way up turns every L into its reciprocal, so convergence and divergence swap places.',
    remediationConceptId: 'ratio-test',
  },
  {
    id: 'alternating-decreasing-condition-skipped',
    title: 'Only the limit condition checked',
    description: 'Applies the alternating series test without confirming that the terms decrease.',
    studentMessage:
      'The test has two conditions and both are needed. Terms that alternate and tend to zero while jumping up and down in size can still diverge — monotone decrease is what makes the partial sums close in from both sides.',
    remediationConceptId: 'alternating-series',
  },
  {
    id: 'alternating-error-bound-off-by-one',
    title: 'The error bounded by the last term used',
    description: 'Bounds the truncation error by the last term included rather than the first one omitted.',
    studentMessage:
      'The error is at most the first term you left out — not the last one you put in. Using the wrong one overstates the error, and solving for a required accuracy with it asks for more terms than are needed.',
    remediationConceptId: 'alternating-series',
  },
  {
    id: 'conditional-taken-as-absolute',
    title: 'Conditional convergence reported as absolute',
    description: 'Concludes absolute convergence from the alternating series test alone.',
    studentMessage:
      'The alternating test says the series converges; it says nothing about the series of absolute values. Strip the signs and test again — the alternating harmonic series converges while Σ1/n does not, so that one is conditional.',
    remediationConceptId: 'alternating-series',
  },
  {
    id: 'radius-endpoints-not-tested',
    title: 'The interval reported without checking its ends',
    description: 'Gives an open interval straight from the ratio test, without substituting either endpoint.',
    studentMessage:
      'The ratio test gives a strict inequality, so at both endpoints L = 1 and it has nothing to say. Substitute each end and test the resulting number series separately — they can, and often do, disagree with each other.',
    remediationConceptId: 'power-series',
  },
  {
    id: 'radius-taken-as-reciprocal',
    title: 'The radius inverted',
    description: 'Reports the limit from the ratio test as the radius, rather than solving the inequality for the distance from the centre.',
    studentMessage:
      'Finish the inequality. The ratio test gives |x − a| times L below 1, so the radius is 1/L, not L. Check the direction on a series you know: Σxⁿ has ratio limit 1 and radius 1, while Σxⁿ/2ⁿ has radius 2.',
    remediationConceptId: 'power-series',
  },
  {
    id: 'taylor-factorial-omitted',
    title: 'The factorial left out of a Taylor coefficient',
    description: 'Uses the derivative itself as the coefficient rather than dividing by n factorial.',
    studentMessage:
      'The coefficient is the n-th derivative at the centre divided by n!, and the factorial comes from differentiating (x − a)ⁿ n times. Without it the cubic coefficient of e^x would be 1 rather than 1/6.',
    remediationConceptId: 'taylor-series',
  },
  {
    id: 'taylor-derivative-not-evaluated',
    title: 'A derivative left as a function in the coefficient',
    description: 'Writes the derivative as a function of x into the coefficient instead of evaluating it at the centre.',
    studentMessage:
      'Each coefficient is a number: evaluate the derivative at the centre before dividing by the factorial. A coefficient still containing x is not a coefficient, and the result is not a power series.',
    remediationConceptId: 'taylor-series',
  },
  {
    id: 'parametric-second-derivative-as-ratio',
    title: 'Second derivatives divided',
    description: 'Computes the second derivative as a ratio of second derivatives, by analogy with the first.',
    studentMessage:
      'The second derivative is the derivative of the slope with respect to x — so differentiate dy/dx with respect to t, then divide by dx/dt. Test the shortcut on x = t, y = t²: the true answer is 2 and the shortcut gives 2/0.',
    remediationConceptId: 'parametric-curves',
  },
  {
    id: 'parametric-slope-inverted',
    title: 'The parametric slope taken upside down',
    description: 'Computes dx/dt over dy/dt rather than dy/dt over dx/dt.',
    studentMessage:
      'Slope is rise over run, so it is dy/dt over dx/dt. The time cancels either way, which is exactly why the wrong one looks reasonable — check it on x = t, y = 2t, where the slope must be 2.',
    remediationConceptId: 'parametric-curves',
  },
  {
    id: 'polar-area-element-wrong',
    title: 'The polar area taken without the half or the square',
    description: 'Copies the shape of the Cartesian area integral into polar coordinates, losing the square and the half.',
    studentMessage:
      'The piece being added up is a wedge, not a rectangle: a sector of radius r and angle dθ has area ½r² dθ. Without the square it is not even an area — doubling r would only double the answer instead of quadrupling it.',
    remediationConceptId: 'polar-calculus',
  },
  {
    id: 'polar-limits-trace-twice',
    title: 'The curve traced twice by the limits',
    description: 'Integrates over a full turn on a polar curve that is already complete after half a turn.',
    studentMessage:
      'Sketch the curve and find where r returns to 0 before choosing limits. r = 2cos θ is a whole circle by the time θ reaches π/2, so running on to 2π counts its area twice — and nothing in the algebra will look wrong.',
    remediationConceptId: 'polar-calculus',
  },
  {
    id: 'polar-slope-taken-as-dr-dtheta',
    title: 'The radial rate reported as the slope',
    description: 'Treats r and θ as though they were vertical and horizontal coordinates.',
    studentMessage:
      'r is not a height and θ is not a horizontal position, so their derivative is not a slope — it is how fast the curve moves away from the origin as it turns. For dy/dx, write x = r cos θ and y = r sin θ and use the parametric rule.',
    remediationConceptId: 'polar-calculus',
  },
  /* ------------------------------------------------------- linear algebra */
  /*
   * The band where a wrong answer is most often a *rule from arithmetic
   * applied where it does not hold*. Multiplication commutes, a scalar comes
   * out of a product once, a determinant of a sum is the sum of determinants —
   * every one of those is true for numbers, false here, and each has a name
   * below rather than a red cross.
   */
  {
    id: 'vector-length-as-component-sum',
    title: 'Components added instead of squared',
    description: 'Computes a vector’s length by adding its entries rather than by taking the root of the sum of squares.',
    studentMessage:
      'Length is Pythagoras in n dimensions: square every component, add, then take the root. Adding the components measures nothing — it changes when you rotate the vector, and a length cannot.',
    remediationConceptId: 'vectors-rn',
  },
  {
    id: 'vector-difference-reversed',
    title: 'The displacement taken backwards',
    description: 'Computes P − Q where the vector from P to Q was asked for.',
    studentMessage:
      'The vector from P to Q is Q − P: finish minus start, exactly as it was in one dimension. Reversing it points the arrow the wrong way and negates every component.',
    remediationConceptId: 'vectors-rn',
  },
  {
    id: 'vector-scaled-only-once',
    title: 'One scalar applied, the other forgotten',
    description: 'Scales the first vector in a combination and adds the second unchanged.',
    studentMessage:
      'Each vector carries its own scalar, and each scalar multiplies *every* component of its vector. Check that both have been scaled before you add.',
    remediationConceptId: 'vectors-rn',
  },
  {
    id: 'combination-coefficients-swapped',
    title: 'The two coefficients exchanged',
    description: 'Solves for the weights in a linear combination correctly and reports them against the wrong vectors.',
    studentMessage:
      'You have the right pair of numbers on the wrong pair of vectors. Substitute back — the combination only reproduces the target one way round.',
    remediationConceptId: 'linear-combinations',
  },
  {
    id: 'combination-scalars-ignored',
    title: 'Vectors added without their weights',
    description: 'Adds the vectors of a combination and never multiplies by the scalars.',
    studentMessage:
      'A linear combination weights each vector before adding. Adding them raw answers a different question — the one where every scalar happens to be 1.',
    remediationConceptId: 'linear-combinations',
  },
  {
    id: 'span-count-taken-as-dimension',
    title: 'Three vectors assumed to span three dimensions',
    description: 'Reads the size of a spanning set as the dimension of what it spans.',
    studentMessage:
      'The count is an upper bound, not the answer. Three vectors span ℝ³ only if none of them was already reachable from the other two — if one is, they span a plane however many you write down.',
    remediationConceptId: 'span',
  },
  {
    id: 'span-of-parallel-taken-as-plane',
    title: 'Parallel vectors taken to span a plane',
    description: 'Treats any two vectors as spanning a plane, without checking whether one is a multiple of the other.',
    studentMessage:
      'Two vectors span a plane only when they point in genuinely different directions. If one is a multiple of the other, every combination lies on a single line through the origin.',
    remediationConceptId: 'span',
  },
  {
    id: 'independence-checked-pairwise',
    title: 'Independence tested two at a time',
    description: 'Concludes a set is independent because no two of its vectors are parallel.',
    studentMessage:
      'Independence is a statement about the whole set at once. Three vectors can be pairwise non-parallel and still lie in one plane, and then the third is a combination of the first two.',
    remediationConceptId: 'linear-independence',
  },
  {
    id: 'dependence-taken-as-all-zero',
    title: 'Dependence read as “some vector is zero”',
    description: 'Looks for a zero vector in the set instead of a non-trivial relation between the vectors.',
    studentMessage:
      'Dependent means *some* combination with not-all-zero weights gives the zero vector. None of the vectors has to be zero — usually none of them is.',
    remediationConceptId: 'linear-independence',
  },
  {
    id: 'matrix-product-taken-entrywise',
    title: 'Matrices multiplied entry by entry',
    description: 'Multiplies corresponding entries, as addition does, instead of taking rows against columns.',
    studentMessage:
      'Addition works entry by entry; multiplication does not. Entry (i, j) of AB is row i of A dotted with column j of B — a whole sum, not a single product.',
    remediationConceptId: 'matrices',
  },
  {
    id: 'matrix-multiplication-assumed-commutative',
    title: 'AB assumed to equal BA',
    description: 'Multiplies in whichever order is convenient, as with numbers.',
    studentMessage:
      'Order matters here. AB and BA are usually different matrices, and for non-square shapes only one of them is even defined — so read which way round the question wrote it.',
    remediationConceptId: 'matrices',
  },
  {
    id: 'matrix-product-row-times-row',
    title: 'Row taken against row',
    description: 'Pairs row i of A with row j of B rather than with column j.',
    studentMessage:
      'Rows go against *columns*. Run your finger across A and down B — if both fingers move the same way, the entry you are computing is not the one the product defines.',
    remediationConceptId: 'matrices',
  },
  {
    id: 'row-operation-sign-slipped',
    title: 'A row added where it should be subtracted',
    description: 'Performs R2 + kR1 when R2 − kR1 was needed to clear the entry.',
    studentMessage:
      'The multiplier is chosen to *cancel* the entry below the pivot. Check the sign by looking at the entry you were clearing: after the operation it must be zero.',
    remediationConceptId: 'row-reduction',
  },
  {
    id: 'pivot-count-taken-as-row-count',
    title: 'Pivots counted as rows',
    description: 'Reports the number of rows of a matrix where the number of pivots was asked for.',
    studentMessage:
      'A pivot is a leading entry in a *non-zero* row of the echelon form. Rows that reduce to nothing carry no pivot, which is exactly why the count is worth taking.',
    remediationConceptId: 'row-reduction',
  },
  {
    id: 'rref-pivot-column-not-cleared',
    title: 'Reduced form left with entries above a pivot',
    description: 'Stops at echelon form and calls it reduced row echelon form.',
    studentMessage:
      'Echelon form only clears *below* each pivot. Reduced form clears above it too, and scales every pivot to 1 — until both are done the solution cannot simply be read off.',
    remediationConceptId: 'row-reduction',
  },
  {
    id: 'echelon-leading-entry-not-one',
    title: 'A leading entry left unscaled',
    description: 'Accepts a form whose pivots are not 1 as reduced row echelon form.',
    studentMessage:
      'In reduced row echelon form every pivot is exactly 1. Divide the row through — it is a legal row operation, and without it the variable is not yet isolated.',
    remediationConceptId: 'row-reduction',
  },
  {
    id: 'free-variables-counted-as-zero-rows',
    title: 'Free variables counted from the zero rows',
    description: 'Counts the rows that vanished rather than the columns without a pivot.',
    studentMessage:
      'Free variables live in *columns*, not rows: one for every column of the coefficient matrix that has no pivot. Count the pivots and subtract from the number of unknowns.',
    remediationConceptId: 'gaussian-elimination',
  },
  {
    id: 'inconsistent-row-read-as-solvable',
    title: 'A row of zeros with a non-zero constant ignored',
    description: 'Treats the row 0 = c as a free row rather than as a contradiction.',
    studentMessage:
      'That row says 0 = c, and no choice of the unknowns can make that true. One such row and the whole system has no solutions — the other rows do not get a vote.',
    remediationConceptId: 'gaussian-elimination',
  },
  {
    id: 'back-substitution-stopped-early',
    title: 'The last unknown reported as the answer',
    description: 'Finds the bottom variable by elimination and stops before substituting back up.',
    studentMessage:
      'Elimination gives you the last unknown; back-substitution gives you the rest. Feed the value you found into the row above and keep going until the variable the question asked for is the one you have.',
    remediationConceptId: 'gaussian-elimination',
  },
  {
    id: 'inverse-determinant-not-divided',
    title: 'The adjugate given as the inverse',
    description: 'Swaps and negates the entries of a 2×2 matrix but never divides by the determinant.',
    studentMessage:
      'Swapping the diagonal and negating the other two is only half of it. Divide the whole matrix by the determinant, or multiply back and you will get det(A)·I rather than I.',
    remediationConceptId: 'matrix-inverse',
  },
  {
    id: 'inverse-adjugate-signs-misplaced',
    title: 'The wrong pair of entries negated',
    description: 'Negates the leading diagonal instead of the off-diagonal when inverting a 2×2 matrix.',
    studentMessage:
      'The diagonal entries *swap*; the off-diagonal ones change sign and stay put. Check by multiplying your answer by A — anything else fails to give the identity.',
    remediationConceptId: 'matrix-inverse',
  },
  {
    id: 'inverse-applied-as-multiplication',
    title: 'Multiplied by A where A⁻¹ was needed',
    description: 'Computes Ab instead of A⁻¹b when solving Ax = b.',
    studentMessage:
      'A is what was *done* to x; to recover x you have to undo it. Multiply b by the inverse, not by A — multiplying by A again applies the transformation twice.',
    remediationConceptId: 'matrix-inverse',
  },
  {
    id: 'determinant-products-added',
    title: 'The two diagonal products added',
    description: 'Computes ad + bc rather than ad − bc.',
    studentMessage:
      'The determinant subtracts: ad − bc. The minus sign is the whole content of it — with a plus, every matrix would look invertible and the number would predict nothing.',
    remediationConceptId: 'determinants',
  },
  {
    id: 'determinant-scalar-factored-once',
    title: 'A scalar taken out of a determinant once',
    description: 'Writes det(kA) = k·det(A), pulling the factor out of one row rather than out of every row.',
    studentMessage:
      'Scaling A scales *every* row, and a determinant is linear in each row separately. For an n×n matrix that makes det(kA) = kⁿ·det(A), so the size of the matrix is part of the answer.',
    remediationConceptId: 'determinants',
  },
  {
    id: 'determinant-of-sum-split',
    title: 'det(A + B) split into two determinants',
    description: 'Distributes the determinant across a sum, as if it were linear in the whole matrix.',
    studentMessage:
      'det is multiplicative, not additive: det(AB) = det(A)·det(B) is true, det(A + B) = det(A) + det(B) is not. Add the matrices first, then take one determinant.',
    remediationConceptId: 'determinants',
  },
  {
    id: 'system-coefficients-read-by-column',
    title: 'The coefficient matrix filled in transposed',
    description: 'Writes each equation down a column instead of across a row when forming Ax = b.',
    studentMessage:
      'One equation is one *row*. Reading them into columns transposes A, which changes the system into a different one that usually has a different answer.',
    remediationConceptId: 'linear-systems',
  },
  {
    id: 'matrix-equation-rows-and-columns-confused',
    title: 'The column view applied to rows',
    description: 'Treats Ax as a combination of the rows of A rather than of its columns.',
    studentMessage:
      'Ax weights the *columns* of A by the entries of x. The rows give the other reading — each row is one equation — and the two views answer different questions.',
    remediationConceptId: 'linear-systems',
  },
  {
    id: 'rank-taken-as-matrix-size',
    title: 'Rank read off the shape of the matrix',
    description: 'Reports the number of rows or columns where the number of pivots was asked for.',
    studentMessage:
      'Rank counts pivots, and pivots survive only where a row carried new information. A 3×3 matrix whose third row is the sum of the first two has rank 2, not 3.',
    remediationConceptId: 'rank',
  },
  {
    id: 'rank-nullity-rows-used',
    title: 'Rank–nullity applied to the rows',
    description: 'Subtracts the rank from the number of rows instead of from the number of columns.',
    studentMessage:
      'rank + nullity = number of *columns*, because the columns are the variables and every variable is either pivot or free. The row count never enters it.',
    remediationConceptId: 'rank',
  },
  {
    id: 'underdetermined-assumed-consistent',
    title: 'Fewer equations assumed to mean infinitely many solutions',
    description: 'Concludes from having more unknowns than equations that the system must have solutions.',
    studentMessage:
      'More unknowns than equations rules out a *unique* solution, and nothing else. The system can still be inconsistent, in which case the answer is none rather than infinitely many.',
    remediationConceptId: 'solution-sets',
  },
  {
    id: 'solution-set-taken-as-subspace',
    title: 'The particular solution dropped',
    description: 'Gives the solutions of Ax = 0 where the solutions of Ax = b were asked for.',
    studentMessage:
      'The solution set of Ax = b is one particular solution *plus* everything solving Ax = 0. Leaving the particular one off moves the whole set back to the origin, where it does not pass.',
    remediationConceptId: 'solution-sets',
  },
  {
    id: 'vector-space-zero-vector-missing',
    title: 'A set with no zero vector called a space',
    description: 'Checks the arithmetic axioms without checking that the set contains an additive identity.',
    studentMessage:
      'Every vector space contains a zero vector, so a set that misses the origin fails before any other axiom is tested. It is the fastest check there is — do it first.',
    remediationConceptId: 'vector-spaces',
  },
  {
    id: 'closure-checked-only-for-addition',
    title: 'Closure tested for addition alone',
    description: 'Verifies that the sum of two members stays in the set and never tries a scalar multiple.',
    studentMessage:
      'Two closures have to hold: under addition *and* under scaling. The integers inside ℝ are closed under addition and leave the set the moment you multiply by a half.',
    remediationConceptId: 'vector-spaces',
  },
  {
    id: 'subspace-not-through-origin',
    title: 'A shifted plane called a subspace',
    description: 'Accepts a line or plane that does not pass through the origin as a subspace.',
    studentMessage:
      'A subspace has to contain the zero vector. A plane like x + y + z = 5 is closed under nothing — double a point on it and you land on x + y + z = 10.',
    remediationConceptId: 'subspaces',
  },
  {
    id: 'null-space-confused-with-column-space',
    title: 'Null space and column space exchanged',
    description: 'Answers a question about what A reaches with what A destroys, or the reverse.',
    studentMessage:
      'The null space lives among the *inputs* — the x with Ax = 0. The column space lives among the outputs. They sit in different spaces and usually have different dimensions.',
    remediationConceptId: 'subspaces',
  },
  {
    id: 'basis-spanning-without-independence',
    title: 'A spanning set called a basis',
    description: 'Accepts any set that reaches everything, without checking that none of it is redundant.',
    studentMessage:
      'Spanning is half the definition. A basis also has to be independent — otherwise you can throw one vector away and still reach everything, and it was never minimal.',
    remediationConceptId: 'basis',
  },
  {
    id: 'basis-independent-without-spanning',
    title: 'An independent set called a basis',
    description: 'Accepts any independent set as a basis without checking that it reaches the whole space.',
    studentMessage:
      'Independence is the other half. Two independent vectors in ℝ³ are a basis for the plane they span, not for ℝ³ — there is a whole direction they never reach.',
    remediationConceptId: 'basis',
  },
  {
    id: 'dimension-counted-as-vector-length',
    title: 'Dimension read off the length of a vector',
    description: 'Reports the number of entries in each vector where the dimension of the subspace was asked for.',
    studentMessage:
      'The entries say which space the vectors *live in*; the dimension says how much of it they fill. A line in ℝ³ is spanned by vectors with three entries and has dimension 1.',
    remediationConceptId: 'dimension',
  },
  {
    id: 'polynomial-degree-taken-as-dimension',
    title: 'The degree given as the dimension',
    description: 'Says the polynomials of degree at most n form a space of dimension n.',
    studentMessage:
      'Count the basis, not the degree. Degree at most n gives 1, x, …, xⁿ — that is n + 1 vectors, because the constant polynomial is one of them.',
    remediationConceptId: 'dimension',
  },
  {
    id: 'coordinates-taken-as-components',
    title: 'The entries of a vector given as its coordinates',
    description: 'Reports v itself where the coordinate vector of v in a non-standard basis was asked for.',
    studentMessage:
      'The entries of v are its coordinates in the *standard* basis. In another basis you have to solve for the weights that rebuild v from those particular vectors.',
    remediationConceptId: 'coordinates',
  },
  {
    id: 'change-of-basis-matrix-inverted',
    title: 'The change-of-basis matrix used the wrong way',
    description: 'Multiplies by P where P⁻¹ was needed, translating in the opposite direction.',
    studentMessage:
      'P has the basis vectors as its columns, so P turns coordinates *into* standard entries. Going the other way needs the inverse — check which of the two the question is asking for.',
    remediationConceptId: 'coordinates',
  },
  {
    id: 'linearity-assumed-for-affine',
    title: 'A map with a constant term called linear',
    description: 'Accepts T(x) = Ax + c as a linear transformation because its graph is a straight line.',
    studentMessage:
      'Straight is not the test. A linear transformation must send the zero vector to the zero vector, and T(x) = Ax + c sends it to c — the school sense of “linear” and this one genuinely differ.',
    remediationConceptId: 'linear-transformations',
  },
  {
    id: 'linearity-not-used-on-combination',
    title: 'The known images not combined',
    description: 'Given T(u) and T(v), fails to build T(au + bv) as aT(u) + bT(v).',
    studentMessage:
      'That is exactly what linearity is for: T(au + bv) = aT(u) + bT(v). You never need a formula for T — the two images you were given already determine this one.',
    remediationConceptId: 'linear-transformations',
  },
  {
    id: 'kernel-confused-with-range',
    title: 'Kernel and range exchanged',
    description: 'Answers with the dimension of the image where the dimension of the kernel was asked for, or the reverse.',
    studentMessage:
      'The kernel is what collapses to zero and lives in the domain; the range is what gets reached and lives in the codomain. Rank–nullity relates them but does not make them the same.',
    remediationConceptId: 'kernel-range',
  },
  {
    id: 'kernel-taken-as-empty',
    title: 'A trivial kernel called empty',
    description: 'Says the kernel of an injective map contains nothing at all.',
    studentMessage:
      'The kernel always contains the zero vector — every linear map sends 0 to 0. “Trivial” means it contains *only* 0, which is a subspace of dimension 0, not an empty set.',
    remediationConceptId: 'kernel-range',
  },
  {
    id: 'transformation-images-written-as-rows',
    title: 'The images written into rows',
    description: 'Builds the standard matrix by laying the images of the basis vectors across rows instead of down columns.',
    studentMessage:
      'The images of the basis vectors are the *columns*. Written as rows you get the transpose, which is a different transformation — check by applying your matrix to the first basis vector.',
    remediationConceptId: 'transformation-matrix',
  },
  {
    /*
     * Not `composition-order-reversed`: the functions unit already owns that
     * id for g(f(x)) computed in place of f(g(x)). Two entries with one id is
     * not a duplicate warning — `BY_ID` is a Map, so the later one wins and the
     * earlier diagnosis is silently replaced by a message about matrices.
     */
    id: 'matrix-composition-order-reversed',
    title: 'Composition multiplied the wrong way round',
    description: 'Writes the matrix of S after T as TS rather than ST.',
    studentMessage:
      'Doing T first means the matrix for T is the one on the *right*: ST. Matrices apply right to left, the same order the composition symbol reads in.',
    remediationConceptId: 'transformation-matrix',
  },
  {
    id: 'characteristic-polynomial-sign-slipped',
    title: 'A sign lost in the characteristic determinant',
    description: 'Expands det(A − λI) without carrying the minus signs on the diagonal.',
    studentMessage:
      'Both diagonal entries lose λ, so the product contributes λ² − (a + d)λ + ad. Dropping one of those minus signs flips the sum of the eigenvalues and usually flips their signs too.',
    remediationConceptId: 'eigenvalues',
  },
  {
    id: 'eigenvalue-read-off-diagonal',
    title: 'Diagonal entries given as eigenvalues',
    description: 'Reads the eigenvalues straight off the diagonal of a matrix that is not triangular.',
    studentMessage:
      'That shortcut is real, and it only works for triangular matrices. With a non-zero entry on both sides of the diagonal the eigenvalues have to come from the characteristic equation.',
    remediationConceptId: 'eigenvalues',
  },
  {
    id: 'eigenvalue-given-as-determinant',
    title: 'The determinant reported as an eigenvalue',
    description: 'Confuses det(A) — the product of the eigenvalues — with an eigenvalue itself.',
    studentMessage:
      'det(A) is the *product* of the eigenvalues and the trace is their sum. Both are useful checks on your answer, and neither is an eigenvalue.',
    remediationConceptId: 'eigenvalues',
  },
  {
    id: 'eigenvector-solved-without-shift',
    title: 'Ax = 0 solved instead of the shifted system',
    description: 'Looks for the eigenvector in the null space of A rather than of A with λ subtracted from the diagonal.',
    studentMessage:
      'Subtract λ from the diagonal first. A itself usually has only the zero vector in its null space, which is precisely why the shift is what makes the system degenerate.',
    remediationConceptId: 'eigenvectors',
  },
  {
    id: 'eigenvector-scaled-taken-as-different',
    title: 'A scaled eigenvector taken for a new one',
    description: 'Counts multiples of one eigenvector as separate members of a basis for the eigenspace.',
    studentMessage:
      'If v is an eigenvector then so is every non-zero multiple of it — they are the same direction. An eigenspace of dimension 1 has one basis vector, however many multiples you write down.',
    remediationConceptId: 'eigenvectors',
  },
  {
    id: 'eigenspace-dimension-taken-as-multiplicity',
    title: 'Multiplicity used as the eigenspace dimension',
    description: 'Assumes an eigenvalue repeated twice always has a two-dimensional eigenspace.',
    studentMessage:
      'The multiplicity is an upper bound only. A repeated eigenvalue can have a one-dimensional eigenspace — that is the exact situation in which a matrix fails to be diagonalisable.',
    remediationConceptId: 'eigenvectors',
  },
  {
    id: 'repeated-eigenvalue-assumed-fatal',
    title: 'Diagonalisability decided by whether the eigenvalues repeat',
    description: 'Rules a matrix in or out from the repetition of its eigenvalues rather than from the number of independent eigenvectors.',
    studentMessage:
      'Repetition decides nothing by itself — the identity matrix repeats an eigenvalue and is already diagonal. What matters is whether the eigenvectors amount to a basis.',
    remediationConceptId: 'diagonalization',
  },
  {
    id: 'diagonalization-factors-swapped',
    title: 'P and its inverse the wrong way round',
    description: 'Writes the factorisation with the inverse on the outside.',
    studentMessage:
      'With the eigenvectors as the columns of P, the factorisation is A = PDP⁻¹: undo the basis change, scale, then put it back. Swapping them diagonalises a different matrix.',
    remediationConceptId: 'diagonalization',
  },
  {
    id: 'matrix-power-taken-entrywise',
    title: 'A matrix power taken entry by entry',
    description: 'Raises each entry of A to the power rather than multiplying A by itself.',
    studentMessage:
      'A to the n means A multiplied by itself n times. Raising the entries is not that — and the point of diagonalising is that it turns the real power into powers of the diagonal entries alone.',
    remediationConceptId: 'eigen-applications',
  },
  {
    id: 'dominant-eigenvalue-not-identified',
    title: 'The wrong eigenvalue used for the long run',
    description: 'Predicts long-run growth from the smaller eigenvalue, or from their sum.',
    studentMessage:
      'After many steps the largest eigenvalue in size swamps the others, so it alone sets the growth factor. The smaller one decides how quickly the ratio settles, not what it settles to.',
    remediationConceptId: 'eigen-applications',
  },
  {
    id: 'steady-state-not-normalised',
    title: 'A steady state left unnormalised',
    description: 'Gives an eigenvector for eigenvalue 1 without scaling its entries to sum to 1.',
    studentMessage:
      'Any multiple of that vector is still fixed by the matrix. A distribution has to add up to the whole population, so divide through by the sum before reading off a share.',
    remediationConceptId: 'eigen-applications',
  },
];

const BY_ID = new Map(MISCONCEPTIONS.map((m) => [m.id, m]));

export function misconception(id: string): Misconception | undefined {
  return BY_ID.get(id);
}

export function misconceptionIds(): string[] {
  return MISCONCEPTIONS.map((m) => m.id);
}
